import { objectionTurnFromRole } from '../utils/objectionTurn';
import { AdminPayoutAlerts } from '../components/AdminPayoutAlerts';
import { PingRefundPanel } from '../components/PingRefundPanel';
import { PingPayoutPanel } from '../components/PingPayoutPanel';
import { PingPayoutHistoryItem } from '../components/PingPayoutHistoryItem';
import { TransactionKindPill, transactionKindForType, type TransactionKind } from '../components/TransactionKindPill';
import type { PingPayout } from '../services/api';
import { useRefreshOnReturn } from '../hooks/useRefreshOnReturn';
import MilestoneReplacementNotice, { MilestoneReplacementBadge } from '../components/MilestoneReplacementNotice';
import PaidMilestoneNotice from '../components/PaidMilestoneNotice';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useCompanyTheme } from '../components/company-theme/useCompanyTheme';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { inactivityViewerActs } from '../components/InactivityCasePanel';
import { DashboardLayout, Card, Notice, FormInput, Button, ContractTimeline, ContractReplacementNotice, Stepper, StepContent, ContractSigningButtons, ContractSigningHelp, ContractSigningRejectionNotice, ContractHistory, ContractList, type ContractListRow, type ContractRowAction, DocumentIntegrityModal, DocumentPreviewModal, useDocumentPreview, ProjectFundingPanel, ProjectWorkflowStatusBadge, ProjectInactivitySummary, MilestoneConflictActionButton, MilestoneRemediationActions } from '../components';
import { MilestoneContractActions, MilestoneNodeActions, contractMilestoneActionState, hasContractMilestoneActions, type MilestoneActionHandlers } from '../components/MilestoneTimelineActions';
import { contractVersionBadge, draftStatusBadge } from '../utils/contractBadges';
import { contractTitle } from '../utils/contractTitle';
import { variationChanges } from '../utils/variationChanges';
import { VariationTransactionItem, withVariationRows } from '../components/VariationTransactionItem';
import { validateContractForForwarding } from '../utils/contractForwardValidation';
import MilestoneAttestationModal from '../components/MilestoneAttestationModal';
import { attestationRegistered } from '../utils/attestationRegistered';
import ContractReferenceNote from '../components/ContractReferenceNote';
import ConflictVariationNotice from '../components/ConflictVariationNotice';
import { VariationWizard, variationReturnKey } from '../components/contract-wizard/VariationWizard';
import CreateAgreementTypeModal from '../components/contract-wizard/CreateAgreementTypeModal';
import { hasSignedAgreement, variationBasis, type AgreementChoice } from '../components/contract-wizard/agreementTypeChoice';
import { VariationChanges } from '../components/contract-wizard/VariationChanges';
import { canSignContract } from '../utils/contractSigningAvailability';
import { needsPersonDraftReview } from '../utils/personTodos';
import { subjectPath } from '../utils/messageThreads';
import { VariationAgreementModal } from '../components/contract-wizard/VariationAgreementModal';
import { PersonContractReview, type ReviewMessage } from '../components/contract-review/PersonContractReview';
import { StageVariationBadge, stageVariationState } from '../components/StageVariationNotice';
import { contractFrameTotals, contractOutstandingAmount } from '../utils/contractFrameTotals';
import { contractListFunding } from '../utils/contractListFunding';
import { stageVariationMilestones } from '../utils/stageVariation';
import { agreementBasis, agreementLabel, agreementRoot, agreementVariationNumber, contractFolderHierarchy } from '../utils/agreementIdentity';
import { VariationBlockedError, variationBlock, variationBlockText, variationOpenErrorText } from '../utils/variationBlock';
import Modal from '../components/Modal';
import ExternalDisputeModal from '../components/ExternalDisputeModal';
import { ProjectHeldFundsSummary } from '../components/ProjectHeldFundsSummary';
import { AmountBreakdownTable } from '../components/AmountBreakdownTable';
import { payoutPresentationMilestones } from '../utils/payoutAvailability';
import StatusChangeModal from '../components/StatusChangeModal';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectStatusOverview, type ProjectStatusMenuItem } from '../components/ProjectStatusOverview';
import { objectionNoticeMilestoneIds, projectStatusOverview } from '../utils/projectStatusOverview';
import { useAuth } from '../context/AuthContext';
import { useAdyenOnboardingPrompt } from '../hooks/useAdyenOnboardingPrompt';
import { useMilestoneStarts } from '../hooks/useMilestoneStarts';
import { useProjectTabNavigation } from '../hooks/useProjectTabNavigation';
import { useResolutionOverlapWarning } from '../hooks/useResolutionOverlapWarning';
// `systemApi` was only used to load the global construction agreement (byggavtal)
// in the contract preview, which is DISCONNECTED for MVP. Re-add it to reactivate
// (feat/kontrakt_andringar).
import { adyenOnboardingApi, messageThreadApi, projectApi, customerApi, contractApi, milestoneApi, inactivityApi, documentApi, companyApi, personApi, marketplaceApi, contractConfigApi, projectStatusApi, adminApi, type AdyenAccount } from '../services/api';
import { refreshMessageSummary } from '../hooks/useMessageSummary';
import { useRefreshOnNotification } from '../hooks/useRefreshOnNotification';
import { MessageThreadStateLink } from '../components/MessageThreadStateLink';
import { MarketplaceTab } from '../components/MarketplaceTab';
import { ProjectDocumentsTab } from '../components/ProjectDocumentsTab';
import { ProjectParticipantsTab } from '../components/ProjectParticipantsTab';
import { ProjectTabsBar, type ProjectTab } from '../components/ProjectTabsBar';
import {
  AddProjectCustomerModal,
  ProjectCustomerPanel,
  ProjectCustomerReadiness,
  ProjectCustomerSigningBlock,
  ProjectCustomerStatusBadge,
  ProjectCustomerWaiting,
  ProjectCustomerChange,
  useCustomerReminder,
  projectCustomerSigningReason,
  projectCustomerState
} from '../components/ProjectCustomer';
import { getStatusLabel as getStatusLabelUtil } from '../services/projectStatusUtils';
import {
  shouldShowIncomingDirection,
  transactionBaseAmountLabelKey,
  transactionBaseAmountSek,
  transactionCustomerFeeSek,
  transactionPayoutAmountSek,
  transactionReleasedToBuilder,
  transactionStatusDisplay,
  transactionTotalAmountSek
} from '../utils/transactionDisplay';
import { canAttestMilestone } from '../utils/milestoneAttestation';
import { contractFundingConfirmed } from '../utils/timelineFunding';
import { reconcileMilestonePlanRows, type MilestonePlanWizardRow } from '../utils/milestonePlanWizard';
import {
  prepareAttachmentsForUpload,
  attachmentErrorCode,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_FORMATS_LABEL,
  MAX_ATTACHMENT_SIZE_MB,
} from '../utils/attachmentFiles';
import { typedPreviewBlob, parsePptxToHtml } from '../utils/documentPreview';
import { redirectToExternalSigning, signingHandoffDestination } from '../utils/signingNavigation';
import { signingProviderDisplayName } from '../utils/signingProvider';
import { signingReturnFeedback, signingReturnResult } from '../utils/signingReturn';
import { apiErrorCode, apiErrorText, isRequestTimeout, knownApiErrorText, saveErrorText } from '../utils/apiError';
import { milestonePaymentDisplay } from '../utils/milestonePaymentDisplay';
import { paymentTimelineDisplay } from '../utils/paymentTimelineText';
import { milestoneFormAmounts } from '../utils/milestoneFormAmounts';
import MilestoneAmountSummary from '../components/MilestoneAmountSummary';
import { FEATURE_CONTRACT_VIEW_LEGACY_SECTIONS, FEATURE_CONTRACT_WIZARD_V2, FEATURE_PROJECT_CONFLICT_SHORTCUT } from '../constants/featureFlags';
import { ContractOverview, ContractStatePill } from '../components/ContractOverview';
import { ContractSummary } from '../components/ContractSummary';
import { contractMissingItems, contractViewState } from '../utils/contractOverview';
import {
  AssignmentStep,
  AttachmentLibraryPicker,
  ContractDocumentPreview,
  ContractEditBanner,
  ContractPreviewModal,
  ContractWizardModal,
  PricingStep,
  ReviewStep,
  SignedContractView,
  WizardSummary,
  diffContract,
  hasPlanErrors,
  previewFromContract,
  planStateFromContract,
  stageMarkers,
  useContractWizardDraft,
  useDraftMessages,
  useMilestonePlanStep,
  useReviewStep,
  wizardEditStatus,
  type ContractDocumentPreviewProps,
  type ContractEditBannerStatus,
  type ContractWizardStep,
  type WizardEditStatus,
  type WizardSnapshot,
} from '../components/contract-wizard';
import { ProjectNotesGuidance } from '../components/ProjectNotesGuidance';
import MilestoneGuidance from '../components/MilestoneGuidance';
import { timelineContracts, timelineMilestones } from '../utils/timelineContracts';
import { useOngoingObjections } from '../utils/objectionVariation';
import {
  isActiveProjectHeldFundsTransaction,
  projectHeldFundsSummaryFromTimeline,
  type ProjectHeldFundsSummary as ProjectHeldFundsSummaryData,
} from '../utils/projectHeldFunds';
import {
  isConflictDeliveryStatus,
  projectWorkflowStatus,
} from '../utils/milestoneWorkflowStatus';
import type { Project, Customer, Company, ProjectStatus, Contract, ContractReplacement, SigningStatus, Milestone, MilestoneAttestationStatus, MilestoneMessage, MessageThread, MilestoneStartDecision, DocumentRecord, FolderTree, AttestationStatus, ProjectStatusMessage, ContractHistoryEntry, ProjectFundingStatus, InactivityCase } from '../types';
import { projectCompanyNameOrEmpty } from '../components/ProjectCompanyName';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

interface ProjectFormData {
  isTest?: boolean;
  customerId: string;
  description: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  externalProjectNumber: string;
  notes: string;
}

interface ContractFormData {
  contractAmount: string;
  rotAmount: string;
  afterRotAmount: string;
  contractType: 'fixed' | 'variation';
  settlementType: 'milestones' | 'price_adjustment';
  parentContractId: string | null;
}

const emptyContractForm: ContractFormData = {
  contractAmount: '',
  rotAmount: '',
  afterRotAmount: '',
  contractType: 'fixed',
  settlementType: 'milestones',
  parentContractId: null
};

interface MilestoneFormData {
  rotAmount: string;
  description: string;
  grossAmount: string;
  amount: string;
  endDate: string;
}

const emptyMilestoneForm: MilestoneFormData = {
  rotAmount: '0',
  description: '',
  grossAmount: '',
  amount: '',
  endDate: ''
};


const fallbackStatusOptions: { value: string; labelKey: string }[] = [
  { value: 'not_started', labelKey: 'projectView.statusOptions.not_started' },
  { value: 'contract', labelKey: 'projectView.statusOptions.contract' },
  { value: 'payment', labelKey: 'projectView.statusOptions.payment' },
  { value: 'in_progress', labelKey: 'projectView.statusOptions.in_progress' },
  { value: 'milestone', labelKey: 'projectView.statusOptions.milestone' },
  { value: 'on_hold', labelKey: 'projectView.statusOptions.on_hold' },
  { value: 'conflict', labelKey: 'projectView.statusOptions.conflict' },
  { value: 'dispute', labelKey: 'projectView.statusOptions.dispute' },
  { value: 'completed', labelKey: 'projectView.statusOptions.completed' }
];

const activeSigningStatuses: SigningStatus[] = ['sent', 'pending_signature'];
const incompleteSigningStatuses: SigningStatus[] = ['declined', 'expired', 'failed'];

const isActiveSigningStatus = (status?: SigningStatus) =>
  !!status && activeSigningStatuses.includes(status);

const needsSigningSync = (contract: Contract) =>
  isActiveSigningStatus(contract.signingStatus) ||
  (['proposed', 'settlement_pending'].includes(contract.versionStatus ?? '') && contract.companySigned && contract.personSigned);

const isIncompleteSigningStatus = (status?: SigningStatus) =>
  !!status && incompleteSigningStatuses.includes(status);

const formatSigningRejectedAt = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTransactionDate = (value?: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const timelinePayoutTransferDates = (txn: any) => {
  const payout = txn?.metadata?.payout;
  if (!payout || typeof payout !== 'object') {
    return { bookingDate: null, valueDate: null };
  }
  return {
    bookingDate: formatTransactionDate(payout.adyenTransferBookingDate),
    valueDate: formatTransactionDate(payout.adyenTransferValueDate)
  };
};

const capabilityIsValid = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const capability = value as { allowed?: unknown; verificationStatus?: unknown; status?: unknown };
  return capability.allowed === true || capability.verificationStatus === 'valid' || capability.status === 'valid';
};

const hasLocalMockAdyenId = (value: string | null | undefined, prefix: string): boolean =>
  value ? new RegExp(`^${prefix}-[0-9a-f-]{20,}$`, 'i').test(value) : false;

const adyenAccountAllowsPayments = (account: AdyenAccount | null): boolean => {
  if (!account) return false;
  if (
    hasLocalMockAdyenId(account.adyenAccountHolderId, 'AH') ||
    hasLocalMockAdyenId(account.adyenLegalEntityId, 'LE') ||
    hasLocalMockAdyenId(account.adyenBalanceAccountId, 'BA') ||
    hasLocalMockAdyenId(account.transferInstrumentId, 'SE')
  ) {
    return false;
  }
  const required = account.requiredCapabilities ?? [];
  return (
    account.payoutsAllowed === true &&
    required.length > 0 &&
    required.every((capability) => capabilityIsValid(account.capabilities?.[capability]))
  );
};

type TabType = 'overview' | 'contract' | 'participants' | 'documents' | 'timeline' | 'transactions' | 'marketplace';

const PROJECT_VIEW_TABS: readonly TabType[] = [
  'overview',
  'contract',
  'participants',
  'documents',
  'timeline',
  'transactions',
  'marketplace'
];
type ContractSortField = 'contractNumber' | 'description' | 'contractAmount' | 'paidAmount' | 'remainingAmount';
type SortDirection = 'asc' | 'desc';

const descriptionQuillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'],
    ['clean']
  ]
};

const descriptionQuillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'link'
];

const parseSekInput = (value: string): number | null => {
  const normalized = String(value || '')
    .replace(/\s/g, '')
    .replace(',', '.')
    .replace(/[^0-9.-]/g, '');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};


const effectiveContractVersions = (items: Contract[]) => {
  const byChain = new Map<string, Contract[]>();
  items.forEach((contract) => {
    if (contract.versionStatus === 'superseded' || contract.versionStatus === 'activation_failed') {
      return;
    }
    const chainId = contract.contractChainId ?? contract.id;
    byChain.set(chainId, [...(byChain.get(chainId) ?? []), contract]);
  });
  return [...byChain.values()].map((versions) => {
    const signedPending = versions
      .filter(
        (contract) => contract.versionStatus === 'settlement_pending' && contract.companySigned && contract.personSigned
      )
      .sort((a, b) => (b.contractVersion ?? 1) - (a.contractVersion ?? 1))[0];
    return (
      signedPending ??
      timelineContracts(versions)[0] ??
      [...versions].sort((a, b) => (b.contractVersion ?? 1) - (a.contractVersion ?? 1))[0]
    );
  });
};

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const WORD_EXTENSIONS = ['.doc', '.docx'];
const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];
const PPTX_EXTENSIONS = ['.ppt', '.pptx'];

function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.substring(idx).toLowerCase() : '';
}

export function ProjectView() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { confirmOverlap, confirmationDialog: resolutionOverlapDialog } = useResolutionOverlapWarning(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const showAdyenOnboardingPrompt = useAdyenOnboardingPrompt(user?.personaType === 'company' && !user?.isAdmin);
  const isPerson = user?.personaType === 'person';
  // The stages' conversations, for "Läst · väntar på svar" on the stage card (ClickUp 869feg849).
  const [projectThreads, setProjectThreads] = useState<MessageThread[]>([]);
  const loadProjectThreads = useCallback(async () => {
    if (!id || !['person', 'company', 'builder'].includes(user?.personaType ?? '') || user?.isAdmin) return;
    try {
      const threads = await messageThreadApi.list(id);
      setProjectThreads(Array.isArray(threads) ? threads : []);
    } catch {
      // The stage cards work without it.
    }
  }, [id, user?.personaType, user?.isAdmin]);
  useEffect(() => { void loadProjectThreads(); }, [loadProjectThreads]);
  useRefreshOnNotification(loadProjectThreads);
  const projectThreadsRef = useRef(projectThreads);
  projectThreadsRef.current = projectThreads;
  // Reading a stage's dialog reads its thread when it has unread messages; the
  // menu badge and stage card follow.
  const readStageThread = (milestoneId: string) => {
    const thread = projectThreadsRef.current.find(item => item.threadType === 'milestone' && item.threadId === milestoneId);
    if (!thread?.unreadCount) return;
    void Promise.resolve()
      .then(() => messageThreadApi.markRead(milestoneId))
      .then(() => Promise.all([refreshMessageSummary(), loadProjectThreads()]))
      .catch(() => {});
  };
  // After a reply the stage card and the badge show the new state.
  const refreshStageThreads = () => {
    void Promise.resolve()
      .then(() => Promise.all([refreshMessageSummary(), loadProjectThreads()]))
      .catch(() => {});
  };
  // Person and company both get the in-project marketplace tab (company =
  // any non-admin account that isn't person/lawyer/inspector).
  const canUseProjectMarketplace = isPerson || (
    !!user && !user.isAdmin
    && user.personaType !== 'person'
    && user.personaType !== 'lawyer'
    && user.personaType !== 'inspector'
    && user.personaType !== 'admin'
  );
  const basePath = isPerson ? '/person/projects' : user?.isAdmin ? '/admin/projects' : '/projects';
  const initialContractId = searchParams.get('contractId');
  const initialContractNumber = searchParams.get('contractNumber') ? parseInt(searchParams.get('contractNumber')!, 10) : null;
  const signingResultParam = searchParams.get('signingResult');
  const requestedMilestoneId = searchParams.get('milestoneId');
  const requestedMilestoneAction = searchParams.get('action');
  const [activeTab, selectTab] = useProjectTabNavigation(PROJECT_VIEW_TABS, 'timeline');
  // "Lägg till beställare" (ClickUp 869ff1egy): which tab the dialog opens on.
  const [addCustomerTab, setAddCustomerTab] = useState<'invite' | 'pick' | null>(null);
  const paymentStatusParam = searchParams.get('paymentStatus');
  const [projectCompany, setProjectCompany] = useState<Company | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  // "Skicka påminnelse" in the contract wizard and its notice (ClickUp 869fn1nyr).
  const customerReminder = useCustomerReminder(project, () => void loadData());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    customerId: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'not_started',
    externalProjectNumber: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Contract state
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractReplacements, setContractReplacements] = useState<Record<string, ContractReplacement>>({});
  const [isUpdatingReplacementProposal, setIsUpdatingReplacementProposal] = useState(false);
  const [stageVariationDirty, setStageVariationDirty] = useState(false);
  // A variation (ÄTA) can only be linked to an origin contract that both
  // parties have already signed, so only those are eligible parents.
  const eligibleParentContracts = contracts.filter(
    c => c.draftStatus === 'signed_by_both' && (c.versionStatus ?? 'current') === 'current'
  );
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  // The URL identifies a return, but its result may already be out of date.
  // Derive feedback from the same contract data that drives the rest of the view.
  const returnResult = signingResultParam && selectedContract?.id === initialContractId
    ? signingReturnResult(selectedContract, user?.personaType)
    : null;
  const returnFeedback = signingReturnFeedback(returnResult);
  const returnFeedbackKey = `${initialContractId}:${returnResult}`;
  const [dismissedReturnFeedback, setDismissedReturnFeedback] = useState<string | null>(null);
  const [configuredSigningProvider, setConfiguredSigningProvider] = useState<
    string | null
  >(null);
  const selectedContractRef = useRef<Contract | null>(null);
  const signingSyncsInFlight = useRef(new Set<string>());
  const selectedReplacementSettlement = selectedContract
    ? contractReplacements[selectedContract.id]
    : undefined;
  const isStageVariation = !!selectedReplacementSettlement?.amendmentScope &&
    selectedReplacementSettlement.replacementContractId === selectedContract?.id;
  const isBuyerProposalAwaitingSupplier =
    selectedReplacementSettlement?.initiatorRole === 'person' &&
    selectedReplacementSettlement.proposalStatus === 'pending';
  const isRejectedDraft = selectedContract?.draftStatus === 'draft_rejected';
  const isDeclinedSigning = selectedContract?.signingStatus === 'declined';
  const isRejectedContract = isRejectedDraft || isDeclinedSigning;
  const isSignedContract =
    selectedContract?.signingStatus === 'signed' ||
    selectedContract?.draftStatus === 'signed_by_both' ||
    Boolean(selectedContract?.companySigned && selectedContract?.personSigned);
  const isProjectClosedForSigning = ['completed', 'dispute'].includes(
    project?.status ?? ''
  );
  const hasExternalSigningProcess = Boolean(
    selectedContract?.externalSigningProcessId
  );
  const hasIncompleteSigning = isIncompleteSigningStatus(
    selectedContract?.signingStatus
  );
  const signingRejectedAtLabel = formatSigningRejectedAt(
    selectedContract?.signingRejectedAt
  );
  const signingRejectedByLabel = selectedContract?.signingRejectedByRole
    ? t(
        `contracts.signingStatus.rejectedByRole.${selectedContract.signingRejectedByRole}`
      )
    : null;
  const signingRejectedByName = selectedContract?.signingRejectedByName ?? null;
  const signingRejectionDetail =
    signingRejectedByLabel || signingRejectedAtLabel || signingRejectedByName
      ? t(
          signingRejectedByName
            ? 'contracts.signingStatus.rejectionDetailNamed'
            : 'contracts.signingStatus.rejectionDetail',
          {
            role:
              signingRejectedByLabel ??
              t('contracts.signingStatus.rejectedByRole.unknown'),
            name: signingRejectedByName ?? '',
            date:
              signingRejectedAtLabel ?? t('contracts.signingStatus.unknownDate')
          }
        )
      : null;
  const hasCompanyCompletedSigning =
    !!selectedContract?.companySigned ||
    selectedContract?.draftStatus === 'signed_by_company' ||
    selectedContract?.draftStatus === 'signed_by_both';
  const isCompanySigned =
    !isPerson &&
    hasCompanyCompletedSigning &&
    !selectedContract?.adminUnlocked &&
    !isRejectedDraft &&
    !isDeclinedSigning;
  // Mirrors the backend's single lock rule (isContractLockedForEditing): a
  // contract is locked for content edits once signing has started (sent for
  // signing) OR any party has signed. Broader than isCompanySigned, which only
  // covers the company having signed — using the broader rule here stops the UI
  // from letting the user edit milestones the backend will reject with 400.
  const contractLockedForEditing =
    !isPerson &&
    !!selectedContract &&
    !selectedContract.adminUnlocked &&
    selectedContract.draftStatus !== 'draft_rejected' &&
    (['sent', 'pending_signature', 'signed'].includes(
      selectedContract.signingStatus ?? ''
    ) ||
      ['signed_by_company', 'signed_by_person', 'signed_by_both'].includes(
        selectedContract.draftStatus ?? ''
      ) ||
      !!selectedContract.companySigned ||
      !!selectedContract.personSigned);
  const isPriceAdjustmentVariation =
    selectedContract?.settlementType === 'price_adjustment';
  const isZeroValuePriceAdjustmentVariation =
    isPriceAdjustmentVariation &&
    (Number(
      String(
        selectedContract?.afterRotAmount ?? selectedContract?.contractAmount ?? '0'
      ).replace(/[^0-9.-]/g, '')
    ) || 0) === 0;
  // Attachments are part of the signed agreement and must be locked under the
  // same rule the backend enforces (contract-lock.util): once signing has
  // started (sent) or any party has signed, via either the external or manual
  // flow. Admin unlock / rejected draft re-opens it.
  const isAttachmentLocked =
    !selectedContract?.adminUnlocked &&
    !isRejectedDraft &&
    (['sent', 'pending_signature', 'signed'].includes(
      selectedContract?.signingStatus ?? ''
    ) ||
      ['signed_by_company', 'signed_by_person', 'signed_by_both'].includes(
        selectedContract?.draftStatus ?? ''
      ) ||
      !!selectedContract?.companySigned ||
      !!selectedContract?.personSigned);
  const signingProviderKey =
    selectedContract?.externalSigningProcessId &&
    isActiveSigningStatus(selectedContract.signingStatus)
      ? selectedContract.signingProvider
      : configuredSigningProvider;
  const signingProviderName =
    signingProviderDisplayName(signingProviderKey) ??
    t('contracts.signingProviders.external');
  const reviewInfoCards = useMemo(() => ([
    {
      icon: 'file-text',
      title: t('contracts.actions.reviewCards.contentTitle'),
      text: t('contracts.actions.reviewCards.contentText')
    },
    {
      icon: 'message-square',
      title: t('contracts.actions.reviewCards.feedbackTitle'),
      text: t('contracts.actions.reviewCards.feedbackText')
    },
    {
      icon: 'external-link',
      title: t('contracts.actions.reviewCards.signingTitle', {
        provider: signingProviderName
      }),
      text: t('contracts.actions.reviewCards.signingText', {
        provider: signingProviderName
      })
    }
  ]), [signingProviderName, t]);
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [contractFormData, setContractFormData] = useState<ContractFormData>(emptyContractForm);
  // Latest form values for the short-description autosave (read after awaits).
  const [contractFormErrors, setContractFormErrors] = useState<Record<string, string>>({});
  const [nextContractNumber, setNextContractNumber] = useState(1);
  const [showContractWizard, setShowContractWizard] = useState(false);
  // Step the wizard opens on: 0, or 1 ("Pris & etappmål") from the row's "Etappmål".
  const [contractWizardStartStep, setContractWizardStartStep] = useState(0);
  // The step on screen; null until the user moves (then the start step shows).
  const [contractWizardStepIndex, setContractWizardStepIndex] = useState<number | null>(null);
  // "Visa hela kontraktet" on the contract tab: the wizard's review, read-only.
  const [contractWizardViewOnly, setContractWizardViewOnly] = useState(false);
  // "Förhandsgranska": the read-only view offers "Gå till signering" while the
  // company can still sign the contract.
  const [contractWizardCanSign, setContractWizardCanSign] = useState(false);
  // The person's review opens as a preview from "Förhandsgranska" (869fmxfb7).
  const [personReviewAsPreview, setPersonReviewAsPreview] = useState(false);
  const contractWizardDraft = useContractWizardDraft(id);
  const contractWizardPlan = useMilestonePlanStep(contractWizardDraft.contract?.id);
  const [contractWizardReachedPricing, setContractWizardReachedPricing] = useState(false);
  const contractWizardReview = useReviewStep(contractWizardDraft.contract?.id);
  const [contractWizardCompany, setContractWizardCompany] = useState<{ name: string; organizationNumber: string }>({
    name: '',
    organizationNumber: '',
  });
  // Edit mode: the state the contract opened in and the version it opened with
  // (the baseline for "Ändringar sedan version N"). null when creating.
  const [contractWizardEdit, setContractWizardEdit] = useState<{
    status: WizardEditStatus;
    /** Was sent; the company withdrew the sending in this session. */
    withdrawn: boolean;
    snapshot: WizardSnapshot;
    contract: Contract;
  } | null>(null);
  // Confirmation before a sent contract is withdrawn (asked on the first write).
  const [contractWizardWithdraw, setContractWizardWithdraw] = useState<{ busy: boolean; error: string } | null>(null);
  const [contractWizardStopError, setContractWizardStopError] = useState('');
  const contractWizardWithdrawRef = useRef<{ promise: Promise<boolean>; resolve: (ok: boolean) => void } | null>(null);
  const [showCreateContractTypePrompt, setShowCreateContractTypePrompt] = useState(false);
  // "Vad vill du skapa?" (ClickUp 869fh4c2b): the chosen flow is being opened, or failed to open.
  const [agreementChoiceOpening, setAgreementChoiceOpening] = useState(false);
  const [agreementChoiceError, setAgreementChoiceError] = useState('');
  const [showChangeTypeConfirm, setShowChangeTypeConfirm] = useState(false);
  const [showCancelNewContractConfirm, setShowCancelNewContractConfirm] = useState(false);
  const [isSavingNewContract, setIsSavingNewContract] = useState(false);
  const [pendingScrollSection, setPendingScrollSection] = useState<'milestones' | 'history' | 'edit' | 'reference' | null>(null);
  const [deleteDraftTarget, setDeleteDraftTarget] = useState<Contract | null>(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [deleteDraftError, setDeleteDraftError] = useState('');
  const [contractReference, setContractReference] = useState<{
    projectId: string;
    contractId: string;
    message: MilestoneMessage;
  } | null>(null);
  const [referenceLoadError, setReferenceLoadError] = useState(false);
  const [referenceReload, setReferenceReload] = useState(0);
  const referenceContractId = selectedContract?.id;
  useEffect(() => {
    if (!id || isPerson || !referenceContractId) return;
    let cancelled = false;
    const contractId = referenceContractId;
    setReferenceLoadError(false);
    void contractApi.getCounterproposalReference(contractId).then(reference => {
      if (cancelled) return;
      setContractReference(reference ? { projectId: id, contractId, message: reference } : null);
    }).catch(() => { if (!cancelled) setReferenceLoadError(true); });
    return () => { cancelled = true; };
  }, [id, isPerson, referenceContractId, referenceReload]);
  const [showAssignmentRequired, setShowAssignmentRequired] = useState(false);
  const [assignmentRequiredMissing, setAssignmentRequiredMissing] = useState<{
    name: boolean;
    assignment: boolean;
  }>({ name: false, assignment: false });
  const [createTypeChoice, setCreateTypeChoice] = useState<'fixed' | 'variation'>('fixed');
  const [conflictVariationDraft, setConflictVariationDraft] = useState<{
    milestone: Milestone;
    reason: string;
    reference?: MilestoneMessage;
  } | null>(null);
  const [showConflictStagePicker, setShowConflictStagePicker] = useState(false);
  const [stageVariationDraft, setStageVariationDraft] = useState<{
    contract: Contract;
    stages: Milestone[];
    entryStageId?: string;
    initialReason: string;
    reference?: MilestoneMessage;
    existing?: Contract;
    settlement?: ContractReplacement;
    initialStep?: number;
    viewOnly?: boolean;
    canSign?: boolean;
  } | null>(null);
  const [variationAgreementId, setVariationAgreementId] = useState<string | null>(null);
  const [createTypeParentId, setCreateTypeParentId] = useState<string>('');
  const [createSettlementType, setCreateSettlementType] = useState<'milestones' | 'price_adjustment'>('milestones');
  const [createPriceAdjustmentAmount, setCreatePriceAdjustmentAmount] = useState('');
  const [negativeVariationCapacityOre, setNegativeVariationCapacityOre] = useState<number | null>(null);
  const [negativeVariationCapacityLoading, setNegativeVariationCapacityLoading] = useState(false);
  const [showNegativeVariationConfirm, setShowNegativeVariationConfirm] = useState(false);
  const [negativeVariationConfirmAction, setNegativeVariationConfirmAction] = useState<
    'create' | 'milestone_plan' | 'edit_price_adjustment' | null
  >(null);
  const [negativeVariationConfirmAmountOre, setNegativeVariationConfirmAmountOre] = useState(0);
  const [contractSortField, setContractSortField] = useState<ContractSortField>('contractNumber');
  const [contractSortDirection, setContractSortDirection] = useState<SortDirection>('asc');
  const [contractFilters] = useState<Record<string, string>>({});

  // The first contract is created with the project. Finish preparing every
  // active draft before offering another contract, including after a reload.
  const activeContracts = contracts.filter(contract =>
    contract.versionStatus !== 'superseded' && contract.versionStatus !== 'activation_failed'
  );
  const canCreateContract = !isPerson &&
    !isCreatingContract && !showCreateContractTypePrompt && !stageVariationDraft &&
    activeContracts.length > 0 && activeContracts.every(contract =>
      !contract.adminUnlocked && contract.draftStatus !== 'draft_rejected' &&
      !['declined', 'expired', 'failed'].includes(contract.signingStatus ?? '') &&
      (['sent', 'pending_signature', 'signed'].includes(contract.signingStatus ?? '') ||
        ['signed_by_company', 'signed_by_person', 'signed_by_both'].includes(contract.draftStatus) ||
        contract.companySigned || contract.personSigned)
    );


  // All milestones grouped per contract — used by the per-contract timelines
  const [contractMilestonesMap, setContractMilestonesMap] = useState<Record<string, Milestone[]>>({});
  const [inactivityCases, setInactivityCases] = useState<InactivityCase[]>([]);
  const [isDownloadingFinalReport, setIsDownloadingFinalReport] = useState(false);

  const loadContractReplacements = async (items: Contract[]) => {
    const newestVersionByChain = new Map<string, Contract>();
    items.forEach((contract) => {
      if ((contract.contractVersion ?? 1) <= 1 && !contract.replacesContractId && !contract.supersededByContractId) {
        return;
      }
      const chainId = contract.contractChainId ?? contract.id;
      const current = newestVersionByChain.get(chainId);
      if (!current || (contract.contractVersion ?? 1) > (current.contractVersion ?? 1)) {
        newestVersionByChain.set(chainId, contract);
      }
    });
    const settlements = await Promise.all(
      [...newestVersionByChain.values()].map((contract) => contractApi.getReplacement(contract.id).catch(() => null))
    );
    const byContract: Record<string, ContractReplacement> = {};
    settlements.forEach((settlement) => {
      if (!settlement) return;
      byContract[settlement.originalContractId] = settlement;
      byContract[settlement.replacementContractId] = settlement;
    });
    setContractReplacements(byContract);
  };

  // Milestone state
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const canAdjustPaidMilestone = !!selectedContract?.replacesContractId && !contractLockedForEditing;
  const milestoneLockedForEditing = Boolean(selectedMilestone?.stageReplacement || selectedMilestone?.continuedBy) || contractLockedForEditing || (selectedMilestone?.deliveryStatus === 'paid' && !canAdjustPaidMilestone);
  const [isCreatingMilestone, setIsCreatingMilestone] = useState(false);
  const [milestoneFormData, setMilestoneFormData] = useState<MilestoneFormData>(emptyMilestoneForm);
  const [milestoneFormErrors, setMilestoneFormErrors] = useState<Record<string, string>>({});
  const [isSavingMilestone, setIsSavingMilestone] = useState(false);
  const [nextMilestoneNumber, setNextMilestoneNumber] = useState(1);

  // Saving flag shared by the guided wizard submit.
  const [isSavingPending, setIsSavingPending] = useState(false);
  // Which milestone modal a close was requested for while it had unsaved edits —
  // drives the small "discard unsaved changes?" confirmation modal.
  const [pendingCloseTarget, setPendingCloseTarget] = useState<'milestone' | 'wizard' | null>(null);
  const [showDeleteMilestoneConfirm, setShowDeleteMilestoneConfirm] = useState(false);

  // Guided milestone-plan wizard (parallel, additive — does not touch the
  // existing "Skapa Etappmål" count-prompt + pending-rows flow above).
  const [milestoneWizardOpen, setMilestoneWizardOpen] = useState(false);
  const [wizStep, setWizStep] = useState(0); // 0=total, 1=count, 2=distribute
  const [wizTotal, setWizTotal] = useState('');
  const [wizCount, setWizCount] = useState('1');
  const [wizMode, setWizMode] = useState<'amount' | 'percent'>('amount');
  const [wizAfterRotValue, setWizAfterRotValue] = useState('');
  const [wizRows, setWizRows] = useState<MilestonePlanWizardRow[]>([]);
  const [wizError, setWizError] = useState('');
  const wizDistributionBasisRef = useRef('');

  // Contract description state
  const [contractName, setContractName] = useState('');
  const [assignmentDescription, setAssignmentDescription] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [variationOtherDescription, setVariationOtherDescription] = useState('');
  const [variationReason, setVariationReason] = useState('');
  // When checked, the contract refers to the attached documents (offer/agreement)
  // as its assignment description (combinable with free text).
  const [referToAttachments, setReferToAttachments] = useState(false);
  const [assignmentVersions, setAssignmentVersions] = useState<Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }>>([]);
  const [otherVersions, setOtherVersions] = useState<Array<{ version: string; content: string; savedBy: string; savedByName: string; savedAt: string }>>([]);
  // Per-field autosave state (replaces the former "Spara" button flow).
  type DescriptionField = 'contractName' | 'assignmentDescription' | 'other';
  const descSavedValuesRef = useRef<Record<DescriptionField, string>>({
    contractName: '',
    assignmentDescription: '',
    other: ''
  });
  const descSavingRef = useRef<Set<DescriptionField>>(new Set());
  // Latest editor values, readable after an await or from a stale closure.
  const descValuesRef = useRef<Record<DescriptionField, string>>({
    contractName: '',
    assignmentDescription: '',
    other: ''
  });
  descValuesRef.current = {
    contractName,
    assignmentDescription,
    other: otherDescription
  };
  // Fields left (blurred) while their previous save was still in flight. They
  // are saved again as soon as that save returns, instead of being dropped.
  const descPendingRef = useRef<Set<DescriptionField>>(new Set());
  // Fields the user has edited since they were last saved. Set from the input
  // handlers (not by comparing values), so a reload racing the first render
  // cannot mistake a not-yet-rendered value for a local edit.
  const descDirtyRef = useRef<Set<DescriptionField>>(new Set());
  const descErrorFieldsRef = useRef<Set<DescriptionField | 'referToAttachments'>>(new Set());
  // The contract whose descriptions are loaded; a reload of the same contract
  // must keep unsaved local text.
  const descContractIdRef = useRef<string | null>(null);
  const referSavingRef = useRef(false);
  const referSavedRef = useRef(false);
  const referDesiredRef = useRef(false);
  const [descAutoSaveStatus, setDescAutoSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [descSavedAtLabel, setDescSavedAtLabel] = useState('');
  const [descAutoSaveError, setDescAutoSaveError] = useState<{
    message: string;
    retry: () => void;
  } | null>(null);
  const [contractNameError, setContractNameError] = useState('');
  const [descLockedByServer, setDescLockedByServer] = useState(false);

  // Contract attachments state
  const [contractAttachments, setContractAttachments] = useState<DocumentRecord[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [removeAttachmentTarget, setRemoveAttachmentTarget] = useState<DocumentRecord | null>(null);
  const [attachmentDetailsTarget, setAttachmentDetailsTarget] = useState<DocumentRecord | null>(null);
  // Attachment preview on top of the review/signing view (shared document viewer).
  const attachmentPreview = useDocumentPreview();
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState<DocumentRecord[]>([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // The Beställare's review and signing (PersonContractReview).
  const [showPreview, setShowPreview] = useState(false);
  // The Hantverkare's read-only "Förhandsgranska" for a contract neither wizard
  // can open (ContractPreviewModal).
  const [companyPreviewContractId, setCompanyPreviewContractId] = useState<string | null>(null);
  const [signingStartDecisions, setSigningStartDecisions] = useState<
    MilestoneStartDecision[] | undefined
  >();
  // Reusable confirmation overlay — shown before signing / approving /
  // rejecting / sending a draft to prevent accidental clicks.
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    confirmVariant: 'primary' | 'success' | 'danger' | 'warning';
    icon: string;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  // Conversation thread tied to the contract's draft.
  const [draftMessages, setDraftMessages] = useState<Array<{
    id: string; userId: string; userName: string; userRole: string;
    message: string; draftAction?: string; createdAt: string;
  }>>([]);
  const [contractHistory, setContractHistory] = useState<ContractHistoryEntry[]>([]);
  const [isOpeningSigning, setIsOpeningSigning] = useState(false);
  const [signingError, setSigningError] = useState<string | null>(null);
  // Global construction agreement (byggavtal) preview state — DISCONNECTED for MVP,
  // kept for future reactivation (feat/kontrakt_andringar).
  // const [previewAgreementText, setPreviewAgreementText] = useState('');
  // const [previewAgreementDate, setPreviewAgreementDate] = useState('');
  const [previewCompanyName, setPreviewCompanyName] = useState('');
  const [previewCompanyOrgNr, setPreviewCompanyOrgNr] = useState('');

  const applyContractUpdate = useCallback((contract: Contract) => {
    setContracts(prev =>
      prev.map(item => {
        if (item.id !== contract.id) return item;
        return JSON.stringify(item) === JSON.stringify(contract) ? item : contract;
      })
    );
    setSelectedContract(prev => {
      if (prev?.id !== contract.id) return prev;
      return JSON.stringify(prev) === JSON.stringify(contract) ? prev : contract;
    });
  }, []);

  const syncContractSigning = useCallback(async (contract: Contract) => {
    if (!contract.externalSigningProcessId) return;
    if (!needsSigningSync(contract)) return;
    if (signingSyncsInFlight.current.has(contract.id)) return;
    signingSyncsInFlight.current.add(contract.id);
    try {
      const synced = await contractApi.syncSigning(contract.id);
      applyContractUpdate(synced.contract);
      if (contract.companySigned !== synced.contract.companySigned || contract.personSigned !== synced.contract.personSigned || contract.signingStatus !== synced.contract.signingStatus || contract.versionStatus !== synced.contract.versionStatus) {
        setFundingRefreshToken(value => value + 1);
        void loadProjectStatusSteps(synced.contract.projectId);
        if (selectedContractRef.current?.id === synced.contract.id) {
          void loadContractHistory(synced.contract.id);
          void documentApi.listDocuments(synced.contract.projectId, synced.contract.contractNumber)
            .then(docs => {
              if (selectedContractRef.current?.id === synced.contract.id) setContractAttachments(docs);
            }).catch(() => {});
        }
      }
      if (contract.versionStatus !== 'current' && synced.contract.versionStatus === 'current' &&
        synced.contract.replacesContractId) {
        // Activation also supersedes the source version. Refresh the whole list
        // so contract history gets the same lifecycle change as the synced row.
        setContracts(await contractApi.getContracts(synced.contract.projectId));
      }
    } catch (error) {
      console.error('Failed to sync signing status:', error);
    } finally {
      signingSyncsInFlight.current.delete(contract.id);
    }
  }, [applyContractUpdate]);

  const syncOpenContractSignings = useCallback(async (items: Contract[]) => {
    const signingsToSync = items.filter(contract =>
      !!contract.externalSigningProcessId &&
      needsSigningSync(contract)
    );
    if (signingsToSync.length === 0) return;
    await Promise.all(signingsToSync.map(contract => syncContractSigning(contract)));
  }, [syncContractSigning]);

  // Document state
  const [folderTree, setFolderTree] = useState<FolderTree | null>(null);
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const milestoneFormRef = useRef<HTMLDivElement>(null);
  const projectEventRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Participants state
  const [participants, setParticipants] = useState<any>(null);
  const [contractConfigs, setContractConfigs] = useState<any[]>([]);
  const [projectContract, setProjectContract] = useState<any>(null);
  const [simulationResult, setSimulationResult] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'bank' | 'swish' | 'card'>('bank');
  const [selectedPaymentContractId, setSelectedPaymentContractId] = useState<string | null>(null);
  const [selectedPaymentMilestoneId, setSelectedPaymentMilestoneId] = useState<string | null>(null);
  const [paymentInterestNotice, setPaymentInterestNotice] = useState<string | null>(null);
  const [fundingRefreshToken, setFundingRefreshToken] = useState(0);
  const [projectFunding, setProjectFunding] = useState<ProjectFundingStatus | null>(null);
  const isProjectFunded = projectFunding?.isFunded === true;
  const [showAttestModal, setShowAttestModal] = useState(false);
  // The attestation was opened from a notification or e-mail (ClickUp 869fnf0e5).
  const [attestOpenedFromLink, setAttestOpenedFromLink] = useState(false);
  const [attestModalMode, setAttestModalMode] = useState<'attestation' | 'resolution'>('attestation');
  const [showStatusChangeModal, setShowStatusChangeModal] = useState<'on_hold' | 'resume' | null>(null);
  const [statusMessages, setStatusMessages] = useState<ProjectStatusMessage[]>([]);
  const [attestMilestone, setAttestMilestone] = useState<Milestone | null>(null);
  const [milestoneMessages, setMilestoneMessages] = useState<MilestoneMessage[]>([]);
  const [allMilestoneMessages, setAllMilestoneMessages] = useState<Record<string, MilestoneMessage[]>>({});
  const [latestDisputeReplies, setLatestDisputeReplies] = useState<Record<string, MilestoneMessage>>({});
  const [latestCounterpartyReplies, setLatestCounterpartyReplies] = useState<Record<string, MilestoneMessage>>({});
  // Whose turn it is in a stage's objection dialogue: whoever wrote last waits;
  // none yet: the company answers. Shared by the overview, the contract cards
  // and "Aktuellt etappmål" (ClickUp 869fj6n8x).
  const objectionTurn = (milestone: Milestone): 'person' | 'company' =>
    objectionTurnFromRole(latestDisputeReplies[milestone.id]?.userRole);
  const [progressingMilestoneId, setProgressingMilestoneId] = useState<string | null>(null);
  const handledMilestoneDeepLinkRef = useRef<string | null>(null);
  // Set below, once the remedy resubmission handler is defined.
  const openRemediedAttestationRef = useRef<((milestone: Milestone) => Promise<void>) | null>(null);
  const [projectEconomics, setProjectEconomics] = useState<any>(null);
  // Per-method economics (Trustly/Swish) for the payment modal so every
  // method card shows its own correct total at a glance, not just the
  // selected one.
  const [methodEcos, setMethodEcos] = useState<Record<string, any>>({});
  const [paymentEconomicsLoading, setPaymentEconomicsLoading] = useState(false);
  const [paymentEconomicsError, setPaymentEconomicsError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [pingPayouts, setPingPayouts] = useState<PingPayout[]>([]);
  const [heldFundsSummary, setHeldFundsSummary] = useState<ProjectHeldFundsSummaryData>({
    amountOre: 0,
    amountSek: 0,
    currency: 'SEK',
    items: [],
  });
  // Every signed ÄTA, also as a row of its own in the list (ClickUp 869fnvpvp).
  const signedVariations = useMemo(() => variationChanges(contracts), [contracts]);
  const visibleTransactions = useMemo(
    () => withVariationRows(
      transactions.filter(transaction => !isActiveProjectHeldFundsTransaction(transaction)),
      signedVariations,
    ),
    [transactions, signedVariations],
  );
  const applyProjectPaymentTimeline = useCallback((timeline: any) => {
    const items = Array.isArray(timeline?.items) ? timeline.items : [];
    setTransactions(items);
    setHeldFundsSummary(projectHeldFundsSummaryFromTimeline(timeline, items));
  }, []);
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState(false);
  const hasConfirmedPaymentTransaction = transactions.some((txn: any) =>
    ['payment_received', 'escrow_held'].includes(txn.type) ||
    ['completed', 'held', 'released', 'releasing'].includes(txn.status)
  );
  const hasRegisteredPaymentTransaction = transactions.some((txn: any) =>
    txn.type === 'payment_pending'
  );
  const latestConfirmedPaymentAt = Math.max(
    0,
    ...transactions
      .filter((txn: any) => ['payment_received', 'escrow_held'].includes(txn.type))
      .map((txn: any) => new Date(txn.occurredAt || txn.createdAt || 0).getTime())
      .filter(Number.isFinite)
  );
  const latestRefundAt = Math.max(
    0,
    ...transactions
      .filter((txn: any) => txn.type === 'payment_refund')
      .map((txn: any) => new Date(txn.occurredAt || txn.createdAt || 0).getTime())
      .filter(Number.isFinite)
  );
  const paymentConfirmationSuperseded =
    latestRefundAt > 0 && latestRefundAt >= latestConfirmedPaymentAt;
  const showPaymentCompletedNotice = activeTab === 'transactions' && (
    (paymentStatusParam === 'completed' && hasConfirmedPaymentTransaction) ||
    (paymentStatusParam === 'registered' && hasRegisteredPaymentTransaction)
  ) && !paymentConfirmationSuperseded;
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);

  // Project status instance
  const [projectStatusSteps, setProjectStatusSteps] = useState<any[]>([]);

  useEffect(() => {
    selectedContractRef.current = selectedContract;
  }, [selectedContract]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(contractConfigApi.getAdyenRuntime?.())
      .then(runtime => {
        if (cancelled) return;
        setCardPaymentsEnabled(runtime?.cardPaymentsEnabled === true);
        if (runtime?.cardPaymentsEnabled !== true) {
          setSelectedPaymentMethod('bank');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCardPaymentsEnabled(false);
          setSelectedPaymentMethod('bank');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOptions = useMemo(() => {
    const isEn = (i18next.language || 'sv').toLowerCase().startsWith('en');
    if (projectStatusSteps.length === 0) {
      return fallbackStatusOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }));
    }
    return projectStatusSteps.map((step: any) => ({
      value: step.key,
      label: isEn && step.label_en ? step.label_en : step.label_sv
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectStatusSteps, t, i18next.language]);


  const loadProjectStatusSteps = async (projectId: string) => {
    try {
      const data = await projectStatusApi.getInstance(projectId);
      setProjectStatusSteps(data.steps || []);
    } catch {
      setProjectStatusSteps([]);
    }
  };

  useEffect(() => {
    loadData();
    contractApi
      .getSigningProvider()
      .then(({ provider }) => setConfiguredSigningProvider(provider))
      .catch(() => setConfiguredSigningProvider(null));
    // Load contract config data and status instance
    if (id) {
      contractConfigApi.getProjectContract(id).then(setProjectContract).catch(() => setProjectContract(null));
      loadProjectStatusSteps(id);
      if (user?.isAdmin) {
        contractConfigApi.getAll().then(setContractConfigs).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isPerson, user?.isAdmin]);

  useEffect(() => {
    if (id) {
      projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => setStatusMessages([]));
    }
  }, [id, project?.status]);

  const loadData = async () => {
    try {
      let projectsData: Project[];
      if (isPerson) {
        projectsData = await personApi.getProjects();
        // Load company info for person
        if (id) {
          try {
            const company = await personApi.getProjectCompany(id);
            setProjectCompany(company);
          } catch (error) {
            console.error('Failed to load project company:', error);
          }
        }
      } else {
        const [pData, customersData] = await Promise.all([
          projectApi.getProjects(),
          user?.isAdmin ? adminApi.getAllCustomers() : customerApi.getCustomers()
        ]);
        projectsData = pData;
        setCustomers(customersData);
        if (!user?.isAdmin) {
          // Load the company so the default contract name can be derived.
          try {
            const [company, account] = await Promise.all([
              companyApi.getCompany(),
              adyenOnboardingApi.getStatus().catch(() => null)
            ]);
            setProjectCompany(company);
            setAdyenAccount(account);
          } catch (error) {
            console.error('Failed to load company:', error);
          }
        }
      }

      let foundProject = projectsData.find(p => p.id === id);
      if (!foundProject && id && user?.isAdmin) {
        foundProject = await projectApi.getProject(id);
      }
      if (foundProject) {
        setProject(foundProject);
        setFormData({
          customerId: foundProject.customerId ?? '',
          description: foundProject.description,
          startDate: foundProject.startDate,
          endDate: foundProject.endDate,
          status: foundProject.status,
          isTest: user?.isAdmin ? Boolean(foundProject.isTest) : undefined,
          externalProjectNumber: foundProject.externalProjectNumber,
          notes: foundProject.notes
        });

        // Opportunistically reconcile missed Adyen checkout returns before
        // reading contract balances, e.g. after a browser/power interruption.
        await contractConfigApi.getProjectPaymentTimeline(foundProject.id)
          .then(timeline => {
            if (activeTab === 'transactions') applyProjectPaymentTimeline(timeline);
          })
          .catch(() => {});

        // Load contracts
        const contractsData = await contractApi.getContracts(foundProject.id);
        setContracts(contractsData);
        void syncOpenContractSignings(contractsData);
        void loadContractReplacements(contractsData);

        // Auto-select contract: by URL param, or if only one exists
        const autoSelect = initialContractId
          ? contractsData.find(c => c.id === initialContractId)
          : initialContractNumber
            ? contractsData.find(c => c.contractNumber === initialContractNumber)
            : contractsData.length === 1 ? contractsData[0] : null;

        // Read the ref, not the state: in a background refresh this closure is
        // stale, and treating the open contract as unselected would reload its
        // descriptions and wipe text that has not been saved yet.
        if (autoSelect && !selectedContractRef.current) {
          setSelectedContract(autoSelect);
          setContractFormData({
            contractAmount: autoSelect.contractAmount,
            rotAmount: autoSelect.rotAmount,
            afterRotAmount: autoSelect.afterRotAmount,
            contractType: autoSelect.contractType || 'fixed',
            settlementType: autoSelect.settlementType ?? 'milestones',
            parentContractId: autoSelect.parentContractId ?? null
          });
          loadMilestones(autoSelect.id);
          loadContractDescriptions(autoSelect.id);
          loadContractHistory(autoSelect.id);
          loadContractAttachments(autoSelect.contractNumber);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadContracts = async () => {
    if (!id) return;
    try {
      const contractsData = await contractApi.getContracts(id);
      setContracts(contractsData);
      void loadContractReplacements(contractsData);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    }
  };

  const reloadSelectedContract = async () => {
    if (!id) return;
    try {
      const contractsData = await contractApi.getContracts(id);
      setContracts(contractsData);
      void loadContractReplacements(contractsData);
      if (selectedContract) {
        const updated = contractsData.find(c => c.id === selectedContract.id);
        if (updated) {
          setSelectedContract(updated);
          setContractFormData({
            contractAmount: updated.contractAmount,
            rotAmount: updated.rotAmount,
            afterRotAmount: updated.afterRotAmount,
            contractType: updated.contractType || 'fixed',
            settlementType: updated.settlementType ?? 'milestones',
            parentContractId: updated.parentContractId ?? null
          });
          loadContractAttachments(updated.contractNumber);
          loadContractHistory(updated.id);
        }
      }
    } catch (error) {
      console.error('Failed to reload contract:', error);
    }
  };

  useEffect(() => {
    if (!id) return;

    const refreshProjectPaymentState = () => {
      if (projectEventRefreshTimerRef.current) return;
      projectEventRefreshTimerRef.current = setTimeout(() => {
        projectEventRefreshTimerRef.current = null;
        contractConfigApi.getProjectPaymentTimeline(id).then(applyProjectPaymentTimeline).catch(() => {});
        contractConfigApi.getProjectEconomics(id).then(setProjectEconomics).catch(() => {});
        loadData();
        loadContracts();
        loadProjectStatusSteps(id);
      }, 250);
    };

    let fallbackPolling: ReturnType<typeof setInterval> | null = null;
    const stopEvents = contractConfigApi.subscribeProjectPaymentEvents(
      id,
      (event) => {
        if (event.data.projectId === id) {
          refreshProjectPaymentState();
        }
      },
      {
        onUnavailable: () => {
          if (!fallbackPolling) {
            fallbackPolling = setInterval(refreshProjectPaymentState, 60000);
          }
        }
      }
    );

    return () => {
      stopEvents();
      if (fallbackPolling) clearInterval(fallbackPolling);
      if (projectEventRefreshTimerRef.current) {
        clearTimeout(projectEventRefreshTimerRef.current);
        projectEventRefreshTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startExternalSigning = async (
    createSigning: () => Promise<{
      provider: string;
      signingUrl: string;
      externalSigningProcessId: string;
      contract?: { projectId?: string; contractName?: string | null; description?: string | null };
    }>
  ) => {
    setIsOpeningSigning(true);
    setMessage(null);
    setSigningError(null);

    try {
      const signing = await createSigning();
      if (!signing.signingUrl) throw new Error('Missing signing URL.');
      setShowPreview(false);
      if (signing.provider === 'zigned' && selectedContract) {
        const destination = signingHandoffDestination(selectedContract.id, signing);
        navigate(destination.path, { state: destination.state });
      } else {
        redirectToExternalSigning(signing.signingUrl);
      }
    } catch (error) {
      console.error('Failed to start external signing:', error);
      setMessage({ type: 'error', text: t('contracts.actions.signError') });
      const errorKeyByCode: Record<string, string> = {
        ROT_ALLOCATION_INVALID_DIRECTION: 'contracts.actions.signErrors.rotDirection',
        ROT_ALLOCATION_MILESTONE_TOTAL_MISMATCH: 'contracts.actions.signErrors.milestoneTotalMismatch',
        MILESTONE_AMOUNT_INVALID_DIRECTION: 'contracts.actions.signErrors.milestoneDirection'
      };
      const errorKey = errorKeyByCode[apiErrorCode(error) ?? ''];
      setSigningError(errorKey ? t(errorKey) : t('contracts.actions.signError'));
    } finally {
      setIsOpeningSigning(false);
    }
  };

  const loadMilestones = async (contractId: string) => {
    try {
      const milestonesData = await milestoneApi.getMilestones(contractId);
      setMilestones(milestonesData);
      // Keep the per-contract map in sync in the SAME update so every
      // milestone-derived view (timeline, etapplista, kontraktssumma, "kvar att
      // fördela") reflects a saved change immediately — not only the contract
      // card that reads `milestones`. Without this the map only refreshed via
      // the async [contracts] effect, leaving parts of the page stale until a
      // full reload.
      setContractMilestonesMap(prev => ({ ...prev, [contractId]: milestonesData }));
    } catch (error) {
      console.error('Failed to load milestones:', error);
    }
  };

  const loadContractAttachments = async (contractNumber: number) => {
    if (!id) return;
    try {
      const docs = await documentApi.listDocuments(id, contractNumber);
      setContractAttachments(docs);
    } catch (error) {
      console.error('Failed to load contract attachments:', error);
      setContractAttachments([]);
    }
  };

  const handleAttachmentUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !id || !selectedContract) return;
    setIsUploadingAttachment(true);
    try {
      const { files: prepared, errors } = await prepareAttachmentsForUpload(
        Array.from(files)
      );

      if (prepared.length > 0) {
        await documentApi.uploadFiles(id, prepared, selectedContract.contractNumber);
        await loadContractAttachments(selectedContract.contractNumber);
        loadDocuments();
      }

      if (errors.length > 0) {
        const names = errors.map((e) => e.name).join(', ');
        const base = t(`contracts.attachments.errors.${errors[0].reason}`, {
          formats: ATTACHMENT_FORMATS_LABEL,
          max: MAX_ATTACHMENT_SIZE_MB,
        });
        setMessage({ type: 'error', text: names ? `${base} (${names})` : base });
      } else {
        setMessage({ type: 'success', text: t('contracts.attachments.uploadSuccess') });
      }
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      console.error('Failed to upload attachments:', error);
      const code = attachmentErrorCode(error);
      const key =
        code === 'UNSUPPORTED_FILE_TYPE'
          ? 'contracts.attachments.errors.unsupportedType'
          : code === 'FILE_EMPTY'
            ? 'contracts.attachments.errors.empty'
            : code === 'FILE_TOO_LARGE'
              ? 'contracts.attachments.errors.tooLarge'
              : code === 'FILE_CONTENT_MISMATCH'
                ? 'contracts.attachments.errors.contentMismatch'
                : 'contracts.attachments.uploadError';
      setMessage({
        type: 'error',
        text: t(key, {
          formats: ATTACHMENT_FORMATS_LABEL,
          max: MAX_ATTACHMENT_SIZE_MB,
        }),
      });
    } finally {
      setIsUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (doc: DocumentRecord) => {
    setRemoveAttachmentTarget(doc);
  };

  const confirmRemoveAttachment = async () => {
    if (!removeAttachmentTarget) return;
    try {
      await documentApi.unlinkFromContract(removeAttachmentTarget.id);
      if (selectedContract) {
        await loadContractAttachments(selectedContract.contractNumber);
      }
      loadDocuments();
      setMessage({ type: 'success', text: t('contracts.attachments.removeSuccess') });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      setMessage({ type: 'error', text: t('contracts.attachments.removeError') });
    } finally {
      setRemoveAttachmentTarget(null);
    }
  };

  const openLibraryPicker = async () => {
    if (!id || !selectedContract) return;
    try {
      const allDocs = await documentApi.listDocuments(id);
      // Only show docs not already linked to any contract (project-level/admin folder docs)
      setLibraryDocs(allDocs.filter(d => d.contractNumber === null || d.contractNumber === undefined));
      setLibrarySearch('');
      setShowLibraryPicker(true);
    } catch (error) {
      console.error('Failed to load library documents:', error);
    }
  };

  const handleLinkFromLibrary = async (doc: DocumentRecord) => {
    if (!selectedContract) return;
    try {
      await documentApi.linkToContract(doc.id, selectedContract.contractNumber);
      await loadContractAttachments(selectedContract.contractNumber);
      loadDocuments();
      // Remove from picker list
      setLibraryDocs(prev => prev.filter(d => d.id !== doc.id));
      setMessage({ type: 'success', text: t('contracts.attachments.linkSuccess') });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to link document:', error);
      setMessage({ type: 'error', text: t('contracts.attachments.linkError') });
    }
  };

  const formatAttachmentSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadContractDescriptions = async (contractId: string) => {
    try {
      const data = await contractApi.getContractDescriptions(contractId);
      const server: Record<DescriptionField, string> = {
        contractName: data.contractName?.content || '',
        assignmentDescription: data.assignmentDescription?.content || '',
        other: data.other?.content || ''
      };
      const serverRefer = data.referToAttachments?.content === 'true';
      // Reloading the contract that is already open (after another save, a
      // price change, …) must not overwrite text the user has typed but not
      // saved yet, nor a checkbox change whose save is still running.
      const sameContract = descContractIdRef.current === contractId;
      const keepLocal = (field: DescriptionField) =>
        sameContract &&
        (descDirtyRef.current.has(field) ||
          descSavingRef.current.has(field) ||
          descPendingRef.current.has(field));
      const value = (field: DescriptionField) =>
        keepLocal(field) ? descValuesRef.current[field] : server[field];
      setContractName(value('contractName'));
      setAssignmentDescription(value('assignmentDescription'));
      setOtherDescription(value('other'));
      setVariationOtherDescription(data.variationOther?.content ?? '');
      setVariationReason(data.variationReason?.content ?? '');
      const keepLocalRefer = sameContract && referSavingRef.current;
      if (!keepLocalRefer) {
        setReferToAttachments(serverRefer);
        referDesiredRef.current = serverRefer;
      }
      referSavedRef.current = serverRefer;
      // Baseline for the autosave dirty-check: what the server has stored.
      // Kept local text stays dirty against it and is saved on the next blur.
      descSavedValuesRef.current = server;
      descContractIdRef.current = contractId;
      if (!sameContract) {
        descDirtyRef.current.clear();
        descPendingRef.current.clear();
        descErrorFieldsRef.current.clear();
        setContractNameError('');
        setDescLockedByServer(false);
        setDescAutoSaveStatus('idle');
        setDescAutoSaveError(null);
      }
      setAssignmentVersions(data.assignmentDescription?.versions || []);
      setOtherVersions(data.other?.versions || []);
      return data;
    } catch (error) {
      console.error('Failed to load contract descriptions:', error);
      return null;
    }
  };

  const loadContractHistory = async (contractId: string) => {
    try {
      setContractHistory(await contractApi.getHistory(contractId));
    } catch (error) {
      console.error('Failed to load contract history:', error);
      setContractHistory([]);
    }
  };

  // The contract name is no longer auto-filled from the project — it starts
  // empty (guided by a placeholder) so the company names the contract itself.

  const descriptionSaveErrorMessage = (error: unknown) =>
    apiErrorText(t, error, 'contracts.descriptions.saveError');

  // Editing is disabled once the contract is locked/signed (locally known via
  // isCompanySigned, or discovered on save if it was locked in another tab).
  const descEditingDisabled = contractLockedForEditing || descLockedByServer;

  // A company may only move a main contract forward (Förhandsgranska / Signera
  // och skicka) with a contract name (avtalsnamn) AND either an assignment
  // description or the "refer to attached documents" flag. Same rule as the
  // backend guard; ÄTA/variation amendments are exempt.
  const forwardValidation = validateContractForForwarding({
    contractType: selectedContract?.contractType,
    contractName,
    assignmentDescription,
    referToAttachments
  });

  // Move focus to the first missing field so the company can fix it directly.
  const focusFirstMissingContractField = () => {
    if (forwardValidation.missingName) {
      document.getElementById('contractNameInput')?.focus();
      return;
    }
    const assignmentEmpty =
      assignmentDescription.replace(/<[^>]*>/g, '').trim().length === 0;
    if (assignmentEmpty) {
      const quill = document.querySelector(
        '.contract-description-quill .ql-editor'
      ) as HTMLElement | null;
      (quill ?? document.getElementById('referToAttachments'))?.focus();
    } else {
      document.getElementById('referToAttachments')?.focus();
    }
  };

  // Signed, sent for signing or replaced by a newer version (ClickUp 869ev0jcn).
  const isContractLockedError = (error: unknown): boolean =>
    ['CONTRACT_LOCKED', 'CONTRACT_VERSION_SUPERSEDED'].includes(apiErrorCode(error) ?? '');

  // Quill reports an emptied editor as '<p><br></p>'; that is the same as ''.
  const sameDescription = (a: string, b: string) =>
    (a === '<p><br></p>' ? '' : a) === (b === '<p><br></p>' ? '' : b);
  const descHasUnsavedText =
    !isPerson &&
    Array.from(descDirtyRef.current).some(
      field => !sameDescription(descValuesRef.current[field], descSavedValuesRef.current[field])
    );

  const autoSaveTimeLabel = () =>
    new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  // "Sparat" is shown only once nothing is saving or queued and no field has
  // failed – never because some other field happened to save.
  const settleDescAutoSaveStatus = () => {
    if (
      descSavingRef.current.size > 0 ||
      descPendingRef.current.size > 0 ||
      referSavingRef.current ||
      descErrorFieldsRef.current.size > 0
    ) {
      return;
    }
    setDescAutoSaveStatus(prev => (prev === 'saving' ? 'saved' : prev));
    setDescSavedAtLabel(autoSaveTimeLabel());
  };

  // Per-field autosave on blur: saves only when the value actually changed and
  // reads the latest value from a ref, so it is correct even when called from a
  // stale closure. A blur during an in-flight save is queued, not dropped.
  const autoSaveDescriptionField = async (field: DescriptionField): Promise<void> => {
    const contract = selectedContractRef.current;
    if (!contract || isPerson || descEditingDisabled) return;
    const content = descValuesRef.current[field];
    // Only save what the user has edited – never overwrite the server with a
    // value that merely differs because of a reload or editor normalisation.
    if (!descDirtyRef.current.has(field) || sameDescription(content, descSavedValuesRef.current[field])) {
      descDirtyRef.current.delete(field);
      descPendingRef.current.delete(field);
      settleDescAutoSaveStatus();
      return; // dirty-check
    }
    if (field === 'contractName' && !content.replace(/<[^>]*>/g, '').trim()) {
      setContractNameError(t('contracts.descriptions.contractNameRequired'));
      return; // block save on validation error
    }
    if (field === 'contractName') setContractNameError('');
    if (descSavingRef.current.has(field)) {
      descPendingRef.current.add(field);
      setDescAutoSaveStatus('saving');
      return;
    }
    descSavingRef.current.add(field);
    descPendingRef.current.delete(field);
    setDescAutoSaveStatus('saving');
    setDescAutoSaveError(null);
    try {
      const saved = await contractApi.updateContractDescription(contract.id, {
        field,
        content
      });
      if (descContractIdRef.current !== contract.id) return; // another contract is open now
      descSavedValuesRef.current[field] = saved.content;
      if (sameDescription(descValuesRef.current[field], saved.content)) {
        descDirtyRef.current.delete(field);
      }
      if (field === 'assignmentDescription') setAssignmentVersions(saved.versions);
      if (field === 'other') setOtherVersions(saved.versions);
      descErrorFieldsRef.current.delete(field);
    } catch (error) {
      console.error('Failed to autosave description:', error);
      if (isContractLockedError(error)) setDescLockedByServer(true);
      descPendingRef.current.delete(field);
      descErrorFieldsRef.current.add(field);
      setDescAutoSaveStatus('error');
      setDescAutoSaveError({
        message: descriptionSaveErrorMessage(error),
        retry: () => {
          void autoSaveDescriptionField(field);
        }
      });
      return;
    } finally {
      descSavingRef.current.delete(field);
    }
    if (descPendingRef.current.has(field)) {
      await autoSaveDescriptionField(field);
      return;
    }
    settleDescAutoSaveStatus();
  };

  // Autosave for the "refer to attached documents" checkbox. Optimistic toggle,
  // reverts to the last saved value on failure; a toggle during an in-flight
  // save is saved afterwards. Shares the section status indicator.
  const saveReferToAttachments = async (checked: boolean): Promise<void> => {
    const contract = selectedContractRef.current;
    if (!contract || isPerson || descEditingDisabled) return;
    if (checked && contractAttachments.length === 0) return;
    setReferToAttachments(checked);
    referDesiredRef.current = checked;
    // Clicking the checkbox does not blur the Quill editors, so save the text
    // typed there now – it must never be lost when the box is ticked.
    void autoSaveDescriptionField('assignmentDescription');
    void autoSaveDescriptionField('other');
    if (referSavingRef.current) {
      setDescAutoSaveStatus('saving');
      return;
    }
    referSavingRef.current = true;
    setDescAutoSaveStatus('saving');
    setDescAutoSaveError(null);
    try {
      await contractApi.updateContractDescription(contract.id, {
        field: 'referToAttachments',
        content: checked ? 'true' : 'false'
      });
      if (descContractIdRef.current !== contract.id) return;
      referSavedRef.current = checked;
      descErrorFieldsRef.current.delete('referToAttachments');
    } catch (error) {
      console.error('Failed to save referToAttachments:', error);
      setReferToAttachments(referSavedRef.current);
      referDesiredRef.current = referSavedRef.current;
      if (isContractLockedError(error)) setDescLockedByServer(true);
      descErrorFieldsRef.current.add('referToAttachments');
      setDescAutoSaveStatus('error');
      setDescAutoSaveError({
        message: descriptionSaveErrorMessage(error),
        retry: () => {
          void saveReferToAttachments(checked);
        }
      });
      return;
    } finally {
      referSavingRef.current = false;
    }
    if (referDesiredRef.current !== referSavedRef.current) {
      await saveReferToAttachments(referDesiredRef.current);
      return;
    }
    settleDescAutoSaveStatus();
  };

  // The Beställare's "Granska och signera avtal" (PersonContractReview).
  const openPersonReview = async (contractToPreview: Contract | null, asPreview = false) => {
    setPersonReviewAsPreview(asPreview);
    if (contractToPreview && contractToPreview.id !== selectedContract?.id) {
      await handleSelectContract(contractToPreview);
    }
    // Global construction agreement (byggavtal) — DISCONNECTED for MVP, kept for
    // future reactivation (feat/kontrakt_andringar). Re-add the `systemApi` import
    // and load `systemApi.getAgreement('agreementPersonCompany')` here to restore
    // the agreement in the contract preview.
    if (projectCompany) {
      setPreviewCompanyName(projectCompany.companyName || '');
      setPreviewCompanyOrgNr(projectCompany.organizationNumber || '');
    } else {
      try {
        const company = await companyApi.getCompany();
        setPreviewCompanyName(company.companyName || '');
        setPreviewCompanyOrgNr(company.organizationNumber || '');
      } catch (error) {
        console.error('Failed to load company:', error);
        setPreviewCompanyName('');
        setPreviewCompanyOrgNr('');
      }
    }

    // Reset transient form state and fetch the draft conversation thread.
    setSigningStartDecisions(undefined);
    if (contractToPreview) {
      try {
        const msgs = await contractApi.getDraftMessages(contractToPreview.id);
        setDraftMessages(msgs);
      } catch { setDraftMessages([]); }
    }

    setShowPreview(true);
  };

  // The Hantverkare's "Förhandsgranska" and "Signera och skicka till beställaren"
  // (ClickUp 869fkfnr5): a fixed-price contract opens in the contract wizard, an
  // ÄTA in the ÄTA wizard. Signing starts on the review step; "Förhandsgranska"
  // shows the document read-only, with "Gå till signering" while the company can
  // still sign. A contract neither wizard can open is shown read-only.
  const openCompanyContractReview = async (mode: 'preview' | 'sign', contract: Contract | null) => {
    if (!contract) return;
    let validation = forwardValidation;
    if (contract.id !== selectedContract?.id) {
      const descriptions = await handleSelectContract(contract);
      validation = validateContractForForwarding({
        contractType: contract.contractType,
        contractName: descriptions?.contractName?.content ?? '',
        assignmentDescription: descriptions?.assignmentDescription?.content ?? '',
        referToAttachments: descriptions?.referToAttachments?.content === 'true'
      });
    }
    // A main contract cannot be signed without the required assignment info: a
    // contract name (avtalsnamn) AND either a description or the "refer to
    // attachments" flag. Same rule as the backend guard; ÄTA are exempt.
    if (mode === 'sign' && !validation.ok) {
      setAssignmentRequiredMissing({
        name: validation.missingName,
        assignment: validation.missingAssignment
      });
      setShowAssignmentRequired(true);
      return;
    }
    const signedByBoth = contract.signingStatus === 'signed' ||
      contract.draftStatus === 'signed_by_both' ||
      Boolean(contract.companySigned && contract.personSigned);
    const companySigned = Boolean(contract.companySigned) ||
      contract.draftStatus === 'signed_by_company' ||
      contract.draftStatus === 'signed_by_both';
    const canSign = validation.ok && !signedByBoth && !companySigned && !isProjectClosedForSigning;
    // Signing that is no longer possible (already signed, project closed) opens
    // the document read-only instead.
    const viewOnly = mode === 'preview' || !canSign;
    if (wizardEditStatus(contract)) {
      await openContractWizardForEdit(contract, viewOnly ? { viewOnly: true, canSign } : { startStep: 2 });
      return;
    }
    const isVariation = contract.contractType === 'variation' || Boolean(contract.stageVariation) ||
      contract.settlementType === 'price_adjustment';
    if (isVariation && !signedByBoth &&
      await openVariationWizardForEdit(contract, 2, undefined, viewOnly ? { viewOnly: true, canSign } : undefined)) {
      return;
    }
    void loadContractWizardCompany();
    setCompanyPreviewContractId(contract.id);
  };

  const handleOpenPreview = async (
    mode: 'preview' | 'sign' = 'preview',
    contractToPreview: Contract | null = selectedContract
  ) => {
    if (isPerson) await openPersonReview(contractToPreview, mode === 'preview');
    else await openCompanyContractReview(mode, contractToPreview);
  };

  // "Granska och signera" on the home page or in a notification links with
  // `review=1`: open the person's review directly, once the contract and the
  // project's company (the other party in the document) are loaded.
  const reviewRequested = isPerson && searchParams.get('review') === '1';
  const reviewOpenedRef = useRef(false);
  useEffect(() => {
    if (!reviewRequested || reviewOpenedRef.current || !selectedContract || !projectCompany) return;
    if (initialContractId && selectedContract.id !== initialContractId) return;
    if (initialContractNumber !== null && selectedContract.contractNumber !== initialContractNumber) return;
    reviewOpenedRef.current = true;
    void handleOpenPreview('sign', selectedContract);
    // Opens once per visit; handleOpenPreview is recreated on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewRequested, selectedContract, projectCompany]);

  // "Skapa Projekt" links here with `wizard=1`: open the contract wizard on the
  // project's first contract (created together with the project) right away.
  // Once only, and the parameter is dropped so a reload does not reopen it;
  // after closing, "Redigera" on the contract row opens it again.
  const wizardRequested = FEATURE_CONTRACT_WIZARD_V2 && !isPerson && searchParams.get('wizard') === '1';
  const wizardOpenedRef = useRef(false);
  useEffect(() => {
    if (!wizardRequested || wizardOpenedRef.current || !selectedContract) return;
    if (initialContractNumber !== null && selectedContract.contractNumber !== initialContractNumber) return;
    wizardOpenedRef.current = true;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('wizard');
      return next;
    }, { replace: true });
    if (wizardEditStatus(selectedContract) === 'draft') void openContractWizardForNewDraft(selectedContract);
    // Opens once per visit; the opener is recreated on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardRequested, selectedContract]);

  // "Lägg till beställare" in "Att göra nu" links here with `addCustomer=1`
  // and opens the dialog once the project has loaded (ClickUp 869fmxjgt).
  const addCustomerRequested = searchParams.get('addCustomer') === '1';
  useEffect(() => {
    if (!addCustomerRequested || !project) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('addCustomer');
      return next;
    }, { replace: true });
    if (!isPerson && projectCustomerState(project) === 'missing') {
      setAddCustomerTab(user?.isAdmin ? 'pick' : 'invite');
    }
    // Opens once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCustomerRequested, project]);

  // "Signera kontrakt" in the company's "Att göra nu" links here with `sign=1`:
  // open the company's signing review once the contract is selected, and drop
  // the parameter so a reload does not reopen it (ClickUp 869fmv7b5).
  const signRequested = !isPerson && searchParams.get('sign') === '1';
  const signOpenedRef = useRef(false);
  useEffect(() => {
    if (!signRequested || signOpenedRef.current || !selectedContract) return;
    if (initialContractId && selectedContract.id !== initialContractId) return;
    signOpenedRef.current = true;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('sign');
      return next;
    }, { replace: true });
    void handleOpenPreview('sign', selectedContract);
    // Opens once per visit; handleOpenPreview is recreated on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signRequested, selectedContract]);

  // Document handlers
  const loadDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const [tree, docs] = await Promise.all([
        documentApi.getFolderTree(id),
        documentApi.listDocuments(id)
      ]);
      setFolderTree(tree);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'documents' && id) {
      loadDocuments();
    }
  }, [activeTab, id, loadDocuments]);

  useEffect(() => {
    const contract = selectedContractRef.current;
    if (activeTab === 'contract' && contract) {
      syncContractSigning(contract);
    } else if (activeTab === 'timeline') {
      syncOpenContractSignings(contracts);
    }
    // Run when entering a tab or when the selected signing status changes. Do
    // not depend on the whole contracts array; syncing updates it and would
    // otherwise create a request loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selectedContract?.id,
    selectedContract?.signingStatus,
    syncContractSigning,
  ]);

  useRefreshOnReturn(async () => {
    if (!id) return;
    const latest = await contractApi.getContracts(id);
    setContracts(latest);
    const selected = latest.find(contract => contract.id === selectedContractRef.current?.id);
    if (selected) applyContractUpdate(selected);
    await syncOpenContractSignings(latest);
    setFundingRefreshToken(value => value + 1);
    await loadProjectStatusSteps(id);
    if (selected) {
      await loadContractHistory(selected.id);
      await loadContractAttachments(selected.contractNumber);
    }
  });

  useEffect(() => {
    if (!['contract', 'timeline'].includes(activeTab)) return;
    const pending = contracts.filter(contract =>
      !!contract.externalSigningProcessId && needsSigningSync(contract)
    );
    if (pending.length === 0) return;
    // Provider callbacks and signed documents can arrive after the user returns.
    // Keep reconciling until a terminal status arrives, including after reload.
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void syncOpenContractSignings(pending);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [activeTab, contracts, syncOpenContractSignings]);

  const loadInactivityCases = useCallback(async () => {
    if (!id) return;
    try {
      setInactivityCases(await inactivityApi.listForProject(id));
    } catch (error) {
      console.error('Failed to load inactivity cases:', error);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'timeline' || !id) return;
    void loadInactivityCases();
    const interval = window.setInterval(() => void loadInactivityCases(), 5000);
    return () => window.clearInterval(interval);
  }, [activeTab, id, loadInactivityCases]);

  // Load milestones for every contract so each contract's timeline can render
  useEffect(() => {
    if (contracts.length === 0) {
      setContractMilestonesMap({});
      return;
    }
    Promise.all(
      contracts.map(c =>
        milestoneApi.getMilestones(c.id)
          .then(ms => ({ contractId: c.id, milestones: ms }))
          .catch(() => ({ contractId: c.id, milestones: [] as Milestone[] }))
      )
    ).then(results => {
      const map: Record<string, Milestone[]> = {};
      results.forEach(r => { map[r.contractId] = r.milestones; });
      setContractMilestonesMap(map);
    });
  }, [contracts]);

  useEffect(() => {
    if (activeTab !== 'timeline') return;
    const viewerRole = user?.personaType === 'person'
      ? 'person'
      : ['company', 'builder'].includes(user?.personaType ?? '')
        ? 'company'
        : null;
    const milestoneIds = Array.from(new Set(
      Object.values(contractMilestonesMap)
        .flat()
        .filter(milestone => ['disputed', 'conflict', 'under_resolution'].includes(
          milestone.deliveryStatus ?? ''
        ))
        .map(milestone => milestone.id)
    ));
    if (!viewerRole || milestoneIds.length === 0) {
      setLatestCounterpartyReplies({});
      setLatestDisputeReplies({});
      return;
    }

    let cancelled = false;
    const loadReplies = async () => {
      const results = await Promise.all(milestoneIds.map(async milestoneId => {
        try {
          const messages = await milestoneApi.getMessages(milestoneId);
          let rejectionIndex = -1;
          messages.forEach((message, index) => {
            if (message.attestationAction === 'rejected') rejectionIndex = index;
          });
          const dialogueMessages = rejectionIndex >= 0
            ? messages.slice(rejectionIndex + 1)
            : messages;
          const latestReply = [...dialogueMessages].reverse().find(message => {
            if (message.attestationAction) return false;
            return viewerRole === 'person'
              ? ['company', 'builder'].includes(message.userRole)
              : message.userRole === 'person';
          });
          const latestMessage = [...dialogueMessages].reverse().find(message => !message.attestationAction && ['company', 'builder', 'person'].includes(message.userRole));
          return { milestoneId, message: latestReply, latestMessage };
        } catch {
          return null;
        }
      }));
      if (cancelled) return;
      const replies: Record<string, MilestoneMessage> = {};
      const latest: Record<string, MilestoneMessage> = {};
      results.forEach(result => {
        if (result?.message) replies[result.milestoneId] = result.message;
        if (result?.latestMessage) latest[result.milestoneId] = result.latestMessage;
      });
      setLatestDisputeReplies(latest);
      setLatestCounterpartyReplies(replies);
    };

    void loadReplies();
    const interval = window.setInterval(() => void loadReplies(), 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeTab, contractMilestonesMap, user?.personaType]);

  useEffect(() => {
    if (activeTab === 'participants' && id) {
      Promise.all([
        projectApi.getProjectParties(id).catch(() => null),
        marketplaceApi.getProjectParticipants(id).catch(() => null),
      ]).then(([parties, marketplaceParticipants]) => {
        setParticipants({
          customer: parties?.customer ?? null,
          company: parties?.company ?? null,
          personUserIds: parties?.personUserIds ?? [],
          providers: marketplaceParticipants?.providers ?? [],
        });
      }).catch(() => {});
    }
    if (activeTab === 'transactions' && id) {
      contractConfigApi.getProjectEconomics(id).then(setProjectEconomics).catch(() => {});
      contractConfigApi.getProjectPaymentTimeline(id).then(applyProjectPaymentTimeline).catch(() => {});
    }
  }, [activeTab, applyProjectPaymentTimeline, id]);

  // When the payment modal opens or the chosen contract changes, fetch
  // economics for every method in parallel so each method card shows its
  // own correct total immediately (switching method is then instant — no
  // refetch needed).
  useEffect(() => {
    if (!showPaymentModal || !id) return;
    const cId = selectedPaymentContractId || undefined;
    const mId = selectedPaymentMilestoneId || undefined;
    const hasPaymentEconomics = (value: any) =>
      value &&
      typeof value === 'object' &&
      Number.isFinite(Number(value.customerFee)) &&
      Number.isFinite(Number(value.projectValue)) &&
      Number.isFinite(Number(value.etappioVariableFee)) &&
      Number.isFinite(Number(value.paymentMethodFee));
    setPaymentEconomicsLoading(true);
    setPaymentEconomicsError(null);
    setMethodEcos({});
    const paymentMethods = cardPaymentsEnabled
      ? (['bank', 'swish', 'card'] as const)
      : (['bank', 'swish'] as const);
    Promise.all(paymentMethods.map(method =>
      contractConfigApi.getProjectEconomics(id, method, cId, mId)
    ))
      .then(results => {
        if (results.some(value => !hasPaymentEconomics(value))) {
          throw new Error('Missing payment economics');
        }
        setMethodEcos(Object.fromEntries(
          paymentMethods.map((method, index) => [method, results[index]])
        ));
      })
      .catch(() => {
        setMethodEcos({});
        setPaymentEconomicsError(t('projectView.payment.economicsError', 'Det gick inte att hämta avgifterna för insättningen. Försök igen om en stund.'));
      })
      .finally(() => setPaymentEconomicsLoading(false));
  }, [cardPaymentsEnabled, showPaymentModal, selectedPaymentContractId, selectedPaymentMilestoneId, id, t]);

  const handlePreview = async (doc: DocumentRecord) => {
    try {
      const blob = await documentApi.getFileBlob(doc.id);
      const ext = getFileExtension(doc.originalName);

      if (WORD_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewDoc(doc);
        setPreviewHtml(result.value);
        setPreviewUrl(null);
      } else if (EXCEL_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetNames = workbook.SheetNames;
        let html = '';
        for (const name of sheetNames) {
          const sheet = workbook.Sheets[name];
          if (sheetNames.length > 1) {
            html += `<h5 style="margin-top:1rem">${name}</h5>`;
          }
          html += XLSX.utils.sheet_to_html(sheet, { editable: false });
        }
        setPreviewDoc(doc);
        setPreviewHtml(html);
        setPreviewUrl(null);
      } else if (PPTX_EXTENSIONS.includes(ext)) {
        const arrayBuffer = await blob.arrayBuffer();
        const html = await parsePptxToHtml(arrayBuffer);
        setPreviewDoc(doc);
        setPreviewHtml(html);
        setPreviewUrl(null);
      } else {
        // Sätt rätt MIME-typ så att PDF:er (t.ex. signerade avtal) och bilder
        // renderas i förhandsvisningen i stället för att visas som rå text.
        const url = URL.createObjectURL(typedPreviewBlob(blob, doc.originalName));
        setPreviewDoc(doc);
        setPreviewUrl(url);
        setPreviewHtml(null);
      }
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewHtml(null);
  };

  const userCanUpload = user?.isAdmin || user?.personaType === 'company' || user?.personaType === 'person';
  const userCanDelete = user?.isAdmin;
  const userCanRename = user?.isAdmin || user?.personaType === 'company' || user?.personaType === 'person';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // A project may lack a Beställare (ClickUp 869ff1egy); it is added from
    // "Lägg till beställare", never cleared here.
    if (!formData.customerId && project?.customerId) {
      errors.customerId = t('validation.required');
    }

    if (!formData.description.trim()) {
      errors.description = t('validation.required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !project) return;

    setIsSaving(true);
    setMessage(null);

    try {
      await projectApi.updateProject(project.id, formData);
      setMessage({ type: 'success', text: t('projects.form.saveSuccess') });
      // The reload runs in the background; the save is done (ClickUp 869fgxq45).
      void loadData();
    } catch (error) {
      console.error('Failed to save project:', error);
      setMessage({ type: 'error', text: saveErrorText(t, error, 'projects.form.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  // Contract handlers
  const handleContractInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContractFormData(prev => ({ ...prev, [name]: value }));
    if (contractFormErrors[name]) {
      setContractFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const parseFormAmount = (value: string): number => {
    return parseSekInput(value) ?? 0;
  };

  const milestoneAfterRotAmount = milestones.reduce(
    (sum, milestone) => sum + Number(milestone.afterRotAmount || 0),
    0
  );
  const milestoneRotAmount = milestones.length > 0
    ? parseFormAmount(contractFormData.contractAmount) - milestoneAfterRotAmount
    : 0;

  const loadNegativeVariationCapacity = async (sourceId?: string): Promise<number | null> => {
    if (!id) return null;
    setNegativeVariationCapacityLoading(true);
    try {
      const result = await contractApi.getNegativeVariationRefundCapacity(id, sourceId || createTypeParentId || contractFormData.parentContractId || selectedContract?.parentContractId || selectedContract?.id);
      setNegativeVariationCapacityOre(result.availableAmountOre);
      return result.availableAmountOre;
    } catch (error) {
      console.error('Failed to load negative variation refund capacity:', error);
      setNegativeVariationCapacityOre(null);
      return null;
    } finally {
      setNegativeVariationCapacityLoading(false);
    }
  };

  const loadContractWizardCompany = async () => {
    // Parties for the preview in step 3 (same source as the contract page's preview).
    try {
      const company = await companyApi.getCompany();
      setContractWizardCompany({ name: company.companyName || '', organizationNumber: company.organizationNumber || '' });
    } catch (error) {
      console.error('Failed to load company for the contract wizard:', error);
    }
  };

  const resetContractWizard = () => {
    contractWizardDraft.reset();
    contractWizardPlan.reset();
    contractWizardReview.reset();
    setContractWizardEdit(null);
    setContractWizardWithdraw(null);
    setContractWizardStopError('');
    setContractWizardStartStep(0);
    setContractWizardViewOnly(false);
    setContractWizardCanSign(false);
    contractWizardWithdrawRef.current?.resolve(false);
    contractWizardWithdrawRef.current = null;
  };

  const openContractWizard = async () => {
    if (!id) return;
    try {
      const nextNumber = await contractApi.getNextContractNumber(id);
      setNextContractNumber(nextNumber.nextContractNumber);
    } catch {
      /* the number is only shown in the subtitle; open with the last known value */
    }
    resetContractWizard();
    setContractWizardReachedPricing(false);
    setContractWizardStepIndex(null);
    setShowContractWizard(true);
    void loadContractWizardCompany();
  };

  // A sent contract is locked by the API. The first write in the wizard asks
  // the company to withdraw the sending; every write waits for that answer.
  const requestContractWithdrawal = (): Promise<boolean> => {
    if (contractWizardWithdrawRef.current) return contractWizardWithdrawRef.current.promise;
    let resolve: (ok: boolean) => void = () => undefined;
    const promise = new Promise<boolean>(settle => { resolve = settle; });
    contractWizardWithdrawRef.current = { promise, resolve };
    setContractWizardWithdraw({ busy: false, error: '' });
    return promise;
  };

  const settleContractWithdrawal = (ok: boolean) => {
    const pending = contractWizardWithdrawRef.current;
    contractWizardWithdrawRef.current = null;
    setContractWizardWithdraw(null);
    pending?.resolve(ok);
  };

  const confirmContractWithdrawal = async () => {
    const contract = contractWizardDraft.contract;
    if (!contract) return;
    setContractWizardWithdraw({ busy: true, error: '' });
    try {
      const result = await contractApi.withdrawSigning(contract.id);
      contractWizardDraft.replaceContract(result.contract);
      // An ordinary draft from here on: writes go straight to the API.
      contractWizardDraft.setWriteGuard(null);
      contractWizardPlan.setWriteGuard(null);
      setContractWizardEdit(previous => (previous ? { ...previous, withdrawn: true } : previous));
      settleContractWithdrawal(true);
    } catch (error) {
      console.error('Failed to withdraw the contract from signing:', error);
      const code = apiErrorCode(error);
      setContractWizardWithdraw({
        busy: false,
        error: t(
          code === 'SIGNING_ALREADY_COMPLETED'
            ? 'contracts.wizard.edit.withdraw.alreadySigned'
            : code === 'SIGNING_NOT_WITHDRAWABLE'
              ? 'contracts.wizard.edit.withdraw.notWithdrawable'
              : 'contracts.wizard.edit.withdraw.error',
        ),
      });
    }
  };

  // A signing the company started but has not signed has not reached the
  // customer, so the first change simply stops it: no question, no new version
  // (ClickUp 869fnf0qc). Should the provider already have the company's
  // signature, the contract is sent after all and the withdrawal is asked for.
  const stopUnsignedContractSigning = async (contractId: string): Promise<boolean> => {
    setContractWizardStopError('');
    try {
      const result = await contractApi.withdrawSigning(contractId, undefined, { beforeCompanySignature: true });
      contractWizardDraft.replaceContract(result.contract);
      contractWizardDraft.setWriteGuard(null);
      contractWizardPlan.setWriteGuard(null);
      setContractWizardEdit(previous => (previous ? { ...previous, status: 'draft' } : previous));
      return true;
    } catch (error) {
      if (apiErrorCode(error) === 'SIGNING_ALREADY_SENT') {
        contractWizardDraft.setWriteGuard(requestContractWithdrawal);
        contractWizardPlan.setWriteGuard(requestContractWithdrawal);
        setContractWizardEdit(previous => (previous ? { ...previous, status: 'sent' } : previous));
        return requestContractWithdrawal();
      }
      console.error('Failed to stop the unsigned contract signing:', error);
      setContractWizardStopError(t('contracts.wizard.edit.stopSigningError'));
      return false;
    }
  };

  // Writes to a locked contract wait for the guard of its state.
  const setContractWizardWriteGuard = (status: WizardEditStatus, contractId: string) => {
    const guard = status === 'sent'
      ? requestContractWithdrawal
      : status === 'signing'
        ? () => stopUnsignedContractSigning(contractId)
        : null;
    if (!guard) return;
    contractWizardDraft.setWriteGuard(guard);
    contractWizardPlan.setWriteGuard(guard);
  };

  /**
   * Opens an existing fixed-price contract in the wizard. Returns false when the
   * wizard cannot edit it (the caller then falls back to the contract page).
   */
  const openContractWizardForEdit = async (
    contract: Contract,
    options: { startStep?: number; viewOnly?: boolean; canSign?: boolean } = {}
  ): Promise<boolean> => {
    const status = wizardEditStatus(contract);
    if (!status) return false;
    resetContractWizard();
    setContractWizardViewOnly(Boolean(options.viewOnly));
    setContractWizardCanSign(Boolean(options.viewOnly && options.canSign));
    // A signed contract opens as its single read-only view (ÄTA for changes).
    setContractWizardStartStep(status === 'signed' ? 0 : options.startStep ?? 0);
    try {
      const [loaded, contractMilestones] = await Promise.all([
        contractWizardDraft.load(contract),
        milestoneApi.getMilestones(contract.id),
      ]);
      const plan = planStateFromContract(contract, contractMilestones);
      contractWizardPlan.load(plan, true);
      if (!options.viewOnly) setContractWizardWriteGuard(status, contract.id);
      setContractWizardEdit({
        status,
        withdrawn: false,
        snapshot: { values: loaded.values, attachments: loaded.attachments, plan },
        contract,
      });
      setContractWizardReachedPricing(true);
      setContractWizardStepIndex(null);
      setShowContractWizard(true);
      void loadContractWizardCompany();
    } catch (error) {
      console.error('Failed to open the contract in the wizard:', error);
      resetContractWizard();
      setMessage({ type: 'error', text: t('contracts.wizard.edit.loadError') });
    }
    return true;
  };

  /**
   * The wizard in create mode ("Skapa nytt kontrakt") on a draft that already
   * exists: the project's first contract right after "Skapa Projekt". Steps run
   * in order as when creating, and every save goes to that contract instead of
   * creating another one.
   */
  const openContractWizardForNewDraft = async (contract: Contract) => {
    resetContractWizard();
    try {
      const [, contractMilestones] = await Promise.all([
        contractWizardDraft.load(contract, { editing: false }),
        milestoneApi.getMilestones(contract.id),
      ]);
      contractWizardPlan.load(planStateFromContract(contract, contractMilestones));
      setNextContractNumber(contract.contractNumber);
      setContractWizardReachedPricing(false);
      setContractWizardStepIndex(null);
      setShowContractWizard(true);
      void loadContractWizardCompany();
    } catch (error) {
      console.error('Failed to open the new contract in the wizard:', error);
      resetContractWizard();
      setMessage({ type: 'error', text: t('contracts.wizard.edit.loadError') });
    }
  };

  // "Gå till signering" from the read-only view: the same loaded contract, now
  // on the review step (the wizard restarts there, see its `key`).
  const goToContractWizardSigning = () => {
    if (!contractWizardEdit) return;
    setContractWizardWriteGuard(contractWizardEdit.status, contractWizardEdit.contract.id);
    setContractWizardCanSign(false);
    setContractWizardStartStep(2);
    setContractWizardViewOnly(false);
  };

  const contractWizardResend = contractWizardEdit?.status === 'sent';
  const contractWizardLocked = contractWizardResend && !contractWizardEdit?.withdrawn;

  // Before signing: persist anything still unsaved in steps 1–2. A sent
  // contract is withdrawn first; declining that simply stays in the wizard.
  const saveContractWizardBeforeSign = async (): Promise<boolean | 'cancelled'> => {
    if (contractWizardLocked && !(await requestContractWithdrawal())) return 'cancelled';
    if (!(await contractWizardDraft.save())) return false;
    if (contractWizardPlan.dirty) return contractWizardPlan.validateAndSave();
    return true;
  };

  // "Spara utkast" / "Spara ändringar": save step 1, and the milestone plan once
  // it is complete (an incomplete plan is kept in the wizard until "Nästa" shows
  // what is missing).
  const saveContractWizard = async () => {
    const saved = await contractWizardDraft.save();
    if (!saved || !contractWizardPlan.dirty) return;
    if (hasPlanErrors(contractWizardPlan.errors)) {
      contractWizardPlan.noteIncompletePlan();
      return;
    }
    await contractWizardPlan.save();
  };

  const closeContractWizard = () => {
    const edited = contractWizardEdit?.contract;
    setShowContractWizard(false);
    setContractWizardStepIndex(null);
    // A draft may have been created, or an existing contract changed or withdrawn.
    if (edited) {
      void reloadSelectedContract();
      if (selectedContract?.id === edited.id) {
        milestoneApi.getMilestones(edited.id).then(setMilestones).catch(error => {
          console.error('Failed to reload milestones after the contract wizard:', error);
        });
      }
    } else if (contractWizardDraft.contract) {
      void loadContracts();
    }
    resetContractWizard();
  };

  // From the signed contract: start an ÄTA linked to it (existing ÄTA flow).
  const startVariationFromContract = (parentId: string) => {
    closeContractWizard();
    void openStageVariation(parentId).catch(error => setMessage({ type: 'error', text: variationOpenErrorText(error, knownApiErrorText(t, error) ?? t('variationWizard.loadError')) }));
  };

  const contractWizardChanges =
    contractWizardEdit && contractWizardEdit.status !== 'signed'
      ? diffContract(contractWizardEdit.snapshot, {
          values: contractWizardDraft.values,
          attachments: contractWizardDraft.attachments,
          plan: contractWizardPlan.state,
        })
      : [];
  const contractWizardChangeTexts = contractWizardChanges.map(change =>
    t(`contracts.wizard.changes.${change.key}`, change.params),
  );
  const contractWizardStageMarkers =
    contractWizardEdit && contractWizardEdit.status !== 'signed'
      ? stageMarkers(contractWizardEdit.snapshot.plan, contractWizardPlan.state)
      : undefined;

  // The project's company theme for the wizard's preview: through the contract
  // once the draft exists (right company for everyone who opens it), the
  // company's own theme before that.
  const contractWizardContractId = contractWizardDraft.contract?.id;
  const contractWizardTheme = useCompanyTheme(
    !FEATURE_CONTRACT_WIZARD_V2 || !showContractWizard
      ? null
      : contractWizardContractId
        ? { contractId: contractWizardContractId }
        : { own: true }
  );
  // The contracting company's theme for the customer's review (PersonContractReview)
  // and the company's read-only preview (ContractPreviewModal).
  const storedPreviewTheme = useCompanyTheme(
    isPerson && showPreview && selectedContract
      ? { contractId: selectedContract.id }
      : !isPerson && companyPreviewContractId
        ? { contractId: companyPreviewContractId }
        : null
  );
  // The draft conversation in the wizard's review step.
  const contractWizardMessages = useDraftMessages(showContractWizard ? contractWizardEdit?.contract.id : undefined);
  const contractWizardPreview = (goTo?: (stepIndex: number) => void): ContractDocumentPreviewProps => {
    const customer = customers.find(c => c.id === project?.customerId);
    const { plan, state } = contractWizardPlan;
    const values = contractWizardDraft.values;
    const replaced = contracts.find(item => item.id === contractWizardEdit?.contract.replacesContractId);
    return {
      theme: contractWizardTheme,
      contractType: 'fixed',
      replaces: replaced
        ? {
            contractNumber: replaced.contractNumber,
            contractVersion: replaced.contractVersion,
            contractName: contractTitle(replaced),
            contractType: replaced.contractType,
            variationNumber: replaced.variationNumber,
          }
        : null,
      contractNumber: contractWizardDraft.contract?.contractNumber ?? nextContractNumber,
      contractVersion: contractWizardDraft.contract?.contractVersion,
      contractName: values.contractName.trim(),
      companyName: contractWizardCompany.name,
      organizationNumber: contractWizardCompany.organizationNumber,
      customerName: customer?.name,
      customerEmail: customer?.email,
      assignmentHtml: values.assignmentDescription,
      referToAttachments: values.referToAttachments,
      otherHtml: values.otherDescription,
      gross: plan.gross,
      rot: plan.rot,
      afterRot: plan.afterRot,
      milestones: state.rows.map((row, index) => ({
        number: index + 1,
        description: row.description.trim(),
        afterRotAmount: plan.stageAfterRot[index],
        endDate: row.endDate,
      })),
      attachments: contractWizardDraft.attachments,
      onOpenAttachment: doc => void attachmentPreview.openPreview(doc),
      onVerifyAttachment: setAttachmentDetailsTarget,
      ...(goTo ? { onEditAssignment: () => goTo(0), onEditPricing: () => goTo(1) } : {}),
    };
  };

  const contractWizardSigned = contractWizardEdit?.status === 'signed';
  // Nothing to save or sign: a signed contract, or one opened only to read.
  const contractWizardReadOnly = contractWizardSigned || contractWizardViewOnly;
  const contractWizardAssignmentIncomplete = Object.values(contractWizardDraft.errors).some(Boolean);
  const contractWizardSteps: ContractWizardStep[] = contractWizardSigned && contractWizardEdit
    ? [
        {
          key: 'signed',
          title: t('contracts.wizard.edit.signed.title'),
          showSummary: false,
          render: () => (
            <SignedContractView
              preview={{
                ...contractWizardPreview(),
                signedAt: {
                  company: contractWizardEdit.contract.companySignedAt,
                  customer: contractWizardEdit.contract.personSignedAt,
                },
              }}
              version={contractWizardEdit.contract.contractVersion}
              onCreateVariation={
                canCreateContract && eligibleParentContracts.some(c => c.id === contractWizardEdit.contract.id)
                  ? () => startVariationFromContract(contractWizardEdit.contract.id)
                  : undefined
              }
            />
          ),
        },
      ]
    : contractWizardViewOnly && contractWizardEdit
      ? [
          {
            key: 'view',
            title: t('contracts.wizard.view.stepTitle'),
            showSummary: false,
            render: () => <ContractDocumentPreview {...contractWizardPreview()} />,
          },
        ]
      : [
        {
          key: 'assignment',
          title: t('contracts.wizard.steps.assignment.title'),
          subtitle: t('contracts.wizard.steps.assignment.subtitle'),
          changed: contractWizardChanges.some(change => change.section === 'assignment'),
          render: () => (
            <AssignmentStep
              draft={contractWizardDraft}
              stepCounter={t('contracts.wizard.stepper.counter', { current: 1, total: 3 })}
            />
          ),
          validate: contractWizardDraft.validateAndSave,
        },
        {
          key: 'pricing',
          title: t('contracts.wizard.steps.pricing.title'),
          subtitle: t('contracts.wizard.steps.pricing.subtitle'),
          changed: contractWizardChanges.some(change => change.section === 'pricing'),
          render: ({ goTo }) => (
            <>
              {contractWizardAssignmentIncomplete && (
                <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2 mb-0 contract-wizard-step-warning" role="alert">
                  <i className="feather-alert-triangle" aria-hidden="true"></i>
                  <span className="flex-grow-1">{t('contracts.wizard.pricing.assignmentIncomplete')}</span>
                  <button type="button" className="btn btn-outline-dark btn-sm contract-wizard-step-warning-btn" onClick={() => goTo(0)}>
                    {t('contracts.wizard.pricing.goToAssignment')}
                  </button>
                </div>
              )}
              <PricingStep
                step={contractWizardPlan}
                stepCounter={t('contracts.wizard.stepper.counter', { current: 2, total: 3 })}
                stageMarkers={contractWizardStageMarkers}
              />
            </>
          ),
          // Opening on step 2 must not skip step 1's rules: going on needs both.
          validate: async () => !contractWizardAssignmentIncomplete && contractWizardPlan.validateAndSave(),
        },
        {
          key: 'review',
          title: t('contracts.wizard.steps.review.title'),
          subtitle: t('contracts.wizard.steps.review.subtitle'),
          showSummary: false,
          render: ({ goTo }) => (
            <ReviewStep
              review={contractWizardReview}
              providerName={signingProviderName}
              stepCounter={t('contracts.wizard.stepper.counter', { current: 3, total: 3 })}
              preview={contractWizardPreview(goTo)}
              messages={contractWizardMessages}
              customerPanel={
                // The same "Lägg till beställare" as on the Kontrakt tab (ClickUp 869fn1nyr).
                project && !isPerson && projectCustomerState(project) === 'missing' ? (
                  <ProjectCustomerPanel
                    onInvite={() => setAddCustomerTab(user?.isAdmin ? 'pick' : 'invite')}
                    onPick={() => setAddCustomerTab('pick')}
                  />
                ) : undefined
              }
              edit={
                contractWizardEdit
                  ? {
                      resend: contractWizardResend,
                      changes: contractWizardChangeTexts,
                      version: contractWizardEdit.contract.contractVersion,
                    }
                  : undefined
              }
            />
          ),
        },
      ];

  const contractWizardBannerStatus: ContractEditBannerStatus | null = !contractWizardEdit
    ? null
    : contractWizardEdit.status === 'sent'
      ? (contractWizardEdit.withdrawn ? 'withdrawn' : 'sent')
      : contractWizardEdit.status;

  const handleCreateContract = () => {
    if (!canCreateContract) return;
    // New wizard: "Vad vill du skapa?" is only needed once a contract has been
    // signed by both parties. Otherwise the only choice is a new contract, so
    // go straight to the wizard (ClickUp 869fh4c2b).
    if (FEATURE_CONTRACT_WIZARD_V2) {
      if (!hasSignedAgreement(contracts)) {
        void openContractWizard();
        return;
      }
      setAgreementChoiceError('');
      setShowCreateContractTypePrompt(true);
      return;
    }
    setConflictVariationDraft(null);
    // Reset the prompt state and open the type selection modal.
    setCreateTypeChoice('fixed');
    setCreateTypeParentId('');
    setCreateSettlementType('milestones');
    setCreatePriceAdjustmentAmount('');
    setNegativeVariationCapacityOre(null);
    setNegativeVariationConfirmAction(null);
    setNegativeVariationConfirmAmountOre(0);
    setShowCreateContractTypePrompt(true);
    void loadNegativeVariationCapacity();
  };

  const beginContractCreation = async () => {
    if (!id) return;
    const nextNumber = await contractApi.getNextContractNumber(id);
    setNextContractNumber(nextNumber.nextContractNumber);
    setIsCreatingContract(true);
    setSelectedContract(null);
    const priceAdjustmentAmount =
      createTypeChoice === 'variation' && createSettlementType === 'price_adjustment'
        ? String(parseSekInput(createPriceAdjustmentAmount) ?? 0)
        : '';
    setContractFormData({
      ...emptyContractForm,
      contractAmount: priceAdjustmentAmount,
      rotAmount: priceAdjustmentAmount ? '0' : '',
      afterRotAmount: priceAdjustmentAmount,
      contractType: createTypeChoice,
      settlementType: createTypeChoice === 'variation' ? createSettlementType : 'milestones',
      parentContractId: createTypeChoice === 'variation' ? createTypeParentId : null
    });
    setContractFormErrors({});
    setShowCreateContractTypePrompt(false);
  };

  /** "Nästa" in "Vad vill du skapa?": a new contract or an ÄTA, both in the guided wizard. */
  const handleAgreementChoice = async (choice: AgreementChoice) => {
    if (choice === 'fixed') {
      setShowCreateContractTypePrompt(false);
      await openContractWizard();
      return;
    }
    // Always the agreement in force: the original contract and its latest ÄTA.
    const basis = variationBasis(contracts, Object.values(contractMilestonesMap).flat(), selectedContract?.id);
    if (!basis) return;
    setAgreementChoiceOpening(true);
    setAgreementChoiceError('');
    try {
      // Closes the choice when the ÄTA wizard opens.
      await openStageVariation(basis.parentId);
    } catch (error) {
      setAgreementChoiceError(variationOpenErrorText(error, knownApiErrorText(t, error) ?? t('stageVariation.loadFailed')));
    } finally {
      setAgreementChoiceOpening(false);
    }
  };

  const confirmCreateContractType = async () => {
    if (!id) return;
    if (createTypeChoice === 'variation') {
      const parentId = createTypeParentId || eligibleParentContracts.find(item => item.id === selectedContract?.id)?.id || eligibleParentContracts[0]?.id;
      if (!parentId) return;
      try { await openStageVariation(parentId); }
      catch (error) { setMessage({ type: 'error', text: variationOpenErrorText(error, knownApiErrorText(t, error) ?? t('stageVariation.loadFailed')) }); }
      return;
    }
    if (FEATURE_CONTRACT_WIZARD_V2) {
      setShowCreateContractTypePrompt(false);
      await openContractWizard();
      return;
    }
    await beginContractCreation();
  };

  // Explicit save from the "Skapa nytt kontrakt" modal (autosave removed here).
  // Creates the contract, refreshes the list and selects the new row so its
  // milestones/description/history/attachments load in the editing views.
  const handleSaveNewContract = async () => {
    if (!id || isSavingNewContract) return;
    setIsSavingNewContract(true);
    try {
      const created = await contractApi.createContract(id, {
        ...contractFormData,
        ...(conflictVariationDraft ? {
          resolvesMilestoneId: conflictVariationDraft.milestone.id,
          referenceMessageId: conflictVariationDraft.reference?.id
        } : {})
      });
      setIsCreatingContract(false);
      setConflictVariationDraft(null);
      handleSelectContract(created);
      setMessage({ type: 'success', text: t('contracts.form.saveSuccess') });
      // The list is refreshed in the background (ClickUp 869fgxq45).
      void loadContracts();
    } catch (error) {
      console.error('Failed to create contract:', error);
      // A create that ran out of time may still have gone through: show the
      // current list so the user can check before trying again.
      if (isRequestTimeout(error)) void loadContracts();
      setMessage({ type: 'error', text: saveErrorText(t, error, 'contracts.form.saveError', 'create') });
    } finally {
      setIsSavingNewContract(false);
    }
  };

  // Cancelling the create modal asks for confirmation first — the unsaved
  // contract details are discarded (no autosave keeps them).
  const requestCancelNewContract = () => setShowCancelNewContractConfirm(true);

  const confirmCancelNewContract = () => {
    setShowCancelNewContractConfirm(false);
    setIsCreatingContract(false);
    setConflictVariationDraft(null);
    setContractFormData(emptyContractForm);
    setContractFormErrors({});
  };

  // The type is locked in the form, so changing it means starting over. Ask for
  // confirmation in a styled overlay first — any data already entered in the
  // form is discarded when the type-selection overlay reopens.
  const handleChangeContractType = () => {
    setShowChangeTypeConfirm(true);
  };

  const confirmChangeContractType = () => {
    setShowChangeTypeConfirm(false);
    setCreateTypeChoice(contractFormData.contractType);
    setCreateTypeParentId(contractFormData.parentContractId ?? '');
    setCreateSettlementType(contractFormData.settlementType);
    setContractFormData(emptyContractForm);
    setContractFormErrors({});
    setIsCreatingContract(false);
    setShowCreateContractTypePrompt(true);
  };

  const handleSelectContract = async (contract: Contract) => {
    setStageVariationDirty(false);
    setSelectedContract(contract);
    setIsCreatingContract(false);
    setMilestones([]);
    setContractFormData({
      contractAmount: contract.contractAmount,
      rotAmount: contract.rotAmount,
      afterRotAmount: contract.afterRotAmount,
      contractType: contract.contractType || 'fixed',
      settlementType: contract.settlementType ?? 'milestones',
      parentContractId: contract.parentContractId ?? null
    });
    setContractFormErrors({});
    // Load milestones, descriptions, history and attachments for this contract
    const loading = Promise.all([
      loadMilestones(contract.id),
      loadContractDescriptions(contract.id),
      loadContractHistory(contract.id),
      loadContractAttachments(contract.contractNumber)
    ]);
    setSelectedMilestone(null);
    setIsCreatingMilestone(false);
    setMilestoneFormData(emptyMilestoneForm);
    const [, descriptions] = await loading;
    return descriptions;
  };

  // Row-action buttons in the contract list: select the contract (loading its
  // views) and scroll to the requested section below.
  const handleContractRowAction = (
    contractId: string,
    section: ContractRowAction
  ) => {
    if (section === 'signedDocument') {
      // The stored signed PDF in the documents' preview (loading, error, download).
      const target = contracts.find((c) => c.id === contractId);
      if (target) {
        void attachmentPreview.openPreviewFrom(
          () => contractApi.getSignedDocument(target.id),
          t('contracts.signedDocument.fileName', { number: target.contractNumber }),
          t('contracts.signedDocument.missing')
        );
      }
      return;
    }
    if (section === 'createVariation') {
      void openStageVariation(contractId).catch(error => setMessage({ type: 'error', text: variationOpenErrorText(error, knownApiErrorText(t, error) ?? t('variationWizard.loadError')) }));
      return;
    }
    if (section === 'agreement') { setVariationAgreementId(contractId); return; }
    // A double-click on a row (ClickUp 869fmxfb7): a contract signed by both
    // opens its preview, one the company can still change opens "Redigera".
    if (section === 'open') {
      const target = contracts.find((c) => c.id === contractId);
      if (!target) return;
      if (contractViewState(target) === 'signed') {
        // The company's read-only view keeps "Skapa ÄTA-kontrakt".
        if (FEATURE_CONTRACT_WIZARD_V2 && !isPerson && wizardEditStatus(target)) void openContractWizardForEdit(target);
        else void handleOpenPreview('preview', target);
        return;
      }
      // The Beställare's turn opens "Granska och signera avtal" (869fmxfb7);
      // otherwise the agreement opens as a preview.
      if (isPerson) {
        const canReview = canSignContract(target, 'person') || needsPersonDraftReview(target);
        void handleOpenPreview(canReview ? 'sign' : 'preview', target);
        return;
      }
      section = 'edit';
    }
    if (section === 'review') {
      const target = contracts.find((c) => c.id === contractId);
      if (target) void handleOpenPreview('sign', target);
      return;
    }
    const variation = contracts.find(c => c.id === contractId);
    if (!isPerson && variation && (variation.stageVariation || variation.settlementType === 'price_adjustment') && (section === 'edit' || section === 'milestones')) {
      void openVariationWizardForEdit(variation, section === 'milestones' ? 1 : 0); return;
    }
    if (section === 'delete') {
      const target = contracts.find((c) => c.id === contractId);
      if (target) {
        setDeleteDraftError('');
        setDeleteDraftTarget(target);
      }
      return;
    }
    // New wizard: "Redigera" on a fixed-price contract opens it in the wizard,
    // "Etappmål" opens it directly on step 2 "Pris & etappmål" (signed
    // contracts open read-only). Contracts the wizard cannot handle keep the
    // contract page.
    if (FEATURE_CONTRACT_WIZARD_V2 && (section === 'edit' || section === 'milestones') && !isPerson) {
      const contract = contracts.find((c) => c.id === contractId);
      if (contract && wizardEditStatus(contract)) {
        void openContractWizardForEdit(contract, { startStep: section === 'milestones' ? 1 : 0 });
        return;
      }
    }
    if (selectedContract?.id !== contractId) {
      const contract = contracts.find((c) => c.id === contractId);
      if (contract) handleSelectContract(contract);
    }
    // The scroll itself runs in an effect so it fires after the selected
    // contract's sections (milestones/history/attachments) have loaded and the
    // layout has settled — a fixed timeout races the async loads.
    setPendingScrollSection(section);
  };

  // "Öppna och ändra kontrakt" (a returned contract in "Att göra nu") links
  // here with `edit=1`: open the contract in its editor once it is selected,
  // and drop the parameter so a reload does not reopen it (ClickUp 869fftzmh).
  const editRequested = !isPerson && searchParams.get('edit') === '1';
  const editOpenedRef = useRef(false);
  useEffect(() => {
    if (!editRequested || editOpenedRef.current || !selectedContract) return;
    if (initialContractId && selectedContract.id !== initialContractId) return;
    editOpenedRef.current = true;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('edit');
      return next;
    }, { replace: true });
    handleContractRowAction(selectedContract.id, 'edit');
    // Opens once per visit; the handler is recreated on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRequested, selectedContract]);

  // Unsent drafts can be removed (same rule as the API's ContractsService.remove):
  // never sent, signed or paid, no ÄTA hangs on it, and not the project's only
  // active contract — the first contract is created with the project.
  const isDeletableDraft = (contract: Contract) =>
    !isPerson && (isDiscardableAmendment(contract) || (
    contract.draftStatus === 'none' &&
    !contract.companySigned &&
    !contract.personSigned &&
    (contract.versionStatus ?? 'current') === 'current' &&
    !contract.replacesContractId &&
    (!contract.signingStatus || contract.signingStatus === 'draft') &&
    (contract.paidAmount ?? 0) === 0 &&
    !contracts.some((candidate) => candidate.parentContractId === contract.id) &&
    activeContracts.length > 1));

  // An ÄTA with stages the Hantverkare started and has neither signed nor
  // sent: the API discards it with its plan, the original version stays
  // (ClickUp 869fkhkm0, same rule as the API's discardUnsentAmendment).
  const isDiscardableAmendment = (contract: Contract) => {
    const settlement = contractReplacements[contract.id];
    return !!contract.stageVariation &&
      !!contract.replacesContractId &&
      contract.versionStatus === 'proposed' &&
      !contract.companySigned &&
      !contract.personSigned &&
      !contract.draftSentAt &&
      !contract.signingSentAt &&
      settlement?.replacementContractId === contract.id &&
      settlement.status === 'draft' &&
      settlement.initiatorRole !== 'person' &&
      !settlement.disputedMilestoneId;
  };

  const confirmDeleteDraft = async () => {
    if (!deleteDraftTarget) return;
    setIsDeletingDraft(true);
    setDeleteDraftError('');
    try {
      await contractApi.deleteContract(deleteDraftTarget.id);
      if (selectedContract?.id === deleteDraftTarget.id) setSelectedContract(null);
      setDeleteDraftTarget(null);
      await loadContracts();
      loadDocuments();
      setMessage({ type: 'success', text: t(deleteDraftTarget.stageVariation ? 'contracts.deleteDraft.successVariation' : 'contracts.deleteDraft.success') });
      setTimeout(() => setMessage(null), 4000);
    } catch (error) {
      console.error('Failed to delete contract draft:', error);
      const code = apiErrorCode(error);
      setDeleteDraftError(t(
        code === 'CONTRACT_ONLY_IN_PROJECT'
          ? 'contracts.deleteDraft.errorOnlyContract'
          : code === 'CONTRACT_NOT_DELETABLE'
            ? 'contracts.deleteDraft.errorNotDeletable'
            : 'contracts.deleteDraft.error'
      ));
    } finally {
      setIsDeletingDraft(false);
    }
  };

  // Scroll to the requested contract section once it and its async content have
  // rendered; re-runs as the contract's data lands so the target stays in view.
  useEffect(() => {
    if (!pendingScrollSection || !selectedContract) return;
    const anchorId = {
      milestones: 'contract-section-milestones',
      history: 'contract-section-history',
      edit: 'contract-section-edit',
      reference: 'contract-section-reference'
    }[pendingScrollSection];
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pendingScrollSection, selectedContract, milestones, contractHistory, contractAttachments]);

  // Stop re-scrolling shortly after so later updates / manual scrolling win.
  useEffect(() => {
    if (!pendingScrollSection) return;
    const timer = window.setTimeout(() => setPendingScrollSection(null), 900);
    return () => window.clearTimeout(timer);
  }, [pendingScrollSection]);

  const persistPriceAdjustmentAmount = async (amountOre: number) => {
    if (!selectedContract || contractLockedForEditing || isPerson) return;
    setIsSaving(true);
    setMessage(null);
    try {
      const amount = String(amountOre / 100);
      const updated = await contractApi.updateContract(selectedContract.id, {
        contractAmount: amount,
        afterRotAmount: amount
      });
      handleSelectContract(updated);
      setMessage({ type: 'success', text: t('contracts.form.autosave.saved') });
    } catch (error) {
      console.error('Failed to save variation price adjustment:', error);
      setMessage({ type: 'error', text: apiErrorText(t, error, 'projectView.ata.amountSaveError') });
      await reloadSelectedContract();
    } finally {
      setIsSaving(false);
    }
  };

  const handlePriceAdjustmentAmountBlur = async () => {
    if (!selectedContract || contractLockedForEditing || isPerson) return;
    const amount = parseSekInput(contractFormData.contractAmount);
    if (amount === null) {
      setContractFormErrors((previous) => ({
        ...previous,
        contractAmount: t('projectView.ata.invalidAmount')
      }));
      return;
    }
    const amountOre = Math.round(amount * 100);
    const savedAmountOre = Math.round(
      (parseSekInput(selectedContract.afterRotAmount) ?? 0) * 100
    );
    if (amountOre === savedAmountOre) return;
    setContractFormErrors((previous) => ({ ...previous, contractAmount: '' }));
    if (amountOre < 0) {
      const availableAmountOre = await loadNegativeVariationCapacity();
      if (availableAmountOre === null) return;
      if (Math.abs(amountOre) > availableAmountOre) {
        setContractFormErrors((previous) => ({
          ...previous,
          contractAmount: t('projectView.ata.exceedsHeldFunds', {
            available: formatNumber(availableAmountOre / 100)
          })
        }));
        return;
      }
      setNegativeVariationConfirmAmountOre(Math.abs(amountOre));
      setNegativeVariationConfirmAction('edit_price_adjustment');
      setShowNegativeVariationConfirm(true);
      return;
    }
    await persistPriceAdjustmentAmount(amountOre);
  };

  // Milestone handlers
  const paidMilestoneBadge = () => (
    <span className="badge bg-secondary ms-2" title={t(canAdjustPaidMilestone ? 'contractReplacement.paidAdjustmentHelp' : 'projectView.wizard.paidLockedHelp')}>
      <i className="feather-lock me-1" aria-hidden="true"></i>
      {t(canAdjustPaidMilestone ? 'contractReplacement.paidBadge' : 'projectView.wizard.paidLocked')}
    </span>
  );

  const handleMilestoneInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    // Stage amounts are whole kronor, also for a single stage outside the wizard.
    const value = ['amount', 'grossAmount', 'rotAmount'].includes(name) ? toWholeKronor(e.target.value) : e.target.value;
    setMilestoneFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (milestoneFormErrors[name] || ['amount', 'grossAmount', 'rotAmount'].includes(name)) {
      setMilestoneFormErrors(prev => ({ ...prev, [name]: '', ...(['amount', 'grossAmount', 'rotAmount'].includes(name) ? { amount: '', grossAmount: '', rotAmount: '' } : {}) }));
    }
  };

  const validateMilestoneForm = (): boolean => {
    const errors: Record<string, string> = {};

    // The milestone description is optional: it may be cleared or left empty
    // (the agreement/preview fall back to plain "Etappmål N").

    if (!milestoneFormData.amount || isNaN(Number(milestoneFormData.amount))) {
      errors.amount = t('validation.required');
    }
    const wholeKronorFields: Array<'amount' | 'grossAmount' | 'rotAmount'> =
      selectedContract?.pricingMode === 'bottom_up' ? ['amount', 'rotAmount'] : ['amount', 'grossAmount'];
    const notWhole = wholeKronorFields.find(field => {
      const raw = String(milestoneFormData[field] ?? '');
      return raw !== '' && Number.isFinite(Number(raw)) && !Number.isInteger(Number(raw));
    });
    if (notWhole) {
      errors[notWhole] = t('milestones.wizard.errWholeKronor');
      setMilestoneFormErrors(errors);
      return false;
    }
    if (selectedContract?.pricingMode !== 'bottom_up' && (!milestoneFormData.grossAmount || !Number.isFinite(Number(milestoneFormData.grossAmount)))) {
      errors.grossAmount = t('validation.required');
    } else if (!milestoneFormAmounts(
      milestones, selectedMilestone?.id, Number(milestoneFormData.amount),
      Number(milestoneFormData.grossAmount), Number(selectedContract?.contractAmount), selectedContract?.pricingMode, Number(milestoneFormData.rotAmount)
    ).valid) {
      errors[selectedContract?.pricingMode === 'bottom_up' ? 'rotAmount' : 'grossAmount'] = t('milestones.form.invalidStageAmounts');
    }

    setMilestoneFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateMilestone = async () => {
    if (!selectedContract) return;
    try {
      const nextNumber = await milestoneApi.getNextMilestoneNumber(selectedContract.id);
      setNextMilestoneNumber(nextNumber.nextMilestoneNumber);
      // Non-guided flow: one milestone at a time in a modal (guided planning of
      // several at once is the wizard).
      setSelectedMilestone(null);
      setMilestoneWizardOpen(false);
      setMilestoneFormData(emptyMilestoneForm);
      setMilestoneFormErrors({});
      setIsCreatingMilestone(true);
    } catch (error) {
      console.error('Failed to prepare milestone creation:', error);
      setMessage({ type: 'error', text: t('milestones.form.saveError') });
    }
  };

  // ---- Guided milestone-plan wizard ----
  const wizParseAmount = (s: string): number =>
    parseFloat(String(s || '').replace(/[^0-9.-]/g, '')) || 0;

  // Etappmål amounts are whole kronor in the UI — block decimal/exponent keys so
  // the number fields never accept öre through typing.
  const blockDecimalKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['.', ',', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
  };
  // Strip any decimal part that slips in another way (paste, spinner rounding).
  const toWholeKronor = (value: string): string => value.replace(/[.,].*$/, '');

  // The guided plan is always top-down: the company enters the tender total and
  // the amount after ROT, then distributes it over the stages. Single stages
  // are added outside the wizard.
  const wizPreservedMilestones = milestones.filter(milestone => milestone.deliveryStatus === 'paid');
  const wizTotalNum = wizParseAmount(wizTotal);
  const wizCountNum = Math.min(50, Math.max(1, parseInt(wizCount, 10) || 1));
  const wizAfterRotNum = wizAfterRotValue === ''
    ? wizTotalNum
    : wizParseAmount(wizAfterRotValue);
  const wizPlannedRotNum = wizTotalNum - wizAfterRotNum;
  const wizHasRot = Math.round(wizPlannedRotNum * 100) !== 0;
  const wizMinimumCount = Math.max(1, wizRows.length);
  const wizPreservedAfterRotNum = wizPreservedMilestones.reduce(
    (sum, milestone) => sum + milestone.afterRotAmount,
    0
  );
  const wizMilestoneTargetNum =
    (wizHasRot ? wizAfterRotNum : wizTotalNum) - wizPreservedAfterRotNum;

  const wizComputedAmounts = (): number[] => {
    if (wizMode === 'amount') {
      return wizRows.map(r => Math.round(wizParseAmount(r.amount) * 100) / 100);
    }
    // Percent: round to whole kronor and put the deterministic remainder on the
    // final row so the milestone total remains exactly equal to the payable
    // total – stage amounts are always whole kronor.
    const totalKronor = Math.round(wizMilestoneTargetNum);
    const roundedKronor = wizRows.map(r => Math.round((wizParseAmount(r.percent) / 100) * totalKronor));
    if (roundedKronor.length > 0) {
      const priorRowsKronor = roundedKronor
        .slice(0, -1)
        .reduce((sum, amount) => sum + amount, 0);
      roundedKronor[roundedKronor.length - 1] = totalKronor - priorRowsKronor;
    }
    return roundedKronor;
  };

  const wizSyncRows = (n: number) => {
    const nextBasis = `${Math.round(wizMilestoneTargetNum * 100)}:${n}`;
    const distributionChanged = wizDistributionBasisRef.current !== nextBasis;
    setWizRows(prev => {
      return reconcileMilestonePlanRows({
        previousRows: prev,
        count: n,
        startNumber: nextMilestoneNumber,
        defaultDescription: t('milestones.wizard.defaultDesc', 'Etappmål'),
        distributionChanged
      });
    });
    wizDistributionBasisRef.current = nextBasis;
  };

  const wizValidateStep = (step: number): boolean => {
    setWizError('');
    if (step === 0) {
      if (!Number.isInteger(wizTotalNum) || !Number.isInteger(wizAfterRotNum)) {
        setWizError(t('milestones.wizard.errWholeKronor'));
        return false;
      }
      const totalIsValid = selectedContract?.contractType === 'variation'
        ? wizTotalNum !== 0
        : wizTotalNum > 0;
      if (!totalIsValid) {
        setWizError(t('milestones.wizard.errTotal', 'Ange en giltig kontrakts-anbudssumma (större än 0).'));
        return false;
      }
      if (
        wizPlannedRotNum !== 0 &&
        (Math.sign(wizPlannedRotNum) !== Math.sign(wizTotalNum) ||
          Math.abs(wizPlannedRotNum) > Math.abs(wizTotalNum))
      ) {
        setWizError(t('projectView.wizard.errRotDirection'));
        return false;
      }
      if (wizPreservedMilestones.length > 0 && wizMilestoneTargetNum < 0) {
        setWizError(t('projectView.wizard.errBelowPreservedMilestones'));
        return false;
      }
      return true;
    }
    if (step === 1) {
      const n = parseInt(wizCount, 10);
      if (Number.isFinite(n) && n < wizMinimumCount) {
        setWizError(t('projectView.wizard.errExistingCount', { minimum: wizMinimumCount }));
        return false;
      }
      if (isNaN(n) || n < 1 || n > 50) {
        setWizError(t('milestones.wizard.errCount', 'Ange ett antal mellan 1 och 50.'));
        return false;
      }
      return true;
    }
    // step 2
    if (wizRows.some(r => !r.id && !r.description.trim())) {
      setWizError(t('milestones.wizard.errDesc', 'Alla etappmål måste ha en beskrivning.'));
      return false;
    }
    if (wizMode === 'amount') {
      if (wizRows.some(r => !Number.isInteger(wizParseAmount(r.amount)))) {
        setWizError(t('milestones.wizard.errWholeKronor'));
        return false;
      }
      if (wizRows.some(r => wizParseAmount(r.amount) === 0 || Math.sign(wizParseAmount(r.amount)) !== Math.sign(wizMilestoneTargetNum))) {
        setWizError(t('projectView.wizard.errNonZeroSameSign'));
        return false;
      }
      const sumOre = wizRows.reduce((s, r) => s + Math.round(wizParseAmount(r.amount) * 100), 0);
      const targetOre = Math.round(wizMilestoneTargetNum * 100);
      if (sumOre !== targetOre) {
        setWizError(
          (wizHasRot
            ? t('projectView.wizard.errSumAfterRot')
            : t('milestones.wizard.errSumAmount', 'Beloppen ({{sum}} kr) måste summera till anbudssumman ({{target}} kr).'))
            .replace('{{sum}}', formatNumber(sumOre / 100))
            .replace('{{target}}', formatNumber(targetOre / 100))
        );
        return false;
      }
      return true;
    }
    // percent
    const pSum = wizRows.reduce((s, r) => s + wizParseAmount(r.percent), 0);
    if (Math.abs(pSum - 100) > 0.01) {
      setWizError(
        t('milestones.wizard.errSumPercent', 'Procentsatserna ({{sum}} %) måste summera till 100 %.')
          .replace('{{sum}}', String(Math.round(pSum * 100) / 100))
      );
      return false;
    }
    if (wizComputedAmounts().some(amount => amount === 0 || Math.sign(amount) !== Math.sign(wizMilestoneTargetNum))) {
      setWizError(t('projectView.wizard.errNonZeroSameSign'));
      return false;
    }
    return true;
  };

  const handleOpenMilestoneWizard = async () => {
    if (!selectedContract) return;
    try {
      const [currentMilestones, nextNumber] = await Promise.all([
        milestoneApi.getMilestones(selectedContract.id),
        milestoneApi.getNextMilestoneNumber(selectedContract.id)
      ]);
      setMilestones(currentMilestones);
      setNextMilestoneNumber(nextNumber.nextMilestoneNumber);
      const editableMilestones = currentMilestones
        .filter(milestone => milestone.deliveryStatus !== 'paid')
        .sort((left, right) => left.milestoneNumber - right.milestoneNumber);
      const payableTotal = editableMilestones.reduce((sum, milestone) => sum + milestone.afterRotAmount, 0);
      const existingRows: MilestonePlanWizardRow[] = editableMilestones.map(milestone => ({
        id: milestone.id,
        milestoneNumber: milestone.milestoneNumber,
        description: milestone.description,
        amount: String(milestone.afterRotAmount),
        rotAmount: String(milestone.rotAmount),
        percent: payableTotal !== 0 ? String(milestone.afterRotAmount / payableTotal * 100) : '',
        endDate: milestone.endDate?.slice(0, 10) || ''
      }));
      // Close any in-progress single-milestone form so it doesn't render alongside.
      setSelectedMilestone(null);
      setIsCreatingMilestone(false);
      const existingAmt = wizParseAmount(selectedContract.contractAmount || '');
      setWizStep(0);
      setWizTotal(existingAmt !== 0 ? String(existingAmt) : '');
      setWizCount(String(Math.max(1, existingRows.length)));
      setWizMode('amount');
      setWizAfterRotValue(existingAmt !== 0 ? selectedContract.afterRotAmount : '');
      setWizRows(existingRows);
      wizDistributionBasisRef.current = '';
      setWizError('');
      setMilestoneWizardOpen(true);
      setTimeout(() => milestoneFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (error) {
      console.error('Failed to prepare milestone wizard:', error);
      setMessage({ type: 'error', text: t('milestones.form.saveError') });
    }
  };

  const handleWizardNext = () => {
    if (!wizValidateStep(wizStep)) return;
    if (wizStep === 1) wizSyncRows(wizCountNum);
    setWizStep(s => Math.min(2, s + 1));
  };

  const handleWizardCancel = () => {
    setMilestoneWizardOpen(false);
    setWizError('');
  };

  const persistMilestonePlan = async () => {
    if (!selectedContract) return;
    setIsSavingPending(true);
    setMessage(null);
    try {
      const amts = wizComputedAmounts();
      const planMilestones = wizRows.map((row, index) => ({
        id: row.id,
        description: row.description.trim(),
        amount: amts[index],
        endDate: row.endDate
      }));
      if (selectedContract.replacesContractId) {
        await milestoneApi.replaceReplacementMilestonePlan(selectedContract.id, {
          pricingMode: 'top_down',
          contractAmount: String(wizTotalNum),
          afterRotAmount: String(wizAfterRotNum),
          milestones: planMilestones
        });
      } else {
        await milestoneApi.replaceMilestonePlan(selectedContract.id, {
          pricingMode: 'top_down',
          contractAmount: String(wizTotalNum),
          afterRotAmount: String(wizAfterRotNum),
          milestones: planMilestones
        });
      }
      setMessage({ type: 'success', text: t('milestones.form.saveSuccess') });
      setMilestoneWizardOpen(false);
      // Reloads run in the background; the plan is saved (ClickUp 869fgxq45).
      const contractId = selectedContract.id;
      void (async () => {
        await loadMilestones(contractId);
        await reloadSelectedContract();
      })();
      if (id) loadProjectStatusSteps(id);
    } catch (error) {
      console.error('Failed to save milestone plan:', error);
      setWizError(apiErrorCode(error) === 'MILESTONE_PLAN_CHANGED'
        ? t('projectView.wizard.errPlanChanged')
        : saveErrorText(t, error, 'milestones.form.saveError'));
    } finally {
      setIsSavingPending(false);
    }
  };

  const handleWizardSubmit = async () => {
    if (!selectedContract || !wizValidateStep(2)) return;
    const amountOre = Math.round(wizAfterRotNum * 100);
    if (selectedContract.contractType === 'variation' && amountOre < 0) {
      const availableAmountOre = await loadNegativeVariationCapacity();
      if (availableAmountOre === null) {
        setWizError(t('projectView.ata.capacityLoadError'));
        return;
      }
      if (Math.abs(amountOre) > availableAmountOre) {
        setWizError(
          t('projectView.ata.exceedsHeldFunds', {
            available: formatNumber(availableAmountOre / 100)
          })
        );
        return;
      }
      setNegativeVariationConfirmAmountOre(Math.abs(amountOre));
      setNegativeVariationConfirmAction('milestone_plan');
      setShowNegativeVariationConfirm(true);
      return;
    }
    await persistMilestonePlan();
  };

  const closeNegativeVariationConfirmation = () => {
    setShowNegativeVariationConfirm(false);
    if (negativeVariationConfirmAction === 'create') {
      setShowCreateContractTypePrompt(true);
    }
    setNegativeVariationConfirmAction(null);
  };

  const confirmNegativeVariation = async () => {
    const action = negativeVariationConfirmAction;
    setShowNegativeVariationConfirm(false);
    setNegativeVariationConfirmAction(null);
    if (action === 'create') {
      await beginContractCreation();
    } else if (action === 'milestone_plan') {
      await persistMilestonePlan();
    } else if (action === 'edit_price_adjustment') {
      await persistPriceAdjustmentAmount(-negativeVariationConfirmAmountOre);
    }
  };

  const handleSelectMilestone = (milestone: Milestone) => {
    setSelectedMilestone(milestone);
    setIsCreatingMilestone(false);
    setMilestoneWizardOpen(false);
    setMilestoneFormData({
      description: milestone.description,
      grossAmount: String(milestone.amount),
      amount: String(selectedContract?.pricingMode === 'bottom_up' ? milestone.amount : milestone.afterRotAmount),
      rotAmount: String(milestone.rotAmount),
      endDate: milestone.endDate || ''
    });
    setMilestoneFormErrors({});
    setTimeout(() => milestoneFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCancelMilestone = () => {
    setIsCreatingMilestone(false);
    setSelectedMilestone(null);
    setMilestoneFormData(emptyMilestoneForm);
  };

  // Has the milestone form got unsaved edits? (create: any field filled;
  // edit: any field changed from the selected milestone.)
  const milestoneFormDirty = (): boolean => {
    if (isCreatingMilestone) {
      return !!(milestoneFormData.amount || Number(milestoneFormData.rotAmount) || milestoneFormData.description || milestoneFormData.endDate)
        || !!milestoneFormData.grossAmount;
    }
    if (selectedMilestone) {
      return milestoneFormData.amount !== String(selectedContract?.pricingMode === 'bottom_up' ? selectedMilestone.amount : selectedMilestone.afterRotAmount)
        || milestoneFormData.rotAmount !== String(selectedMilestone.rotAmount)
        || milestoneFormData.grossAmount !== String(selectedMilestone.amount)
        || milestoneFormData.description !== (selectedMilestone.description || '')
        || milestoneFormData.endDate !== (selectedMilestone.endDate || '');
    }
    return false;
  };

  // Close the milestone modal, confirming first if there are unsaved edits so
  // nothing typed is silently discarded (Avbryt / Escape / backdrop all route
  // here). The person view is read-only, so it closes without a prompt.
  const handleMilestoneModalRequestClose = () => {
    if (pendingCloseTarget) return; // confirmation already showing
    if (!isPerson && milestoneFormDirty()) {
      setPendingCloseTarget('milestone');
      return;
    }
    handleCancelMilestone();
  };

  const wizardDirty = (): boolean =>
    wizTotal.trim() !== '' || wizRows.some(r => (r.amount || '').trim() !== '' || (r.percent || '').trim() !== '');

  const handleWizardRequestClose = () => {
    if (pendingCloseTarget) return; // confirmation already showing
    if (wizardDirty()) {
      setPendingCloseTarget('wizard');
      return;
    }
    handleWizardCancel();
  };

  // Small confirmation modal: discard the unsaved edits and close the form.
  const confirmDiscardMilestoneClose = () => {
    if (pendingCloseTarget === 'milestone') handleCancelMilestone();
    else if (pendingCloseTarget === 'wizard') handleWizardCancel();
    setPendingCloseTarget(null);
  };

  const milestoneAmounts = milestoneFormAmounts(milestones, selectedMilestone?.id, Number(milestoneFormData.amount), Number(milestoneFormData.grossAmount), Number(selectedContract?.contractAmount), selectedContract?.pricingMode, Number(milestoneFormData.rotAmount));

  const handleSaveMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateMilestoneForm() || !selectedContract) return;

    setIsSavingMilestone(true);
    setMessage(null);

    try {
      const data = {
        description: milestoneFormData.description,
        ...(selectedContract.pricingMode === 'bottom_up'
          ? { amount: Number(milestoneFormData.amount), rotAmount: Number(milestoneFormData.rotAmount) }
          : {
            amount: Number(milestoneFormData.grossAmount),
            rotAmount: (Math.round(Number(milestoneFormData.grossAmount) * 100) - Math.round(Number(milestoneFormData.amount) * 100)) / 100
          }),
        endDate: milestoneFormData.endDate
      };

      if (isCreatingMilestone) {
        await milestoneApi.createMilestone(selectedContract.id, data);
        setMessage({ type: 'success', text: t('milestones.form.saveSuccess') });
      } else if (selectedMilestone) {
        await milestoneApi.updateMilestone(selectedMilestone.id, data);
        setMessage({ type: 'success', text: t('milestones.form.saveSuccess') });
      }
      // Close the modal after a successful save (one milestone at a time); the
      // reloads run in the background (ClickUp 869fgxq45).
      setSelectedMilestone(null);
      setIsCreatingMilestone(false);
      setMilestoneFormData(emptyMilestoneForm);
      const contractId = selectedContract.id;
      void (async () => {
        await loadMilestones(contractId);
        await reloadSelectedContract();
      })();
      if (id) loadProjectStatusSteps(id);
    } catch (error) {
      console.error('Failed to save milestone:', error);
      // A new stage that ran out of time may exist anyway: show the current list.
      if (isCreatingMilestone && isRequestTimeout(error)) void loadMilestones(selectedContract.id);
      setMessage({
        type: 'error',
        text: isContractLockedError(error)
          ? t('milestones.form.lockedError')
          : saveErrorText(t, error, 'milestones.form.saveError', isCreatingMilestone ? 'create' : 'update')
      });
    } finally {
      setIsSavingMilestone(false);
    }
  };

  const performDeleteMilestone = async () => {
    if (!selectedMilestone || !selectedContract) return;
    setShowDeleteMilestoneConfirm(false);
    setMessage(null);
    try {
      await milestoneApi.deleteMilestone(selectedMilestone.id);
      setMessage({ type: 'success', text: t('milestones.form.deleteSuccess') });
      setSelectedMilestone(null);
      setMilestoneFormData(emptyMilestoneForm);
      await loadMilestones(selectedContract.id);
      await reloadSelectedContract();
      if (id) loadProjectStatusSteps(id);
    } catch (error) {
      console.error('Failed to delete milestone:', error);
      setMessage({ type: 'error', text: apiErrorText(t, error, 'milestones.form.saveError') });
    }
  };

  const getMilestoneAttestationBadge = (status: MilestoneAttestationStatus) => {
    switch (status) {
      case 'approved':
        return <span className="badge bg-success">{t('milestones.attestation.approved')}</span>;
      case 'rejected':
        return <span className="badge bg-danger">{t('milestones.attestation.rejected')}</span>;
      case 'dispute':
        return <span className="badge bg-dark">{t('milestones.attestation.dispute')}</span>;
      default:
        return <span className="badge bg-warning">{t('milestones.attestation.notAttested')}</span>;
    }
  };

  // Load messages for ALL milestones (for contract tab display)
  useEffect(() => {
    if (milestones.length === 0) return;
    Promise.all(milestones.map(m =>
      milestoneApi.getMessages(m.id).then(msgs => ({ id: m.id, msgs })).catch(() => ({ id: m.id, msgs: [] as MilestoneMessage[] }))
    )).then(results => {
      const map: Record<string, MilestoneMessage[]> = {};
      results.forEach(r => { map[r.id] = r.msgs; });
      setAllMilestoneMessages(map);
    });
  }, [milestones]);

  const openAttestationModal = useCallback(async (
    milestone: Milestone,
    mode: 'attestation' | 'resolution' = 'attestation'
  ) => {
    if (milestone.stageReplacement || milestone.continuedBy) {
      setShowAttestModal(false);
      window.location.assign(`/projects/${encodeURIComponent(id ?? '')}?tab=contract&contractId=${encodeURIComponent(milestone.contractId)}`);
      return;
    }
    setAttestMilestone(milestone);
    setAttestModalMode(mode);
    try {
      const msgs = await milestoneApi.getMessages(milestone.id);
      setMilestoneMessages(msgs);
    } catch { setMilestoneMessages([]); }
    setShowAttestModal(true);
  }, [id]);

  const openMilestoneMessageId = showAttestModal ? attestMilestone?.id : undefined;
  // The dialog shows the stage's conversation, so opening it reads the thread
  // (ClickUp 869fegbvk); the badge next to Meddelanden follows.
  useEffect(() => {
    if (!openMilestoneMessageId) return;
    readStageThread(openMilestoneMessageId);
    // Again when the threads arrive after the dialog opened; once read the
    // thread has no unread messages, so this does not loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMilestoneMessageId, projectThreads]);
  useEffect(() => {
    if (!openMilestoneMessageId) return;
    let cancelled = false;
    let loading = false;
    const refreshMessages = async () => {
      if (loading) return;
      loading = true;
      try {
        const messages = await milestoneApi.getMessages(openMilestoneMessageId);
        if (!cancelled) setMilestoneMessages(messages);
      } catch {
        // Keep the conversation and draft intact; retry on the next interval.
      } finally {
        loading = false;
      }
    };

    // Opening the dialog already loads its messages. Keep polling independently
    // of the active tab and the timeline's latest-reply summaries.
    const interval = window.setInterval(() => void refreshMessages(), 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [openMilestoneMessageId]);

  // Deep links from notifications and the start page's project cards:
  // ?tab=timeline&milestoneId=…&action=reply|resolution|attest opens the same
  // modal as the stage's action button here.
  useEffect(() => {
    if (
      activeTab !== 'timeline' ||
      !requestedMilestoneAction ||
      !['reply', 'resolution', 'attest'].includes(requestedMilestoneAction) ||
      !requestedMilestoneId
    ) return;
    const milestone = Object.values(contractMilestonesMap)
      .flat()
      .find(candidate => candidate.id === requestedMilestoneId);
    if (!milestone) return;
    const deepLinkKey = `${id ?? ''}:${requestedMilestoneId}:${requestedMilestoneAction}`;
    if (handledMilestoneDeepLinkRef.current === deepLinkKey) return;
    handledMilestoneDeepLinkRef.current = deepLinkKey;
    if (requestedMilestoneAction !== 'attest') {
      void openAttestationModal(milestone, 'resolution');
    } else if (milestone.deliveryStatus === 'under_resolution' && !isPerson) {
      void openRemediedAttestationRef.current?.(milestone);
    } else {
      setAttestOpenedFromLink(true);
      void openAttestationModal(milestone);
    }
  }, [
    activeTab,
    contractMilestonesMap,
    id,
    isPerson,
    openAttestationModal,
    requestedMilestoneAction,
    requestedMilestoneId
  ]);

  // Opened from a link only until the attestation is closed.
  useEffect(() => {
    if (!showAttestModal) setAttestOpenedFromLink(false);
  }, [showAttestModal]);

  const handleAttest = async (
    action: 'approved' | 'rejected',
    bankidVerificationRef?: string,
    message?: string,
    milestoneStartDecisions?: MilestoneStartDecision[],
    files?: File[]
  ) => {
    if (!attestMilestone) return;
    // Look up the contract this milestone belongs to (the user can attest from
    // the timeline tab where no contract is "selected" in the contracts tab).
    const contractId = attestMilestone.contractId;
    const contractForMilestone = contracts.find(c => c.id === contractId) || selectedContract;
    if (!contractForMilestone) return;

    const milestoneId = attestMilestone.id;
    let outcome: Awaited<ReturnType<typeof milestoneApi.attestMilestone>> | undefined;
    try {
      outcome = await milestoneApi.attestMilestone(milestoneId, {
        action,
        bankidVerificationRef,
        message,
        milestoneStartDecisions,
        files
      });
    } catch (error) {
      // A request that timed out, or a repeated attempt the API refuses, may
      // already be saved: check before reporting a failure, so the user never
      // retries an objection that is registered (ClickUp 869fgx15f).
      if (!(await attestationIsRegistered(contractForMilestone.id, milestoneId, action))) throw error;
    }

    // The decision is saved: the modal can say so right away. The page is
    // refreshed in the background, so a slow reload never keeps it spinning.
    void refreshAfterAttestation(contractForMilestone.id);
    return outcome;
  };

  /** True when the milestone already carries this side's decision (checked within 10 s). */
  const attestationIsRegistered = async (contractId: string, milestoneId: string, action: 'approved' | 'rejected') => {
    try {
      const current = await Promise.race([
        milestoneApi.getMilestones(contractId),
        new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('timeout')), 10000))
      ]);
      return attestationRegistered(current.find(candidate => candidate.id === milestoneId), isPerson ? 'person' : 'company', action);
    } catch {
      return false;
    }
  };

  const refreshAfterAttestation = async (contractId: string) => {
    try {
      const updated = await milestoneApi.getMilestones(contractId);
      if (selectedContract && selectedContract.id === contractId) {
        setMilestones(updated);
      }
      setContractMilestonesMap(prev => ({ ...prev, [contractId]: updated }));
      setFundingRefreshToken(token => token + 1);
      await loadInactivityCases();
      if (id) {
        loadProjectStatusSteps(id);
        const projectsData = isPerson ? await personApi.getProjects() : await projectApi.getProjects();
        const foundProject = projectsData.find((candidate: Project) => candidate.id === id);
        if (foundProject) setProject(foundProject);
        projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to refresh after attestation:', error);
    }
  };

  const [checkingPayoutId, setCheckingPayoutId] = useState<string | null>(null);
  const handleCheckMilestonePayout = async (milestone: Milestone) => {
    if (checkingPayoutId) return;
    setCheckingPayoutId(milestone.id);
    try {
      await contractConfigApi.releaseMilestonePayout(milestone.id);
      const updated = await milestoneApi.getMilestones(milestone.contractId);
      setContractMilestonesMap(previous => ({ ...previous, [milestone.contractId]: updated }));
      if (selectedContract?.id === milestone.contractId) setMilestones(updated);
      setFundingRefreshToken(token => token + 1);
      setMessage({ type: 'success', text: t('milestoneAttestation.payoutChecked') });
    } catch (error) {
      setMessage({ type: 'warning', text: apiErrorText(t, error, 'milestoneAttestation.savedPayoutBlocked') });
    } finally {
      setCheckingPayoutId(null);
    }
  };

  // "Markera som startad" in the timelines: shared with the start pages.
  const { timelineStartProps, startModal } = useMilestoneStarts({
    side: isPerson ? 'person' : 'company',
    contracts,
    milestonesByContract: contractMilestonesMap,
    onSaved: (contract, updated) => {
      setContractMilestonesMap(previous => ({ ...previous, [contract.id]: updated }));
      if (selectedContract?.id === contract.id) setMilestones(updated);
      setMessage({ type: 'success', text: t('milestoneStarts.saveSuccess') });
      setFundingRefreshToken(value => value + 1);
      if (id) loadProjectStatusSteps(id);
    }
  });

  const handleStartMilestoneConflict = async (milestone: Milestone) => {
    const contractForMilestone = contracts.find(contract => contract.id === milestone.contractId);
    if (!contractForMilestone) return;
    const refreshMilestone = async () => {
      const updated = await milestoneApi.getMilestones(contractForMilestone.id);
      if (selectedContract?.id === contractForMilestone.id) {
        setMilestones(updated);
      }
      setContractMilestonesMap(prev => ({ ...prev, [contractForMilestone.id]: updated }));
      const refreshedMilestone = updated.find(candidate => candidate.id === milestone.id);
      if (refreshedMilestone) {
        setAttestMilestone(current => current?.id === milestone.id ? refreshedMilestone : current);
      }
    };
    try {
      await milestoneApi.startConflict(milestone.id);
    } catch (error) {
      // It may have started anyway (e.g. after a timeout): show what the
      // server has, so a started conflict is not offered again (ClickUp 869fgxq45).
      if (isRequestTimeout(error)) void refreshMilestone().catch(() => undefined);
      throw error;
    }
    // The reloads run in the background; the conflict has started.
    void (async () => {
      await refreshMilestone();
      setFundingRefreshToken(token => token + 1);
      await loadInactivityCases();
      if (id) {
        loadProjectStatusSteps(id);
        const projectsData = isPerson ? await personApi.getProjects() : await projectApi.getProjects();
        const foundProject = projectsData.find((candidate: Project) => candidate.id === id);
        if (foundProject) setProject(foundProject);
        projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => {});
      }
    })().catch(error => console.error('Failed to refresh after starting the conflict:', error));
  };

  const handleInactivityResolved = async (milestone: Milestone) => {
    const contractForMilestone = contracts.find(
      contract => contract.id === milestone.contractId
    );
    if (contractForMilestone) {
      const updated = await milestoneApi.getMilestones(contractForMilestone.id);
      if (selectedContract?.id === contractForMilestone.id) {
        setMilestones(updated);
      }
      setContractMilestonesMap(previous => ({
        ...previous,
        [contractForMilestone.id]: updated
      }));
    }
    setFundingRefreshToken(token => token + 1);
    await loadInactivityCases();
    if (id) {
      loadProjectStatusSteps(id);
      const projectsData = isPerson
        ? await personApi.getProjects()
        : await projectApi.getProjects();
      const foundProject = projectsData.find(
        (candidate: Project) => candidate.id === id
      );
      if (foundProject) setProject(foundProject);
      projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => {});
    }
  };

  const handleStartMilestoneDispute = async (
    milestone: Milestone,
    verificationRef: string
  ) => {
    const contractForMilestone = contracts.find(contract => contract.id === milestone.contractId);
    if (!contractForMilestone) return;
    await milestoneApi.startDispute(milestone.id, verificationRef);
    const updated = await milestoneApi.getMilestones(contractForMilestone.id);
    if (selectedContract?.id === contractForMilestone.id) {
      setMilestones(updated);
    }
    setContractMilestonesMap(prev => ({ ...prev, [contractForMilestone.id]: updated }));
    const refreshedMilestone = updated.find(candidate => candidate.id === milestone.id);
    if (refreshedMilestone) {
      setAttestMilestone(current => current?.id === milestone.id ? refreshedMilestone : current);
    }
    setFundingRefreshToken(token => token + 1);
    if (id) {
      loadProjectStatusSteps(id);
      const projectsData = isPerson ? await personApi.getProjects() : await projectApi.getProjects();
      const foundProject = projectsData.find((candidate: Project) => candidate.id === id);
      if (foundProject) setProject(foundProject);
      projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => {});
    }
  };

  const handleDownloadDisputeExport = async (milestone: Milestone) => {
    await milestoneApi.downloadDisputeExport(milestone.id);
  };

  const handleDownloadInactivityFinalReport = async (lifecycleCase: InactivityCase) => {
    setIsDownloadingFinalReport(true);
    try {
      await inactivityApi.downloadFinalReport(lifecycleCase.id);
    } catch (error) {
      console.error('Failed to download the final settlement report:', error);
      setMessage({
        type: 'error',
        text: apiErrorText(t, error, 'inactivityCase.finalReportError')
      });
    } finally {
      setIsDownloadingFinalReport(false);
    }
  };

  const handleOpenConflictMarketplace = () => {
    setShowAttestModal(false);
    selectTab('marketplace');
  };

  const handleOpenProjectDocuments = () => {
    setShowAttestModal(false);
    selectTab('documents');
  };

  const openStageVariation = async (contractId: string, reason = '', milestone?: Milestone, reference?: MilestoneMessage) => {
    if (!id || isPerson) return false;
    const [freshContracts, , freshMessages] = await Promise.all([
      contractApi.getContracts(id),
      Promise.resolve([] as Milestone[]),
      reference && milestone ? milestoneApi.getMessages(milestone.id) : Promise.resolve([])
    ]);
    let parent = freshContracts.find(candidate => candidate.id === contractId);
    if (!milestone && parent) {
      if (parent.settlementType === 'price_adjustment' && parent.parentContractId) parent = freshContracts.find(c => c.id === parent!.parentContractId);
      const visited = new Set<string>();
      while (parent?.supersededByContractId && !visited.has(parent.id)) {
        visited.add(parent.id);
        const successorId = parent.supersededByContractId;
        parent = freshContracts.find(c => c.id === successorId);
      }
    }
    const freshMilestones = parent ? await milestoneApi.getMilestones(parent.id) : [];
    const current = milestone ? freshMilestones.find(candidate => candidate.id === milestone.id) : undefined;
    if (!parent || parent.projectId !== id || (parent.versionStatus ?? 'current') !== 'current' ||
      parent.draftStatus !== 'signed_by_both' || (milestone && (!current ||
      (!['disputed', 'conflict', 'under_resolution'].includes(current.deliveryStatus ?? '') && !current.conflictStartedAt) ||
      ['paid', 'external_dispute'].includes(current.deliveryStatus ?? '')))) {
      throw new Error('The objection is no longer open for an ÄTA.');
    }
    // One open ÄTA per contract (the API answers 409; ClickUp 869fkk2jh). One
    // still being drafted or signed is opened below, with its signing status
    // and withdrawal; a signed one waiting for deposit or activation blocks.
    const block = variationBlock(parent, freshContracts, contractReplacements);
    if (block && block.reason !== 'signing') {
      setContracts(freshContracts);
      throw new VariationBlockedError(variationBlockText(block, freshContracts, t));
    }
    const verifiedReference = reference ? freshMessages.find(candidate =>
      candidate.id === reference.id && candidate.userRole === 'person' && !candidate.attestationAction
    ) : undefined;
    if (reference && !verifiedReference) throw new Error('Reference is no longer available.');
    const effectiveParentId = parent.id;
    const existing = freshContracts.find(candidate => candidate.stageVariation?.originalContractId === effectiveParentId &&
      candidate.versionStatus === 'proposed');
    if (existing) {
      setContracts(freshContracts);
      if (!await openVariationWizardForEdit(existing, 0, current?.id)) throw new Error('Could not open the existing amendment.');
    } else {
      setContracts(freshContracts);
      void loadContractWizardCompany();
      setStageVariationDraft({ contract: parent, stages: freshMilestones, entryStageId: current?.id, initialReason: reason, reference: verifiedReference });
    }
    setShowCreateContractTypePrompt(false);
    setShowConflictStagePicker(false);
    void loadNegativeVariationCapacity(parent.id);
    setShowAttestModal(false);
    setAttestMilestone(null);
    selectTab('contract');
    handleSelectContract(parent);
    navigate(`?tab=contract&contractId=${encodeURIComponent(parent.id)}`);
    return true;
  };

  const handleProposeConflictVariation = (milestone: Milestone, reason: string, reference?: MilestoneMessage) =>
    openStageVariation(milestone.contractId, reason, milestone, reference);

  const openVariationWizardForEdit = async (
    contract: Contract,
    initialStep = 0,
    entryStageId?: string,
    view?: { viewOnly: boolean; canSign: boolean }
  ) => {
    try {
      const fresh = await contractApi.getContract(contract.id);
      const settlement = await contractApi.getReplacement(fresh.id);
      const parentId = settlement?.amendmentScope ? settlement.originalContractId : fresh.parentContractId;
      if (!parentId) return false;
      const [parent, stages, reference, conflict] = await Promise.all([contractApi.getContract(parentId), milestoneApi.getMilestones(parentId), contractApi.getCounterproposalReference(fresh.id), contractApi.getConflictVariationScope(fresh.id)]);
      setStageVariationDraft({ contract: parent, stages, initialReason: '', existing: fresh, settlement: settlement ?? undefined, initialStep,
        reference: reference ?? undefined, entryStageId: entryStageId ?? settlement?.disputedMilestoneId ?? conflict?.milestoneId, ...view });
      void loadContractWizardCompany();
      return true;
    } catch { setMessage({ type: 'error', text: t('variationWizard.loadError') }); return false; }
  };

  useEffect(() => {
    if (!id || !selectedContract || isPerson) return;
    const returning = sessionStorage.getItem(variationReturnKey(id));
    if (returning === selectedContract.id && (selectedContract.signingSentAt || selectedContract.companySigned || selectedContract.draftStatus === 'draft_sent')) {
      sessionStorage.removeItem(variationReturnKey(id));
      setVariationAgreementId(selectedContract.id);
    }
  }, [id, selectedContract, isPerson]);

  const applySavedStageVariation = async (settlement: ContractReplacement) => {
    const contractId = settlement.replacementContractId;
    setContractReplacements(previous => ({
      ...previous,
      [settlement.originalContractId]: settlement,
      [contractId]: settlement
    }));
    // Autosave already returns the persisted plan and totals. Do not reload
    // other form fields, which may have their own unsaved edits.
    if (settlement.contract) {
      const { contractAmount, rotAmount, afterRotAmount, stageVariation } = settlement.contract;
      const totals = { contractAmount, rotAmount, afterRotAmount };
      setContracts(previous => previous.map(contract =>
        contract.id === contractId ? { ...contract, ...totals, stageVariation } : contract
      ));
      setSelectedContract(previous => previous?.id === contractId
        ? { ...previous, ...totals, stageVariation } : previous
      );
      if (selectedContractRef.current?.id === contractId) {
        setContractFormData(previous => ({ ...previous, ...totals }));
      }
    }
    if (settlement.milestones) {
      const plan = settlement.milestones;
      setContractMilestonesMap(previous => ({ ...previous, [contractId]: plan }));
      if (selectedContractRef.current?.id === contractId) setMilestones(plan);
    }
  };

  const refreshReplacementProposal = async (settlement: ContractReplacement) => {
    setContractReplacements(previous => ({
      ...previous,
      [settlement.originalContractId]: settlement,
      [settlement.replacementContractId]: settlement
    }));
    await reloadSelectedContract();
    if (selectedContractRef.current?.id === settlement.replacementContractId) {
      await loadMilestones(settlement.replacementContractId);
    }
    if (attestMilestone?.id === settlement.disputedMilestoneId) {
      setMilestoneMessages(await milestoneApi.getMessages(attestMilestone.id));
    }
  };



  const handleReplacementProposalResponse = async (
    settlement: ContractReplacement,
    action: 'approve' | 'reject'
  ) => {
    setIsUpdatingReplacementProposal(true);
    try {
      const updated = await contractApi.respondToReplacementProposal(
        settlement.replacementContractId,
        { action }
      );
      await refreshReplacementProposal(updated);
      setMessage({
        type: 'success',
        text: t(action === 'approve'
          ? 'projectView.replacement.proposalApproved'
          : 'projectView.replacement.proposalRejected')
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: apiErrorText(t, error, 'projectView.replacement.proposalDecisionError')
      });
    } finally {
      setIsUpdatingReplacementProposal(false);
    }
  };

  const handleWithdrawReplacementProposal = async (
    settlement: ContractReplacement
  ) => {
    setIsUpdatingReplacementProposal(true);
    try {
      const updated = await contractApi.withdrawReplacementProposal(
        settlement.replacementContractId
      );
      await refreshReplacementProposal(updated);
      setMessage({
        type: 'success',
        text: t('projectView.replacement.proposalWithdrawn')
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: apiErrorText(t, error, 'projectView.replacement.proposalWithdrawError')
      });
    } finally {
      setIsUpdatingReplacementProposal(false);
    }
  };

  const confirmReplacementProposalRejection = (
    settlement: ContractReplacement
  ) => {
    setConfirmDialog({
      title: 'Avvisa ersättningsförslaget?',
      message:
        'Förslaget avslutas och det befintliga avtalet fortsätter att gälla. Händelsen sparas i historiken.',
      confirmLabel: 'Avvisa förslaget',
      confirmVariant: 'danger',
      icon: 'x-circle',
      onConfirm: () => handleReplacementProposalResponse(settlement, 'reject')
    });
  };

  const confirmReplacementProposalWithdrawal = (
    settlement: ContractReplacement
  ) => {
    setConfirmDialog({
      title: 'Återkalla ersättningsförslaget?',
      message:
        'Hantverkaren meddelas och förslaget sparas som återkallat i historiken.',
      confirmLabel: 'Återkalla förslaget',
      confirmVariant: 'danger',
      icon: 'rotate-ccw',
      onConfirm: () => handleWithdrawReplacementProposal(settlement)
    });
  };

  const progressMilestoneResolution = async (
    milestone: Milestone,
    action: 'start_dispute_resolution' | 'resubmit_for_attestation',
    comment?: string
  ): Promise<Milestone | null | false> => {
    const contractForMilestone = contracts.find(
      contract => contract.id === milestone.contractId
    );
    if (!contractForMilestone) return false;

    if (action === 'start_dispute_resolution' &&
      !await confirmOverlap('remediation', contractForMilestone.id, milestone.id)) return false;

    await milestoneApi.progressMilestone(milestone.id, action, comment);
    const updated = await milestoneApi.getMilestones(contractForMilestone.id);
    if (selectedContract?.id === contractForMilestone.id) {
      setMilestones(updated);
    }
    setContractMilestonesMap(prev => ({ ...prev, [contractForMilestone.id]: updated }));
    const refreshedMilestone = updated.find(candidate => candidate.id === milestone.id);
    if (refreshedMilestone && attestMilestone?.id === milestone.id) {
      setAttestMilestone(refreshedMilestone);
      setMilestoneMessages(await milestoneApi.getMessages(milestone.id));
    }
    setFundingRefreshToken(token => token + 1);
    return refreshedMilestone ?? null;
  };

  const handleProgressMilestoneResolution = async (
    action: 'start_dispute_resolution' | 'resubmit_for_attestation',
    comment?: string
  ) => {
    if (!attestMilestone) return false;
    const result = await progressMilestoneResolution(attestMilestone, action, comment);
    if (result === false) return false;
  };

  const handleOpenRemediedMilestoneAttestation = async (milestone: Milestone) => {
    setProgressingMilestoneId(milestone.id);
    try {
      const refreshedMilestone = await progressMilestoneResolution(
        milestone,
        'resubmit_for_attestation'
      );
      if (refreshedMilestone) {
        await openAttestationModal(refreshedMilestone);
      }
    } catch {
      setMessage({
        type: 'error',
        text: t('milestoneAttestation.progressStatusUpdateFailed')
      });
    } finally {
      setProgressingMilestoneId(null);
    }
  };
  openRemediedAttestationRef.current = handleOpenRemediedMilestoneAttestation;

  const handleStatusChange = async (message: string) => {
    if (!id || !showStatusChangeModal) return;
    if (showStatusChangeModal === 'resume') {
      await projectStatusApi.resumeProject(id, message);
    } else {
      await projectStatusApi.setExceptionStatus(id, showStatusChangeModal, message);
    }
    setShowStatusChangeModal(null);
    // The project is refreshed in the background (ClickUp 869fgxq45).
    void (async () => {
      const projectsData = isPerson ? await personApi.getProjects() : await projectApi.getProjects();
      const foundProject = projectsData.find((p: any) => p.id === id);
      if (foundProject) setProject(foundProject);
    })().catch(error => console.error('Failed to refresh the project:', error));
    loadProjectStatusSteps(id);
    projectStatusApi.getStatusMessages(id).then(setStatusMessages).catch(() => {});
  };

  const handlePostMilestoneMessage = async (message: string, options: { context?: 'attestation_question' | 'attestation_request'; files?: File[] } = {}) => {
    if (!attestMilestone) return;
    const contractForMilestone = contracts.find(
      contract => contract.id === attestMilestone.contractId
    );
    try {
      await milestoneApi.postMessage(attestMilestone.id, message, options);
    } catch (error) {
      // A reminder sent too soon: show the stage's pending reminder (ClickUp 869fmfkyz).
      if (apiErrorCode(error) === 'ATTESTATION_REQUEST_TOO_SOON') refreshStageThreads();
      throw error;
    }
    refreshStageThreads();
    const [msgs, updatedMilestones] = await Promise.all([
      milestoneApi.getMessages(attestMilestone.id),
      contractForMilestone
        ? milestoneApi.getMilestones(contractForMilestone.id)
        : Promise.resolve(null)
    ]);
    setMilestoneMessages(msgs);
    if (contractForMilestone && updatedMilestones) {
      if (selectedContract?.id === contractForMilestone.id) {
        setMilestones(updatedMilestones);
      }
      setContractMilestonesMap(previous => ({
        ...previous,
        [contractForMilestone.id]: updatedMilestones
      }));
      const refreshedMilestone = updatedMilestones.find(
        candidate => candidate.id === attestMilestone.id
      );
      if (refreshedMilestone) setAttestMilestone(refreshedMilestone);
    }
    await loadInactivityCases();
  };

  const handleContractSort = (field: ContractSortField) => {
    if (contractSortField === field) {
      setContractSortDirection(contractSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setContractSortField(field);
      setContractSortDirection('asc');
    }
  };

  const formatNumber = (value: number): string => {
    return (value ?? 0).toLocaleString('sv-SE');
  };

  const listFunding = useCallback((contract: Contract) => contractListFunding(
    contract,
    projectFunding?.contracts.find(item => item.contractId === contract.id),
    contract.versionStatus === 'proposed' && contractReplacements[contract.id]?.status === 'draft'
      ? contractReplacements[contract.id] : null
  ), [projectFunding, contractReplacements]);

  // Filter and sort contracts
  const filteredAndSortedContracts = useMemo(() => {
    let result = [...contracts];

    // Apply filters
    Object.entries(contractFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
        result = result.filter(contract => {
          const value = String((contract as any)[field] || '').toLowerCase();
          return value.includes(filterValue.toLowerCase());
        });
      }
    });

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = (a as any)[contractSortField];
      let bVal: any = (b as any)[contractSortField];
      if (contractSortField === 'contractAmount') {
        aVal = a.stageVariation ? a.stageVariation.grossDeltaOre / 100 : Number(a.contractAmount);
        bVal = b.stageVariation ? b.stageVariation.grossDeltaOre / 100 : Number(b.contractAmount);
      }
      if (contractSortField === 'paidAmount' || contractSortField === 'remainingAmount') {
        aVal = listFunding(a)[contractSortField];
        bVal = listFunding(b)[contractSortField];
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return contractSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (aVal < bVal) return contractSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return contractSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [contracts, contractFilters, contractSortField, contractSortDirection, listFunding]);

  // Reduce each contract to the shared ContractList row view-model. Keeps the
  // company-specific derivations (replacement "prel." values, version chain,
  // "Ersätter kontrakt …" line) here so the shared list component stays
  // presentational and identical for the provider (jurist/besiktningsman) views.
  const contractListRows: ContractListRow[] = useMemo(
    () =>
      filteredAndSortedContracts.map((contract) => {
        const pendingReplacement =
          contract.versionStatus === 'proposed' &&
          contractReplacements[contract.id]?.status === 'draft'
            ? contractReplacements[contract.id]
            : null;
        const original = contract.replacesContractId
          ? contracts.find((candidate) => candidate.id === contract.replacesContractId)
          : undefined;
        const typeLabel = agreementVariationNumber(contract) !== null
          ? agreementLabel(contract, contracts, t)
          : t('contracts.types.fixed');
        const funding = listFunding(contract);
        return {
          id: contract.id,
          contractNumber: contract.contractNumber,
          numberLabel: contract.stageVariation ? String(agreementVariationNumber(contract)) : undefined,
          typeLabel,
          isVariation: !!contract.stageVariation || contract.contractType === 'variation',
          // The agreement name; an old contract without one gets its number (869fmwg63).
          description: contract.contractName || t('contracts.list.contractName', { number: contract.contractNumber }),
          replacesLabel: contract.stageVariation
            ? agreementBasis(contract, contracts, t)
            : original
              ? `Ersätter kontrakt #${original.contractNumber}, avtalsversion ${original.contractVersion ?? 1} – ${contractTitle(original) || '—'}`
              : null,
          etappCount: contractReplacements[contract.id]?.amendmentScope && contractReplacements[contract.id].replacementContractId === contract.id
            ? stageVariationMilestones(contractReplacements[contract.id], contractMilestonesMap[contract.id] || []).length
            : (contractMilestonesMap[contract.id] || []).length,
          amountLabel: formatNumber(
            contract.stageVariation ? contract.stageVariation.grossDeltaOre / 100 : parseFloat(contract.contractAmount?.replace(/[^0-9.-]/g, '') || '0')
          ),
          amountAfterRotLabel: formatNumber(contract.stageVariation
            ? contract.stageVariation.afterRotDeltaOre / 100 : Number(contract.afterRotAmount)),
          totalAfterVariationLabel: contract.stageVariation ? formatNumber(Number(contract.afterRotAmount)) : undefined,
          paidLabel: formatNumber(funding.paidAmount),
          paidTitle: pendingReplacement
            ? t('projectView.replacement.carryoverTitle')
            : undefined,
          paidPreliminary: !!pendingReplacement,
          remainingLabel: formatNumber(funding.remainingAmount),
          remainingTitle: pendingReplacement
            ? t('projectView.replacement.additionalNeedTitle')
            : undefined,
          remainingPreliminary: !!pendingReplacement,
          versionBadge: contractVersionBadge(contract),
          draftBadge: draftStatusBadge(contract.draftStatus, t),
          danger: contract.draftStatus === 'draft_rejected',
          deletable: isDeletableDraft(contract),
          signed: contract.companySigned && contract.personSigned,
          canCreateVariation: !isPerson && contract.companySigned && contract.personSigned,
          variationBlock: !isPerson && contract.companySigned && contract.personSigned
            ? (() => {
                const block = variationBlock(contract, contracts, contractReplacements);
                return block ? { text: variationBlockText(block, contracts, t), agreementId: block.contract.id } : undefined;
              })()
            : undefined,
          hasAgreement: !!contract.stageVariation || contract.contractType === 'variation',
          // An ÄTA is listed under the contract it changes (ClickUp 869fa5qf8).
          parentId: contract.stageVariation ? agreementRoot(contract, contracts).id : undefined,
          // The Beställare gets only their own choices (ClickUp 869fkju30):
          // review when the contract waits on them, never edit or stages.
          customerView: isPerson,
          review: isPerson && (canSignContract(contract, 'person') || needsPersonDraftReview(contract))
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredAndSortedContracts, contracts, contractReplacements, contractMilestonesMap, listFunding]
  );

  // Calculate total contract amount from all contracts
  const totalContractAmount = useMemo(() => {
    return effectiveContractVersions(contracts).reduce((sum, contract) => {
      const amount = parseFloat(contract.contractAmount?.replace(/[^0-9.-]/g, '') || '0');
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  }, [contracts]);

  // An ÄTA the buyer reviews that belongs to an open objection: signing it
  // closes the objection, which the review says (ClickUp 869fgj7uc).
  const reviewedVariation = isPerson && showPreview && selectedContract ? [selectedContract] : [];
  const reviewObjection = useOngoingObjections(reviewedVariation)[selectedContract?.id ?? ''] ?? null;

  const currentProjectContracts = useMemo(() => timelineContracts(contracts), [contracts]);
  const currentProjectMilestones = useMemo(
    () => timelineMilestones(currentProjectContracts, Object.values(contractMilestonesMap).flat()),
    [currentProjectContracts, contractMilestonesMap],
  );
  const displayProjectMilestones = payoutPresentationMilestones(currentProjectMilestones, projectFunding?.milestones);
  const projectDisplayStatus = project
    ? projectWorkflowStatus(
        project.status,
        displayProjectMilestones,
        project.workflowStatus,
      )
    : 'not_started';
  const conflictActionMilestones = currentProjectMilestones.filter(stage =>
    currentProjectContracts.some(contract => contract.id === stage.contractId && contract.draftStatus === 'signed_by_both') &&
    ['in_progress', 'ready_for_attestation', 'ready_for_reattestation', 'seller_attested', 'disputed', 'conflict', 'under_resolution', 'external_dispute'].includes(stage.deliveryStatus ?? '') &&
    !(stage.companyAttestation === 'approved' && stage.personAttestation === 'approved')
  );

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  const selectedCustomer = customers.find(c => c.id === formData.customerId);

  const getStatusLabel = (status: string): string => {
    const option = statusOptions.find(o => o.value === status);
    if (option) return option.label.startsWith('projects.') ? t(option.label) : option.label;
    return getStatusLabelUtil(status, projectStatusSteps);
  };

  const getAttestationBadge = (status: AttestationStatus) => {
    switch (status) {
      case 'approved':
        return <span className="badge bg-success">{t('contracts.attestation.approved')}</span>;
      case 'rejected':
        return <span className="badge bg-danger">{t('contracts.attestation.rejected')}</span>;
      default:
        return <span className="badge bg-warning">{t('contracts.attestation.notAttested')}</span>;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout supportContext={{ projectId: id, contractId: selectedContract?.id }}>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout supportContext={{ projectId: id, contractId: selectedContract?.id }}>
        <div className="text-center py-5">
          <h4>{t('projectView.notFound')}</h4>
          <Link to={basePath} className="btn btn-primary mt-3">
            {t('common.back')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  // The contract tab as a status view (ClickUp 869fa5qf8). The old sections
  // stay for contracts the wizard cannot open, which have no other way to be
  // edited by the company (ÄTA, manually signed).
  const legacyContractSections = FEATURE_CONTRACT_VIEW_LEGACY_SECTIONS || !FEATURE_CONTRACT_WIZARD_V2 ||
    (!isPerson && !!selectedContract && !wizardEditStatus(selectedContract));
  const overviewState = selectedContract ? contractViewState(selectedContract) : null;
  // Delete, ÄTA and the signed document live in the actions row under the
  // contract list; the status card keeps the one way to change the contract,
  // "Redigera" (ClickUp 869fmxfb7): the wizard, the ÄTA wizard or the
  // contract page, as the row's double-click.
  const overviewActions = selectedContract && overviewState ? [
    overviewState !== 'signed' && !isPerson && (
      <button key="wizard" type="button" className="btn btn-sm contract-list-action" data-testid="contract-overview-open-wizard"
        onClick={() => handleContractRowAction(selectedContract.id, 'edit')}>
        <i className="feather-edit-2" aria-hidden="true"></i>{t('contracts.overview.actions.edit')}
      </button>
    ),
  ].filter(Boolean) : [];
  // The viewer's own signature is done: no signing buttons, only greyed out
  // ones would remain.
  const viewerHasSigned = isPerson ? Boolean(selectedContract?.personSigned) : isCompanySigned;
  const showSigningActions = !isSignedContract && !isProjectClosedForSigning && !viewerHasSigned;
  const overviewCustomerName = customers.find(c => c.id === project.customerId)?.name ?? '';
  const overviewCompanyName = projectCompanyNameOrEmpty(projectCompany?.companyName ?? project.companyName);

  const hasSignedContract = contracts.some(
    contract =>
      contract.draftStatus === 'signed_by_both' ||
      (contract.companySigned && contract.personSigned)
  );
  const shouldNagCompanyAboutAdyen =
    showAdyenOnboardingPrompt &&
    !isPerson &&
    !user?.isAdmin &&
    hasSignedContract &&
    !adyenAccountAllowsPayments(adyenAccount);
  const completedSilenceCase = inactivityCases.find(
    lifecycleCase =>
      lifecycleCase.status === 'completed' &&
      ['seller_silence', 'buyer_silence'].includes(lifecycleCase.caseType)
  );

  const headerCounterpart = isPerson
    ? projectCompanyNameOrEmpty(projectCompany?.companyName ?? project.companyName)
    : customers.find(customer => customer.id === project.customerId)?.name || project.customerName;
  // No Beställare yet, or waiting for the invited one's BankID (ClickUp 869ff1egy).
  const customerState = projectCustomerState(project);
  const customerSigningReason = isPerson ? null : projectCustomerSigningReason(project, t);
  // The company's buttons for the chosen contract sit in the actions row under
  // the contract list (ClickUp 869fmxfb7); the summary keeps the text.
  // "Förhandsgranska" for every contract not yet signed by both, for both
  // parties (869fmxfb7); a signed one opens with a double-click or "Signerat
  // avtal". The Beställare signs with "Granska och signera" in the same row.
  const showListPreview = !!selectedContract && !isSignedContract && (!isPerson || overviewState !== 'draft');
  const listActions = selectedContract && (overviewActions.length > 0 || showListPreview || (!isPerson && showSigningActions)) ? (
    <>
      {overviewActions}
      {showListPreview && (
        <button type="button" className="btn btn-sm contract-list-action" data-testid="company-contract-preview"
          disabled={isStageVariation && stageVariationDirty} onClick={() => handleOpenPreview('preview')}>
          <i className="feather-eye" aria-hidden="true"></i>{t('contracts.preview.button')}
        </button>
      )}
      {!isPerson && showSigningActions && (
        <ContractSigningButtons
          signApproveLabel={t('contracts.actions.signApprove')}
          isSignDisabled={isCompanySigned || isBuyerProposalAwaitingSupplier || (isStageVariation && stageVariationDirty) || Boolean(customerSigningReason)}
          isOpeningSigning={isOpeningSigning}
          openingSigningLabel={t('contracts.actions.openingSigning')}
          onCompanySign={() => { void handleOpenPreview('sign'); }}
        />
      )}
    </>
  ) : undefined;
  const showCustomerPanel = !isPerson && customerState === 'missing';
  const openAddCustomer = (tab: 'invite' | 'pick' = 'invite') => setAddCustomerTab(user?.isAdmin ? 'pick' : tab);

  const showEconomicsSummary = !isPerson && Boolean(projectContract || projectEconomics);
  const hasHeldFunds = heldFundsSummary.items.length > 0;
  const showTransactionSummaryColumn = showEconomicsSummary || hasHeldFunds;
  const pingPayoutPanel = (projectFunding?.paymentProvider === 'ping' || Boolean(projectFunding?.pingPayouts?.length))
    ? <><PingPayoutPanel projectId={project.id} refreshToken={fundingRefreshToken} canUseDebugBank={isPerson || Boolean(user?.isAdmin)} embedded={activeTab === 'transactions'} onPayoutsChanged={setPingPayouts} /><PingRefundPanel projectId={project.id} refreshToken={fundingRefreshToken} canSimulate={isPerson} /></>
    : null;
  // The person's "Granska och signera avtal" (PersonContractReview): the same
  // document as the wizard, built from the stored contract. Signing and the
  // objection use the contract page's existing handlers.
  // The selected contract as a document, from what the contract page has loaded:
  // the Beställare's review and the Hantverkare's read-only preview.
  const storedContractPreview = (
    contract: Contract,
    viewer: 'person' | 'company',
    company: { name: string; organizationNumber?: string },
    customer: { name?: string; email?: string },
  ) => ({
    ...previewFromContract({
      contract,
      contracts,
      milestones,
      descriptions: { contractName, assignmentDescription: `${variationReason ? `<p><strong>${t('variationWizard.reason')}: ${t(`variationWizard.reasons.${variationReason}`)}</strong></p>` : ''}${assignmentDescription}`, otherDescription: [otherDescription, variationOtherDescription].filter(Boolean).join('\n'), referToAttachments },
      company,
      customer,
      attachments: contractAttachments,
      replacement: selectedReplacementSettlement,
    }),
    theme: storedPreviewTheme,
    notice: <ConflictVariationNotice contract={contract} viewer={viewer} />,
    pricingOverride: isStageVariation && selectedReplacementSettlement
      ? <VariationChanges settlement={selectedReplacementSettlement} milestones={milestones} />
      : undefined,
    onOpenAttachment: (doc: DocumentRecord) => void attachmentPreview.openPreview(doc),
    onVerifyAttachment: setAttachmentDetailsTarget,
  });
  const personReviewOpen = isPerson && showPreview && !!selectedContract;
  const personReviewPreview = personReviewOpen && selectedContract
    ? storedContractPreview(
        selectedContract,
        'person',
        { name: previewCompanyName, organizationNumber: previewCompanyOrgNr },
        { name: user?.fullName, email: user?.email },
      )
    : null;
  const companyPreviewContract = !isPerson && companyPreviewContractId && selectedContract?.id === companyPreviewContractId
    ? selectedContract
    : null;
  const companyPreviewCustomer = customers.find(c => c.id === project.customerId);
  // What the person pays in: the contract amount, or the increase of a stage variation.
  const personReviewAmountToPay = !personReviewPreview
    ? 0
    : isStageVariation
      ? Math.max(selectedReplacementSettlement?.afterRotDeltaOre ?? 0, 0) / 100
      : Math.max(personReviewPreview.afterRot, 0);
  const personReviewBlockedReason = !selectedContract
    ? null
    : isSignedContract || selectedContract.personSigned
      ? t('contracts.personReview.blocked.signed')
      : isProjectClosedForSigning
        ? t('contracts.actions.projectClosedNotice')
        : isRejectedDraft
          ? t('contracts.personReview.blocked.rejected')
          : !hasExternalSigningProcess
            ? t('contracts.actions.awaitingCompanySigning')
            : null;
  const personReviewMessages: ReviewMessage[] = draftMessages
    .filter(entry => entry.message?.trim())
    .map(entry => ({
      id: entry.id,
      authorName: entry.userName,
      mine: entry.userRole === 'person',
      text: entry.message,
      createdAt: entry.createdAt,
    }));

  const sendPersonObjection = async (comment: string) => {
    if (!selectedContract) return false;
    try {
      await contractApi.respondToDraft(selectedContract.id, { response: 'draft_rejected', comment });
      setMessage({ type: 'success', text: t('contracts.actions.draftResponseSuccess') });
      await reloadSelectedContract();
      try {
        setDraftMessages(await contractApi.getDraftMessages(selectedContract.id));
      } catch {
        /* the thread is reloaded the next time the review opens */
      }
      return true;
    } catch (error) {
      console.error('Failed to send the objection:', error);
      return false;
    }
  };

  return (
    <DashboardLayout supportContext={{ projectId: id, contractId: selectedContract?.id }}>
      {resolutionOverlapDialog}
      {/* Page Header — breadcrumb on its own row ending on the project, the
          project name as heading, then number, counterpart, contract count and
          dates; "Tillbaka" on the right. Styling in App.css (.project-detail-header). */}
      <div className="page-header project-detail-header mb-4">
        <div className="project-detail-header__main">
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('projects.title'), path: '/projects' },
            { label: project.description }
          ]} />
          <h1 className="project-detail-header__name">{project.description}</h1>
          {project.isTest && <span className="badge text-bg-info">{t('testProjects.projectLabel')}</span>}
          <p className="project-detail-header__meta" data-testid="project-detail-header-meta">
            <span>{t('projectView.header.meta', { number: project.projectNumber })}</span>
            {headerCounterpart && (
              <span>
                {t(isPerson ? 'projectView.header.contractor' : 'projectView.header.client')}:{' '}
                <strong>{headerCounterpart}</strong>
              </span>
            )}
            {!isPerson && customerState !== 'linked' && (
              <span>
                {t('projectView.header.client')}:{' '}
                <ProjectCustomerStatusBadge state={customerState} />
              </span>
            )}
            <span>{t('projectView.header.contracts', { count: currentProjectContracts.length })}</span>
            {project.startDate && (
              <span>
                {project.endDate
                  ? t('projectView.header.period', { start: project.startDate, end: project.endDate })
                  : t('projectView.header.start', { date: project.startDate })}
              </span>
            )}
          </p>
        </div>
        <div className="project-detail-header__back">
          {/* Hidden behind a flag: a conflict starts from its stage, not from the header. */}
          {FEATURE_PROJECT_CONFLICT_SHORTCUT && !['completed', 'dispute'].includes(project.status) && conflictActionMilestones.length > 0 && (
            <button type="button" className="btn btn-outline-warning me-2"
              data-testid="project-conflict-actions" onClick={() => setShowConflictStagePicker(true)}>
              <i className="feather-message-circle me-1" aria-hidden="true" />
              {t('conflictVariation.openConflict')}
            </button>
          )}
          <Link to={basePath} className="btn btn-light-brand">
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </Link>
        </div>
      </div>

      {returnFeedback && dismissedReturnFeedback !== returnFeedbackKey && (
        <div className={`alert alert-${returnFeedback.type === 'error' ? 'danger' : returnFeedback.type} alert-dismissible fade show`}
          role="status" data-testid="signing-return-feedback">
          {t(returnFeedback.translationKey)}
          <button type="button" className="btn-close" aria-label={t('common.close')}
            onClick={() => setDismissedReturnFeedback(returnFeedbackKey)} />
        </div>
      )}
      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} alert-dismissible fade show`} role="status">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}
      {FEATURE_PROJECT_CONFLICT_SHORTCUT && showConflictStagePicker && (
        <Modal title={t('conflictVariation.chooseStage')} onClose={() => setShowConflictStagePicker(false)}>
          <div className="d-grid gap-2">
            {conflictActionMilestones.map(stage => (
              <button type="button" className="btn btn-outline-primary text-start" key={stage.id}
                data-testid={`project-conflict-stage-${stage.id}`}
                onClick={() => { setShowConflictStagePicker(false); void openAttestationModal(stage, 'resolution'); }}>
                {t('conflictVariation.stage', { number: stage.milestoneNumber,
                  contract: contracts.find(contract => contract.id === stage.contractId)?.contractNumber })}
                <span className="small d-block">{stage.description}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {shouldNagCompanyAboutAdyen && (
        <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-semibold">
              <i className="feather-alert-triangle me-2"></i>
              {t('projectView.kycBanner.title')}
            </div>
            <div className="small">
              {t('projectView.kycBanner.body')}
            </div>
          </div>
          <Link to="/settings/company?tab=adyen" className="btn btn-sm btn-warning">
            {t('projectView.kycBanner.cta')}
          </Link>
        </div>
      )}

      {user?.isAdmin && <AdminPayoutAlerts key={project.id} projectId={project.id} />}

      <ProjectFundingPanel
        projectId={project.id}
        projectNumber={project.projectNumber}
        refreshToken={fundingRefreshToken}
        customerName={customers.find(customer => customer.id === project.customerId)?.name || project.customerName}
        latestVariation={signedVariations.at(-1) ?? null}
        onShowTransactions={() => selectTab('transactions')}
        onFundingChanged={setProjectFunding}
        onMilestoneProgressed={async contractId => {
          const updated = await milestoneApi.getMilestones(contractId);
          setContractMilestonesMap(prev => ({ ...prev, [contractId]: updated }));
          if (selectedContract?.id === contractId) setMilestones(updated);
        }}
      />

      {activeTab !== 'transactions' && pingPayoutPanel}

      {!isPerson && customerState === 'missing' && (
        <ProjectCustomerReadiness
          hasAssignment={currentProjectContracts.some(item => contractTitle(item) !== '')}
          hasPricing={currentProjectContracts.some(item => Number(item.contractAmount ?? 0) > 0)}
          onAdd={() => openAddCustomer()}
        />
      )}
      {!isPerson && customerState === 'waiting' && (
        <ProjectCustomerWaiting
          project={project}
          onChange={() => void loadData()}
          onReplace={() => openAddCustomer('invite')}
        />
      )}
      {addCustomerTab && !(FEATURE_CONTRACT_WIZARD_V2 && showContractWizard) && (
        <AddProjectCustomerModal
          project={project}
          companyName={project.companyName ?? projectCompany?.companyName}
          initialTab={addCustomerTab}
          isAdmin={Boolean(user?.isAdmin)}
          onClose={() => setAddCustomerTab(null)}
          onDone={() => void loadData()}
        />
      )}

      {/* Tabs Navigation — shared ProjectTabsBar. The tab list is built here so
          role-specific visibility (marketplace) and labels (Ändra vs Översikt)
          stay in the view, never inside the shared component. */}
      <ProjectTabsBar
        activeKey={activeTab}
        onSelect={(k) => selectTab(k as TabType)}
        tabs={[
          { key: 'timeline', label: t('projectView.tabs.timeline'), icon: 'git-branch' },
          { key: 'contract', label: t('projectView.tabs.contract'), icon: 'file-text' },
          { key: 'participants', label: t('projectView.tabs.participants'), icon: 'users' },
          { key: 'documents', label: t('projectView.tabs.documents'), icon: 'folder' },
          { key: 'transactions', label: t('projectView.tabs.transactions', 'Transaktioner'), icon: 'credit-card' },
          ...(canUseProjectMarketplace ? [{ key: 'marketplace', label: t('marketplace.title'), icon: 'shopping-bag' }] : []),
          { key: 'overview', label: isPerson ? t('projectView.tabs.overview') : t('projectView.tabs.edit', 'Ändra'), icon: isPerson ? 'home' : 'edit-3' },
        ] as ProjectTab[]}
      />

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-pane fade show active">
            {isPerson ? (
              /* Person read-only overview */
              <>
                <Card>
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="card-title mb-0"><i className="feather-info" aria-hidden="true"></i>{t('projectView.tabs.overview')}</h5>
                    <ProjectWorkflowStatusBadge
                      project={project}
                      milestones={displayProjectMilestones}
                      steps={projectStatusSteps}
                    />
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('projects.columns.etappioProjectNumber')}</label>
                        <input type="text" className="form-control" value={project.projectNumber} disabled />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('projects.form.totalContractAmount')}</label>
                      <input type="text" className="form-control" value={formatCurrency(totalContractAmount)} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('projects.columns.etappCount', 'Antal etapper')}</label>
                      <input type="text" className="form-control" value={project.etappCount ?? 0} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('projects.columns.description')}</label>
                      <input type="text" className="form-control" value={project.description} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t('projects.columns.status')}</label>
                      <input type="text" className="form-control" value={getStatusLabel(projectDisplayStatus)} disabled />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('projects.columns.startDate')}</label>
                        <input type="text" className="form-control" value={project.startDate || '–'} disabled />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('projects.columns.endDate')}</label>
                        <input type="text" className="form-control" value={project.endDate || '–'} disabled />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Company Information Section */}
                <Card className="mt-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0"><i className="feather-briefcase" aria-hidden="true"></i>{t('personProject.companyInfo')}</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('personProject.companyName')}</label>
                          <input type="text" className="form-control" value={projectCompanyNameOrEmpty(projectCompany?.companyName ?? project.companyName)} disabled />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('personProject.orgNumber')}</label>
                          <input type="text" className="form-control" value={projectCompany?.organizationNumber || '–'} disabled />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t('personProject.vatNumber')}</label>
                        <input type="text" className="form-control" value={projectCompany?.vatNumber || '–'} disabled />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t('personProject.address')}</label>
                        <input type="text" className="form-control" value={projectCompany?.address || '–'} disabled />
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">{t('personProject.postalCode')}</label>
                          <input type="text" className="form-control" value={projectCompany?.postalCode || '–'} disabled />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">{t('personProject.city')}</label>
                          <input type="text" className="form-control" value={projectCompany?.city || '–'} disabled />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">{t('personProject.country')}</label>
                          <input type="text" className="form-control" value={projectCompany?.country || '–'} disabled />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('personProject.phone')}</label>
                          <input type="text" className="form-control" value={projectCompany?.phone || '–'} disabled />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('personProject.email')}</label>
                          <input type="text" className="form-control" value={projectCompany?.email || '–'} disabled />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t('personProject.website')}</label>
                        <input type="text" className="form-control" value={projectCompany?.website || '–'} disabled />
                      </div>
                    </div>
                  </Card>
              </>
            ) : (
            <Card>
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0"><i className="feather-edit-2" aria-hidden="true"></i>{t('projectView.editForm.title', 'Ändra projekt detaljer')}</h5>
                <ProjectWorkflowStatusBadge
                  project={project}
                  milestones={displayProjectMilestones}
                  steps={projectStatusSteps}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="card-body">
                  {/* Internal-only notice: these fields are never shown to the private individual */}
                  <div className="alert alert-info d-flex align-items-start gap-2" role="alert">
                    <i className="feather-eye-off mt-1 flex-shrink-0"></i>
                    <div>{t('projectView.editForm.internalNote', 'Dessa uppgifter visas inte för beställaren — de är endast för er att arbeta med uppföljning och styrning av projektet.')}</div>
                  </div>
                  {/* Two-column grid on tablet/laptop (col-md-6); stacks on mobile */}
                  <div className="row">
                    {/* Etappio project number | Total contract amount */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('projects.columns.etappioProjectNumber')}</label>
                      <input type="text" className="form-control bg-light" value={project.projectNumber} disabled />
                      <small className="text-muted">{t('projects.form.projectNumberHelp')}</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('projects.form.totalContractAmount')}</label>
                      <input type="text" className="form-control bg-light" value={formatCurrency(totalContractAmount)} disabled />
                    </div>

                    {/* Customer | Customer name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('projects.form.customer')} *</label>
                      {!project.customerId ? (
                        // Added from "Lägg till beställare" (ClickUp 869ff1egy).
                        <div className="d-flex flex-wrap align-items-center gap-2" data-testid="project-edit-customer-missing">
                          <ProjectCustomerStatusBadge state={customerState} />
                          {customerState === 'missing' && (
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openAddCustomer()}>
                              {t('projectCustomer.readiness.add')}
                            </button>
                          )}
                        </div>
                      ) : customers.length === 0 ? (
                        <div className="alert alert-warning mb-0">
                          <i className="feather-alert-triangle me-2"></i>
                          {t('projects.form.noCustomers')}
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control"
                            value={selectedCustomer ? `${selectedCustomer.customerNumber} - ${selectedCustomer.name} (${selectedCustomer.email})` : project.customerName || '—'}
                            disabled
                          />
                          {/* Byt beställare, or withdraw the agreement first (ClickUp 869fmxjgt). */}
                          {!isPerson && (
                            <div className="mt-2">
                              <ProjectCustomerChange
                                project={project}
                                onChange={() => void loadData()}
                                onChangeCustomer={() => openAddCustomer('pick')}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {selectedCustomer && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('projects.form.customerName')}</label>
                        <div className="d-flex align-items-center gap-2">
                          <input type="text" className="form-control bg-light" value={selectedCustomer.name} disabled />
                          <a href={`/customers?id=${selectedCustomer.id}`} target="_blank" rel="noopener noreferrer" className="btn btn-light-primary flex-shrink-0" title={t('projects.form.viewCustomer')}>
                            <i className="feather-external-link"></i>
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Number of stages | Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('projects.columns.etappCount', 'Antal etapper')}</label>
                      <input type="text" className="form-control bg-light" value={project.etappCount ?? 0} disabled />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">{t('projects.columns.status')}</label>
                      {user?.isAdmin ? (
                        <select name="status" className="form-select" value={formData.status} onChange={handleInputChange}>
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label.startsWith('projects.') ? t(option.label) : option.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" className="form-control bg-light" value={statusOptions.find(o => o.value === formData.status)?.label || formData.status} disabled />
                      )}
                    </div>

                    {/* Start date | End date */}
                    <div className="col-md-6 mb-3">
                      <FormInput name="startDate" type="date" label={t('projects.columns.startDate')} value={formData.startDate} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <FormInput name="endDate" type="date" label={t('projects.columns.endDate')} value={formData.endDate} onChange={handleInputChange} />
                    </div>
                    <div className="col-12 mb-3">
                      <p className="text-muted small mb-0">{t('projects.form.estimatedDateHelp')}</p>
                    </div>

                    {/* Description — full width */}
                    <div className="col-12 mb-3">
                      <FormInput name="description" label={`${t('projects.columns.description')} *`} value={formData.description} onChange={handleInputChange} error={formErrors.description} maxLength={128} />
                    </div>

                    <div className="col-12 mb-3">
                      {user?.isAdmin ? <div className="form-check">
                        <input id="project-is-test" className="form-check-input" type="checkbox" checked={Boolean(formData.isTest)} disabled={project?.isTestAutomatic} onChange={event => setFormData(previous => ({ ...previous, isTest: event.target.checked }))} />
                        <label htmlFor="project-is-test" className="form-check-label">{t('testProjects.projectLabel')}</label>
                      </div> : <p>{t(project?.isTest ? 'testProjects.test' : 'testProjects.ordinary')}</p>}
                      <p className="text-explain mb-0">{t('testProjects.automaticHelp')}</p>
                    </div>
                    {/* External project number — full width */}
                    <div className="col-12 mb-3">
                      <FormInput name="externalProjectNumber" label={t('projects.form.externalProjectReference')} value={formData.externalProjectNumber} onChange={handleInputChange} maxLength={128} />
                      <small className="text-muted">{t('projects.form.externalProjectNumberHelp')}</small>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label">{t('projects.form.optionalLabel', { label: t('projects.form.notes') })}</label>
                    <ProjectNotesGuidance id="project-edit-notes-guidance" />
                    <ReactQuill theme="snow" value={formData.notes} onChange={handleNotesChange} style={{ height: '150px' }} />
                  </div>
                  <div style={{ height: '45px' }}></div>

                  {/* === Avtalskonfiguration (admin only) === */}
                  {user?.isAdmin && (
                    <>
                      <hr className="my-3" />
                      <h6 className="fw-bold mb-3">
                        <i className="feather-sliders me-2"></i>
                        {t('projectView.contractConfig.title', 'Avtalskonfiguration')}
                      </h6>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('projectView.contractConfig.activeConfig', 'Aktiv konfiguration')}</label>
                          <select
                            className="form-select"
                            value={project.contractConfigId || ''}
                            onChange={async (e) => {
                              const configId = e.target.value || null;
                              await projectApi.updateProject(project.id, { contractConfigId: configId } as any);
                              const updated = await projectApi.getProject(project.id);
                              setProject(updated);
                              // Preview the real next checkout after changing the config.
                              if (configId && totalContractAmount > 0) {
                                contractConfigApi.getProjectEconomics(project.id, selectedPaymentMethod).then(setSimulationResult).catch(() => {});
                              } else {
                                setSimulationResult(null);
                              }
                            }}
                          >
                            <option value="">{t('projectView.contractConfig.useDefault', '— Använd standard (baserat på företagstyp) —')}</option>
                            {contractConfigs.map(c => (
                              <option key={c.id} value={c.id}>{c.name} ({c.customer_type})</option>
                            ))}
                          </select>
                          <small className="text-muted">
                            {t('projectView.contractConfig.overrideHelp', 'Välj en specifik avtalskonfiguration för detta projekt. Lämna tomt för att använda hantverkarens standardkonfiguration.')}
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">{t('projectView.contractConfig.snapshotStatus', 'Avtalsstatus')}</label>
                          {projectContract ? (
                            <div>
                              <span className={`badge bg-${projectContract.status === 'active' ? 'success' : projectContract.status === 'draft' ? 'secondary' : 'primary'} me-2`}>
                                {projectContract.status}
                              </span>
                              {projectContract.snapshot_taken_at && (
                                <small className="text-muted d-block mt-1">
                                  {t('projectView.contractConfig.snapshotAt')}: {new Date(projectContract.snapshot_taken_at).toLocaleDateString(i18n.language)}
                                </small>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="badge bg-secondary">{t('projectView.contractConfig.noSnapshot', 'Inget avtal ännu')}</span>
                              <small className="text-muted d-block mt-1">
                                {t('projectView.contractConfig.snapshotInfo', 'En snapshot skapas automatiskt när båda parter signerar kontraktet.')}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Snapshot economics */}
                      {projectContract && (
                        <div className="bg-light rounded p-3 mb-3">
                          <small className="fw-semibold text-muted d-block mb-2">{t('projectView.contractConfig.economics', 'Ekonomisk sammanfattning')}</small>
                          <div className="row g-3">
                            <div className="col-6 col-md-3">
                              <div className="text-center">
                                <small className="text-muted d-block">{t(projectContract.fee_basis === 'initial_project_amount' ? 'projectView.transactions.initialFeeBasis' : 'projectView.contractConfig.projectValue')}</small>
                                <div className="fw-bold">{new Intl.NumberFormat('sv-SE').format(projectContract.project_value_sek)} kr</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="text-center">
                                <small className="text-muted d-block">{t('projectView.contractConfig.customerFee', 'Beställaravgift')}</small>
                                <div className="fw-bold text-info">{new Intl.NumberFormat('sv-SE').format(projectContract.customer_fee_sek)} kr</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="text-center">
                                <small className="text-muted d-block">{t('projectView.contractConfig.companyFee', 'Plattformsavgift')}</small>
                                <div className="fw-bold text-primary">{new Intl.NumberFormat('sv-SE').format(projectContract.company_fee_sek)} kr</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="text-center">
                                <small className="text-muted d-block">{t('projectView.contractConfig.etappioMargin', 'Etappio marginal')}</small>
                                <div className="fw-bold text-success">{new Intl.NumberFormat('sv-SE').format(projectContract.etappio_margin_sek)} kr ({projectContract.etappio_margin_pct}%)</div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 text-center">
                            <small className="text-muted">
                              {t('projectView.contractConfig.configUsed', 'Konfiguration')}: <strong>{projectContract.config_snapshot?.name}</strong>
                            </small>
                          </div>
                        </div>
                      )}

                      {/* Simulation preview (when override changed) */}
                      {simulationResult && !projectContract && (
                        <div className="alert alert-info py-2 px-3">
                          <small className="fw-semibold d-block mb-1">{t('projectView.contractConfig.preview', 'Förhandsgranskning vid aktuellt projektvärde')}:</small>
                          <small>
                            {t('projectView.contractConfig.customerFee', 'Beställaravgift')}: <strong>{new Intl.NumberFormat('sv-SE').format(simulationResult.customerFee)} kr</strong> —
                            {t('projectView.contractConfig.companyFee', 'Plattformsavgift')}: <strong>{new Intl.NumberFormat('sv-SE').format(simulationResult.companyFee)} kr</strong> —
                            {t('projectView.contractConfig.etappioMargin', 'Marginal')}: <strong>{new Intl.NumberFormat('sv-SE').format(simulationResult.etappioMargin)} kr ({simulationResult.etappioMarginPct}%)</strong>
                          </small>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="card-footer d-flex justify-content-end">
                  <Button type="submit" isLoading={isSaving}>
                    <i className="feather-save me-2"></i>
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </Card>
            )}
          </div>
        )}

        {/* Contract Tab */}
        {activeTab === 'contract' && (
          <div className="tab-pane fade show active">
            {deleteDraftTarget && (
              <Modal
                title={deleteDraftTarget.stageVariation
                  ? t('contracts.deleteDraft.titleVariation', { number: agreementVariationNumber(deleteDraftTarget) })
                  : t('contracts.deleteDraft.title')}
                onClose={() => { if (!isDeletingDraft) setDeleteDraftTarget(null); }}
                footer={
                  <>
                    <Button type="button" variant="secondary" onClick={() => setDeleteDraftTarget(null)} disabled={isDeletingDraft}>
                      {t('common.cancel')}
                    </Button>
                    <button type="button" className="btn btn-danger" onClick={() => void confirmDeleteDraft()} disabled={isDeletingDraft}>
                      {isDeletingDraft && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>}
                      {t(deleteDraftTarget.stageVariation ? 'contracts.deleteDraft.confirmVariation' : 'contracts.deleteDraft.confirm')}
                    </button>
                  </>
                }
              >
                <p>{deleteDraftTarget.stageVariation
                  ? t('contracts.deleteDraft.bodyVariation', { number: agreementVariationNumber(deleteDraftTarget), name: contractTitle(deleteDraftTarget) || '—' })
                  : t('contracts.deleteDraft.body', { number: deleteDraftTarget.contractNumber, name: contractTitle(deleteDraftTarget) || '—' })}</p>
                {!deleteDraftTarget.stageVariation && <p className="text-muted small mb-0">{t('contracts.deleteDraft.note')}</p>}
                {deleteDraftError && <div className="alert alert-danger mt-3 mb-0" role="alert">{deleteDraftError}</div>}
              </Modal>
            )}

            {FEATURE_CONTRACT_WIZARD_V2 && showContractWizard && (
              <ContractWizardModal
                // Leaving the read-only view ("Gå till signering") restarts the wizard on the review step.
                key={contractWizardViewOnly ? 'view' : 'edit'}
                mode={contractWizardEdit ? 'edit' : 'create'}
                initialStep={contractWizardStartStep}
                title={
                  !contractWizardEdit
                    ? t('contracts.wizard.createTitle')
                    : contractWizardSigned
                      ? t('contracts.wizard.edit.signedTitle', { number: contractWizardEdit.contract.contractNumber })
                      : contractWizardViewOnly
                        ? t('contracts.wizard.view.title', { number: contractWizardEdit.contract.contractNumber })
                        : t('contracts.wizard.edit.title')
                }
                subtitle={
                  contractWizardEdit
                    ? t('contracts.wizard.edit.subtitle', {
                        number: contractWizardEdit.contract.contractNumber,
                        name: contractWizardEdit.snapshot.values.contractName || contractTitle(contractWizardEdit.contract),
                      })
                    : t('contracts.wizard.createSubtitle', {
                        number: nextContractNumber,
                        project: project.description,
                      })
                }
                steps={contractWizardSteps}
                banner={
                  <>
                    {/* Why signing waits, and the way forward (ClickUp 869fn1nyr). Not on
                        "Granska & signera": its right-hand panel and the final button
                        already lead to adding the Beställare. */}
                    {!contractWizardReadOnly && !isPerson
                      && contractWizardSteps[contractWizardStepIndex ?? contractWizardStartStep]?.key !== 'review' && (
                      <ProjectCustomerSigningBlock
                        project={project}
                        onAdd={() => openAddCustomer()}
                        onChange={() => void loadData()}
                        reminder={customerReminder}
                      />
                    )}
                    {!contractWizardViewOnly && contractWizardBannerStatus && (
                      <ContractEditBanner
                        status={contractWizardBannerStatus}
                        sentAt={contractWizardEdit?.contract.signingSentAt}
                      />
                    )}
                    {contractWizardStopError && (
                      <div className="alert alert-danger" role="alert">{contractWizardStopError}</div>
                    )}
                  </>
                }
                summary={
                  <WizardSummary
                    contractType={contractWizardEdit?.contract.contractType ?? 'fixed'}
                    values={contractWizardDraft.values}
                    attachmentCount={contractWizardDraft.attachments.length}
                    pricing={
                      contractWizardReachedPricing
                        ? { plan: contractWizardPlan.plan, stageCount: contractWizardPlan.state.rows.length }
                        : undefined
                    }
                  />
                }
                dirty={!contractWizardReadOnly && (contractWizardDraft.dirty || contractWizardPlan.dirty)}
                saveStatus={
                  contractWizardPlan.saveStatusAt > contractWizardDraft.saveStatusAt
                    ? contractWizardPlan.saveStatus
                    : contractWizardDraft.saveStatus
                }
                saving={contractWizardDraft.saving || contractWizardPlan.saving}
                onSave={contractWizardReadOnly ? undefined : () => void saveContractWizard()}
                onStepChange={index => {
                  setContractWizardStepIndex(index);
                  if (index >= 1) setContractWizardReachedPricing(true);
                }}
                onClose={closeContractWizard}
                onDiscard={!contractWizardEdit && contractWizardDraft.contract
                  // A project's only contract cannot be removed; unsent, it
                  // stays hidden from the Beställare by the API.
                  && contracts.some(item => item.id !== contractWizardDraft.contract!.id) ? async () => {
                  // "Avbryt" on a new contract removes its unsent draft (ClickUp 869fkhh2r).
                  await contractApi.deleteContract(contractWizardDraft.contract!.id).catch(() => undefined);
                } : undefined}
                finalAction={
                  contractWizardViewOnly && contractWizardCanSign && !contractWizardSigned
                    ? { label: t('contracts.wizard.view.goToSigning'), onClick: goToContractWizardSigning }
                    : contractWizardReadOnly
                    ? undefined
                    // Without a Beställare the button leads on instead of being
                    // locked (ClickUp 869fn1nyr).
                    : !isPerson && customerState === 'missing'
                    ? { label: t('projectCustomer.readiness.add'), onClick: () => openAddCustomer() }
                    : !isPerson && customerState === 'waiting'
                    ? { label: t('projectCustomer.waiting.remind'), busy: customerReminder.sending, onClick: customerReminder.remind }
                    : {
                        label: t(contractWizardResend ? 'contracts.wizard.edit.review.signButton' : 'contracts.wizard.review.signButton'),
                        busy: contractWizardReview.signing,
                        onClick: () => contractWizardReview.sign(saveContractWizardBeforeSign),
                      }
                }
                overlays={
                  <>
                    {addCustomerTab && (
                      <AddProjectCustomerModal
                        project={project}
                        companyName={project.companyName ?? projectCompany?.companyName}
                        initialTab={addCustomerTab}
                        isAdmin={Boolean(user?.isAdmin)}
                        zIndex={1060}
                        onClose={() => setAddCustomerTab(null)}
                        onDone={() => void loadData()}
                      />
                    )}
                    {contractWizardDraft.libraryOpen && (
                      <AttachmentLibraryPicker
                        documents={contractWizardDraft.libraryDocs}
                        onAdd={contractWizardDraft.linkFromLibrary}
                        onClose={contractWizardDraft.closeLibrary}
                      />
                    )}
                    {contractWizardWithdraw && (
                      <Modal
                        title={t('contracts.wizard.edit.withdraw.title')}
                        onClose={() => {
                          if (!contractWizardWithdraw.busy) settleContractWithdrawal(false);
                        }}
                        zIndex={1060}
                        footer={
                          <>
                            <button
                              type="button"
                              className="btn contract-wizard-btn-outline"
                              onClick={() => settleContractWithdrawal(false)}
                              disabled={contractWizardWithdraw.busy}
                            >
                              {t('contracts.wizard.edit.withdraw.cancel')}
                            </button>
                            <button
                              type="button"
                              className="btn contract-wizard-btn-navy"
                              onClick={() => void confirmContractWithdrawal()}
                              disabled={contractWizardWithdraw.busy}
                            >
                              {contractWizardWithdraw.busy && (
                                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                              )}
                              {t('contracts.wizard.edit.withdraw.confirm')}
                            </button>
                          </>
                        }
                      >
                        <p>{t('contracts.wizard.edit.withdraw.body')}</p>
                        <p className="mb-0">{t('contracts.wizard.edit.withdraw.bodyResign')}</p>
                        {contractWizardWithdraw.error && (
                          <div className="alert alert-danger mt-3 mb-0" role="alert">{contractWizardWithdraw.error}</div>
                        )}
                      </Modal>
                    )}
                  </>
                }
              />
            )}

            {FEATURE_CONTRACT_WIZARD_V2 && showCreateContractTypePrompt && (
              <CreateAgreementTypeModal
                projectName={project.description}
                basis={variationBasis(contracts, Object.values(contractMilestonesMap).flat(), selectedContract?.id)}
                variationBlocked={(() => {
                  // An unfinished ÄTA is named before anything is filled in (ClickUp 869fkk2jh).
                  const basis = variationBasis(contracts, Object.values(contractMilestonesMap).flat(), selectedContract?.id);
                  const parent = basis && contracts.find(c => c.id === basis.parentId);
                  const block = parent ? variationBlock(parent, contracts, contractReplacements) : null;
                  return block ? variationBlockText(block, contracts, t) : undefined;
                })()}
                opening={agreementChoiceOpening}
                error={agreementChoiceError}
                onCancel={() => setShowCreateContractTypePrompt(false)}
                onNext={choice => void handleAgreementChoice(choice)}
              />
            )}

            {/* Create-contract type prompt (the old create flow, without the wizard flag) */}
            {!FEATURE_CONTRACT_WIZARD_V2 && showCreateContractTypePrompt && (
              <Modal title={t('contracts.createTypePrompt.title')} size="lg" fullscreenBelow="sm"
                onClose={() => { setShowCreateContractTypePrompt(false); setConflictVariationDraft(null); }}
                footer={<>
                      <Button type="button" variant="secondary" onClick={() => { setShowCreateContractTypePrompt(false); setConflictVariationDraft(null); }}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={confirmCreateContractType}
                        disabled={
                          (createTypeChoice === 'variation' && eligibleParentContracts.length === 0) ||
                          negativeVariationCapacityLoading
                        }
                      >
                        {t('contracts.createTypePrompt.continue')}
                      </Button>
                </>}
              >

                {conflictVariationDraft && (
                  <div className="alert alert-info" data-testid="conflict-variation-scope">
                    {t('conflictVariation.resolves', {
                      number: conflictVariationDraft.milestone.milestoneNumber,
                      contract: contracts.find(c => c.id === conflictVariationDraft.milestone.contractId)?.contractNumber
                    })}
                    <div className="small mt-1">{t('conflictVariation.scopeHint')}</div>
                  </div>
                )}
                {conflictVariationDraft?.reference && (
                  <ContractReferenceNote contractId={conflictVariationDraft.milestone.contractId} message={conflictVariationDraft.reference} locked={false} />
                )}
                <div className="form-check mb-3 p-3 border rounded" style={{ paddingLeft: '2.5rem' }}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="createContractType"
                    id="createTypeFixed"
                    checked={createTypeChoice === 'fixed'}
                    disabled={!!conflictVariationDraft}
                    onChange={() => setCreateTypeChoice('fixed')}
                  />
                  <label className="form-check-label" htmlFor="createTypeFixed">
                    <span className="fw-semibold d-block">{t('contracts.types.fixed')}</span>
                    <small className="text-muted">{t('contracts.createTypePrompt.fixedDescription')}</small>
                  </label>
                </div>
                <div className="form-check p-3 border rounded" style={{ paddingLeft: '2.5rem' }}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="createContractType"
                    id="createTypeVariation"
                    checked={createTypeChoice === 'variation'}
                    onChange={() => setCreateTypeChoice('variation')}
                    disabled={eligibleParentContracts.length === 0}
                  />
                  <label className="form-check-label" htmlFor="createTypeVariation">
                    <span className="fw-semibold d-block">{t('contracts.types.variation')}</span>
                    <small className="text-muted">{t('contracts.createTypePrompt.variationDescription')}</small>
                    {eligibleParentContracts.length === 0 && (
                      <small className="text-warning d-block mt-1">
                        <i className="feather-info me-1"></i>
                        {t('contracts.createTypePrompt.variationRequiresSigned')}
                      </small>
                    )}
                  </label>
                </div>

              </Modal>
            )}


            {showNegativeVariationConfirm && (
              <div
                className="modal fade show d-block"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="negativeVariationConfirmTitle"
                data-testid="negative-variation-confirmation"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title" id="negativeVariationConfirmTitle">
                        <i className="feather-alert-triangle text-warning me-2"></i>
                        {t('projectView.ata.negativeConfirmTitle')}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={closeNegativeVariationConfirmation}
                        aria-label={t('common.cancel')}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>
                        {t('projectView.ata.negativeConfirmIntro', {
                          amount: formatNumber(negativeVariationConfirmAmountOre / 100)
                        })}
                      </p>
                      <div className="alert alert-info mb-3">
                        <div className="fw-semibold mb-1">
                          {t('projectView.ata.negativeConfirmExact')}
                        </div>
                        <div>{t('projectView.ata.negativeConfirmNoFee')}</div>
                      </div>
                      {negativeVariationCapacityOre !== null && (
                        <p className="small text-muted mb-0">
                          {t('projectView.ata.negativeConfirmHeld', {
                            amount: formatNumber(negativeVariationCapacityOre / 100)
                          })}
                        </p>
                      )}
                    </div>
                    <div className="modal-footer flex-column flex-sm-row">
                      <Button
                        type="button"
                        variant="secondary"
                        className="w-100 w-sm-auto"
                        style={{ minHeight: '44px' }}
                        onClick={closeNegativeVariationConfirmation}
                      >
                        {t('projectView.ata.negativeConfirmBack')}
                      </Button>
                      <Button
                        type="button"
                        className="w-100 w-sm-auto"
                        style={{ minHeight: '44px' }}
                        data-testid="confirm-negative-variation"
                        onClick={() => { void confirmNegativeVariation(); }}
                      >
                        {t('projectView.ata.negativeConfirmContinue')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Change contract type confirmation */}
            {showChangeTypeConfirm && (
              <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="feather-alert-triangle text-warning me-2"></i>
                        {t('contracts.form.changeTypeTitle')}
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowChangeTypeConfirm(false)} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                      <p className="mb-0">{t('contracts.form.changeTypeWarning')}</p>
                    </div>
                    <div className="modal-footer">
                      <Button type="button" variant="secondary" onClick={() => setShowChangeTypeConfirm(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="button" variant="warning" onClick={confirmChangeContractType}>
                        {t('contracts.form.changeTypeConfirm')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showAssignmentRequired && (
              <div
                className="modal fade show d-block"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="assignmentRequiredTitle"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title" id="assignmentRequiredTitle">
                        <i className="feather-alert-triangle text-warning me-2"></i>
                        {t('contracts.descriptions.requiredModalTitle', 'Uppgifter saknas')}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowAssignmentRequired(false)}
                        aria-label={t('common.close', 'Stäng')}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p className="mb-2">
                        {t('contracts.descriptions.requiredModalIntro', 'Fyll i följande innan du går vidare:')}
                      </p>
                      <ul className="mb-0">
                        {assignmentRequiredMissing.name && (
                          <li>{t('contracts.descriptions.requiredNameItem', 'Avtalsnamn är obligatoriskt – fyll i ett avtalsnamn.')}</li>
                        )}
                        {assignmentRequiredMissing.assignment && (
                          <li>{t('contracts.descriptions.requiredAssignmentItem', 'Skriv en uppdragsbeskrivning eller kryssa i rutan om bifogade dokument.')}</li>
                        )}
                      </ul>
                    </div>
                    <div className="modal-footer">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => {
                          setShowAssignmentRequired(false);
                          setTimeout(focusFirstMissingContractField, 0);
                        }}
                      >
                        {t('contracts.descriptions.requiredModalConfirm', 'Fyll i uppgifterna')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contract List */}
            <ContractList
              className="mb-4"
              rows={contractListRows}
              selectedId={selectedContract?.id ?? null}
              onSelect={(id) => {
                const contract = filteredAndSortedContracts.find((c) => c.id === id);
                if (contract) handleSelectContract(contract);
              }}
              sortField={contractSortField}
              sortDirection={contractSortDirection}
              onSort={handleContractSort}
              onRowAction={handleContractRowAction}
              selectedActions={listActions}
              headerAction={
                canCreateContract ? (
                  <Button onClick={handleCreateContract} aria-label={t('contracts.createContract')}>
                    <i className="feather-plus me-2 d-none d-sm-inline"></i>
                    <i className="feather-plus d-sm-none"></i>
                    <span className="d-none d-sm-inline">{t('contracts.createContract')}</span>
                  </Button>
                ) : undefined
              }
              emptyState={
                <div className="text-center text-muted py-4">
                  {contracts.length === 0 ? t('contracts.noContracts') : t('contracts.noResults')}
                </div>
              }
            />

            {/* The cards under the list, with the Beställare card to their right
                while the project has none (869ff1egy, App.css). */}
            <div className={`contract-view-layout${showCustomerPanel ? ' has-customer-panel' : ''}`}>
            {/* The cards under the list: two side by side on wide screens (App.css). */}
            <div className="contract-view-cards">
            {/* Contract Actions */}
            {!isPerson && selectedContract && contractReference && contractReference.projectId === id && contractReference.contractId === selectedContract.id && (
              <div id="contract-section-reference" style={{ scrollMarginTop: '90px' }}>
                <ContractReferenceNote
                  contractId={selectedContract.id}
                  message={contractReference.message}
                  locked={contractLockedForEditing}
                  onDismiss={() => setContractReference(null)}
                />
              </div>
            )}
            {!isPerson && selectedContract && referenceLoadError && <div className="alert alert-warning">
              {t('counterproposal.referenceFailed')}
              <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => setReferenceReload(value => value + 1)}>{t('counterproposal.retry')}</button>
            </div>}
            {selectedContract && !isCreatingContract && <ConflictVariationNotice contract={selectedContract} />}
            {selectedContract && !isCreatingContract && (
              <Card
                className="mb-4 contract-overview-card"
                title={t('contracts.summary.title')}
                icon="file-text"
                headerAction={<ContractStatePill contract={selectedContract} />}
              >
                  <ContractOverview
                    contract={selectedContract}
                    isPerson={isPerson}
                    customerName={overviewCustomerName}
                    companyName={overviewCompanyName}
                    missing={contractMissingItems(milestones, assignmentDescription, referToAttachments)}
                  />
                  <ContractReplacementNotice
                    contract={selectedContract}
                    milestones={milestones}
                    originalContract={
                      selectedContract.replacesContractId
                        ? (contracts.find((contract) => contract.id === selectedContract.replacesContractId) ?? null)
                        : null
                    }
                    settlement={selectedReplacementSettlement}
                    // An ÄTA's figures and help are further down the card (869fmxfb7).
                    compact={isStageVariation}
                    onPay={isPerson ? () => navigate('?tab=transactions&paymentPicker=open') : undefined}
                    onApproveProposal={
                      !isPerson && selectedReplacementSettlement
                        ? () => void handleReplacementProposalResponse(
                            selectedReplacementSettlement,
                            'approve'
                          )
                        : undefined
                    }
                    onRejectProposal={
                      !isPerson && selectedReplacementSettlement
                        ? () => confirmReplacementProposalRejection(
                            selectedReplacementSettlement
                          )
                        : undefined
                    }
                    onWithdrawProposal={
                      isPerson && selectedReplacementSettlement
                        ? () => confirmReplacementProposalWithdrawal(
                            selectedReplacementSettlement
                          )
                        : undefined
                    }
                    isUpdatingProposal={isUpdatingReplacementProposal}
                  />
                  {!isPerson && isDeclinedSigning && (
                    <ContractSigningRejectionNotice
                      variant="danger"
                      statusText={t('contracts.signingStatus.rejectedAlert')}
                      helpText={t('contracts.signingStatus.rejectedAlertInfo')}
                      detailText={signingRejectionDetail}
                      commentLabel={t(
                        'contracts.signingStatus.rejectionCommentLabel'
                      )}
                      comment={selectedContract.signingRejectionMessage}
                    />
                  )}
                  {!isPerson && !isDeclinedSigning && isRejectedDraft && (
                    <ContractSigningRejectionNotice
                      variant="danger"
                      statusText={t('contracts.draftStatus.rejectedAlert')}
                      helpText={t('contracts.draftStatus.rejectedAlertInfo')}
                      detailText={
                        selectedContract.draftRespondedAt
                          ? `Skickad ${new Date(
                              selectedContract.draftRespondedAt
                            ).toLocaleString('sv-SE', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`
                          : null
                      }
                      commentLabel={t('contracts.preview.customerCommentLabel')}
                      comment={selectedContract.draftRejectionMessage}
                    />
                  )}
                  {hasIncompleteSigning && !isDeclinedSigning && (
                    <div className="alert alert-warning py-2 px-3 mb-3" role="status">
                      <i className="feather-alert-circle me-2"></i>
                      {t(`contracts.signingStatus.${selectedContract.signingStatus}`)}
                    </div>
                  )}
                  {/* Signed by both: "Signerat av båda parter" at the top of the
                      card says it, with the dates (869fkjvdx). */}
                  {isSignedContract ? null : isProjectClosedForSigning ? (
                    <div
                      className="alert alert-secondary mb-0"
                      data-testid="closed-project-signing-notice"
                      role="status"
                    >
                      <i className="feather-lock me-2"></i>
                      {t('contracts.actions.projectClosedNotice')}
                    </div>
                  ) : (
                    <>
                      {isPerson && !isSignedContract && !isRejectedDraft && !hasExternalSigningProcess && (
                        <div
                          className="alert alert-info py-2 px-3 mb-3"
                          data-testid="signing-awaiting-company-notice"
                          role="status"
                        >
                          <i className="feather-clock me-2"></i>
                          {t('contracts.actions.awaitingCompanySigning')}
                        </div>
                      )}
                      {!isPerson && showSigningActions && !isCompanySigned && <ProjectCustomerSigningBlock project={project} onAdd={() => openAddCustomer()} />}
                      {isStageVariation && stageVariationDirty && <p className="small text-warning mt-2" role="status">{t('stageVariation.saveBeforeSigning')}</p>}
                    </>
                  )}
                  {/* The contract's summary under the status (869fkjvdx): amount,
                      stages and attachments, once; a private person sees it once
                      the draft has been sent. */}
                  {!(isPerson && overviewState === 'draft') && (
                    <ContractSummary
                      contract={selectedContract}
                      milestones={milestones}
                      attachments={contractAttachments}
                      formatAmount={formatCurrency}
                      formatSize={formatAttachmentSize}
                      onOpenAttachment={(file) => {
                        const doc = contractAttachments.find(candidate => candidate.id === file.id);
                        if (doc) void handlePreview(doc);
                      }}
                      // An ÄTA shows what it changes, not the whole plan (869fmxfb7).
                      keyFigures={isStageVariation && selectedReplacementSettlement
                        ? <VariationChanges settlement={selectedReplacementSettlement} milestones={milestones} part="figures" />
                        : undefined}
                      changes={isStageVariation && selectedReplacementSettlement ? <>
                        <h3 className="contract-summary-heading">{t('variationWizard.diffTitle')}</h3>
                        <VariationChanges settlement={selectedReplacementSettlement} milestones={milestones} inCard part="table" />
                      </> : undefined}
                      help={(isStageVariation || showSigningActions) ? <>
                        {isStageVariation && (
                          <details className="contract-signing-help">
                            <summary>
                              <i className="feather-info" aria-hidden="true"></i>
                              <span className="contract-signing-help-title">{t('stageVariation.helpTitle')}</span>
                              <i className="feather-chevron-down contract-signing-help-chevron" aria-hidden="true"></i>
                            </summary>
                            <p className="contract-actions-text">{t('stageVariation.noticeHelp')}</p>
                          </details>
                        )}
                        {showSigningActions && (
                          <ContractSigningHelp
                            description={isPerson ? t('personProject.reviewInfo') : t('contracts.actions.publishInfo')}
                            reviewInfoCards={reviewInfoCards}
                            reviewInfoTitle={t('contracts.actions.reviewCards.title')}
                          />
                        )}
                      </> : undefined}
                    />
                  )}
                  {/* Admin lock/unlock buttons */}
                  {user?.isAdmin && hasCompanyCompletedSigning && !isRejectedContract && (
                    <div className="border-top pt-3 mt-3 d-flex align-items-center justify-content-between">
                      <div>
                        {selectedContract.adminUnlocked ? (
                          <span className="badge bg-warning text-dark">
                            <i className="feather-unlock me-1"></i>
                            {t('contracts.adminLock.unlockedBadge')}
                            {selectedContract.adminUnlockedBy && ` — ${selectedContract.adminUnlockedBy}`}
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <i className="feather-lock me-1"></i>
                            {t('contracts.adminLock.lockedBadge')}
                          </span>
                        )}
                      </div>
                      <button
                        className={`btn btn-sm ${selectedContract.adminUnlocked ? 'btn-warning' : 'btn-outline-warning'}`}
                        onClick={async () => {
                          try {
                            await contractApi.adminToggleLock(selectedContract.id);
                            setMessage({ type: 'success', text: selectedContract.adminUnlocked ? t('contracts.adminLock.locked') : t('contracts.adminLock.unlocked') });
                            await reloadSelectedContract();
                          } catch {
                            setMessage({ type: 'error', text: t('contracts.adminLock.error') });
                          }
                        }}
                      >
                        <i className={`feather-${selectedContract.adminUnlocked ? 'lock' : 'unlock'} me-1`}></i>
                        {selectedContract.adminUnlocked ? t('contracts.adminLock.lockButton') : t('contracts.adminLock.unlockButton')}
                      </button>
                    </div>
                  )}
              </Card>
            )}

            {/* No separate "Signerat avtal" card: the list's "Signerat avtal" button opens the signed PDF (869fa5qf8). */}

            {/* Contract Form (full width when creating) */}
            {isCreatingContract && (
              <Modal
                title={t('contracts.form.createTitle')}
                size="lg"
                onClose={requestCancelNewContract}
                footer={
                  <>
                    <Button type="button" variant="secondary" onClick={requestCancelNewContract}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="button" onClick={handleSaveNewContract} disabled={isSavingNewContract}>
                      {isSavingNewContract ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t('common.saving', 'Sparar…')}</>
                      ) : t('common.save')}
                    </Button>
                  </>
                }
              >
                {conflictVariationDraft && (
                  <div className="alert alert-info" data-testid="conflict-variation-scope">
                    {t('conflictVariation.resolves', {
                      number: conflictVariationDraft.milestone.milestoneNumber,
                      contract: contracts.find(c => c.id === conflictVariationDraft.milestone.contractId)?.contractNumber
                    })}
                    <div className="small mt-1">{t('conflictVariation.scopeHint')}</div>
                  </div>
                )}
                {conflictVariationDraft?.reference && (
                  <ContractReferenceNote contractId={conflictVariationDraft.milestone.contractId} message={conflictVariationDraft.reference} locked={false} />
                )}
                <form onSubmit={(e) => e.preventDefault()}>
                  <div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">{t('contracts.columns.companyAttestation')}</label>
                        <div>{getAttestationBadge('not_attested')}</div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">{t('contracts.columns.personAttestation')}</label>
                        <div>{getAttestationBadge('not_attested')}</div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('contracts.columns.contractNumber')}</label>
                        <input type="text" className="form-control" value={nextContractNumber} disabled />
                        <small className="text-muted">{t('contracts.form.contractNumberHelp')}</small>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t('contracts.form.contractTypeLabel')}</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="feather-lock text-muted"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control bg-light fw-semibold"
                            value={t(`contracts.types.${contractFormData.contractType}`)}
                            readOnly
                            disabled
                          />
                        </div>
                        <small className="text-muted d-block mt-1">{t('contracts.form.contractTypeHelp')}</small>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mt-1"
                          onClick={handleChangeContractType}
                        >
                          <i className="feather-edit-2 me-1"></i>{t('contracts.form.changeType')}
                        </button>
                      </div>
                    </div>
                    {contractFormData.contractType === 'variation' && (() => {
                      const parent = contracts.find(c => c.id === contractFormData.parentContractId);
                      return (
                        <div className="mb-3">
                          <label className="form-label">{t('contracts.preview.parentContractLabel')}</label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <i className="feather-lock text-muted"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control bg-light"
                              value={parent
                                ? `#${parent.contractNumber} — ${contractTitle(parent) || '—'} (${parent.contractType === 'variation'
                                  ? `${t('contracts.types.variation')}${parent.variationNumber != null ? ` #${parent.variationNumber}` : ''}`
                                  : t('contracts.types.fixed')})`
                                : '—'}
                              readOnly
                              disabled
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </form>
              </Modal>
            )}

            {/* Cancel-new-contract confirmation (shown on top of the create modal) */}
            {showCancelNewContractConfirm && (
              <Modal
                title={t('contracts.form.cancelCreate.title', 'Avbryt kontrakt?')}
                onClose={() => setShowCancelNewContractConfirm(false)}
                zIndex={1060}
                footer={
                  <>
                    <Button type="button" variant="secondary" onClick={() => setShowCancelNewContractConfirm(false)}>
                      {t('contracts.form.cancelCreate.keepEditing', 'Fortsätt redigera')}
                    </Button>
                    <Button type="button" variant="danger" onClick={confirmCancelNewContract}>
                      {t('contracts.form.cancelCreate.confirm', 'Avbryt kontrakt')}
                    </Button>
                  </>
                }
              >
                <p className="mb-0">
                  {t('contracts.form.cancelCreate.body', 'Kontraktet har inte sparats. Om du avbryter går uppgifterna förlorade.')}
                </p>
              </Modal>
            )}

            {/* Contract Form + Milestones side by side when editing */}
            {selectedContract && !isCreatingContract && (
              <>
                {/* Etappmål — full width */}
                <div id="contract-section-milestones" style={{ scrollMarginTop: '90px' }}></div>
                  {/* Same heading row as the page's other cards (Läge, Sammanfattning). */}
                  {!isStageVariation && legacyContractSections && <Card className="mb-4">
                    <div className="card-header d-flex align-items-center justify-content-between">
                      <h5 className="card-title mb-0">
                        <i className="feather-flag" aria-hidden="true"></i>
                        {isPriceAdjustmentVariation
                          ? isZeroValuePriceAdjustmentVariation ? t('projectView.ata.change') : t('projectView.ata.priceChange')
                          : t('milestones.list.title')}
                      </h5>
                      {!isPerson && !isPriceAdjustmentVariation && (
                        <div className="d-flex gap-2">
                          <Button
                            className="btn-guided"
                            aria-label={t('milestones.wizard.openButton')}
                            style={{ minWidth: 44, minHeight: 44 }}
                            onClick={handleOpenMilestoneWizard}
                            disabled={contractLockedForEditing}
                          >
                            <i className="feather-list me-2 d-none d-sm-inline"></i>
                            <i className="feather-list d-sm-none"></i>
                            <span className="d-none d-sm-inline">{t('milestones.wizard.openButton', 'Skapa etappmålsplan – Guidad process')}</span>
                          </Button>
                          <Button onClick={handleCreateMilestone} disabled={contractLockedForEditing} aria-label={t('milestones.createMilestone')} style={{ minWidth: 44, minHeight: 44 }}>
                            <i className="feather-plus me-2 d-none d-sm-inline"></i>
                            <i className="feather-plus d-sm-none"></i>
                            <span className="d-none d-sm-inline">{t('milestones.createMilestone')}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="card-body border-bottom bg-light">
                      <p className="fw-bold mb-2">{t('milestones.intro.title')}</p>
                      <p className="mb-2 small">{t('milestones.intro.p1')}</p>
                      <p className="mb-0 small">{t('milestones.intro.p2')}</p>
                    </div>
                    <div className="card-body">
                      {milestones.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          {t('milestones.noMilestones')}
                        </div>
                      ) : (
                        <>
                          {/* Desktop table */}
                          <div className="table-responsive d-none d-md-block">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>{isPriceAdjustmentVariation ? t('projectView.ata.short') : t('milestones.columns.milestoneNumber')}</th>
                                  <th>{t('milestones.columns.description')}</th>
                                  <th>{milestoneRotAmount !== 0 ? 'Belopp efter ROT' : t('milestones.columns.amount')}</th>
                                  <th>{t('milestones.columns.endDate')}</th>
                                  <th>{t('milestones.columns.companyAttestation')}</th>
                                  <th>{t('milestones.columns.personAttestation')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {milestones.map(milestone => {
                                  const msgs = allMilestoneMessages[milestone.id] || [];
                                  return (
                                      <tr
                                        key={milestone.id}
                                        onClick={isPriceAdjustmentVariation ? undefined : () => handleSelectMilestone(milestone)}
                                        className={milestone.stageReplacement ? 'table-light text-secondary' : selectedMilestone?.id === milestone.id ? 'table-primary' : ''}
                                        data-stage-history-row={milestone.id}
                                        style={{ cursor: isPriceAdjustmentVariation ? 'default' : 'pointer', height: 44 }}
                                      >
                                        <td>{isPriceAdjustmentVariation ? t('projectView.ata.priceAdjustment') : milestone.milestoneNumber}</td>
                                        <td>
                                          {milestone.description}
                                          <MilestoneReplacementBadge milestone={milestone} />
                                          {milestone.deliveryStatus === 'paid' && paidMilestoneBadge()}
                                          {msgs.length > 0 && (
                                            <span
                                              className="badge bg-light text-muted border ms-2"
                                              title={t('milestones.messages.indicator', { count: msgs.length })}
                                            >
                                              <i className="feather-message-square me-1" style={{ fontSize: 11 }}></i>
                                              {msgs.length}
                                            </span>
                                          )}
                                        </td>
                                        <td>{formatNumber(milestone.afterRotAmount)}</td>
                                        <td>{isPriceAdjustmentVariation ? '—' : milestone.endDate || '—'}</td>
                                        <td>{getMilestoneAttestationBadge(milestone.companyAttestation || 'not_attested')}</td>
                                        <td>{getMilestoneAttestationBadge(milestone.personAttestation || 'not_attested')}</td>
                                      </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile card list */}
                          <div className="d-md-none">
                            {milestones.map(milestone => {
                              const msgs = allMilestoneMessages[milestone.id] || [];
                              return (
                                <div
                                  key={milestone.id}
                                  className={`border rounded p-3 mb-2 ${milestone.stageReplacement ? 'bg-light text-secondary' : selectedMilestone?.id === milestone.id ? 'border-primary bg-light' : ''}`}
                                  data-stage-history-card={milestone.id}
                                  onClick={isPriceAdjustmentVariation ? undefined : () => handleSelectMilestone(milestone)}
                                  style={{ cursor: isPriceAdjustmentVariation ? 'default' : 'pointer', minHeight: 44, height: 'auto' }}
                                >
                                  <div className="d-flex align-items-center justify-content-between mb-1">
                                    <span className="fw-bold">
                                      {isPriceAdjustmentVariation
                                        ? isZeroValuePriceAdjustmentVariation ? t('projectView.ata.change') : t('projectView.ata.priceChange')
                                        : `#${milestone.milestoneNumber}`}
                                    </span>
                                    <span>{formatNumber(milestone.afterRotAmount)} kr</span>
                                  </div>
                                  <div className="mb-1">{milestone.description}<MilestoneReplacementBadge milestone={milestone} /></div>
                                  {milestone.deliveryStatus === 'paid' && <div className="mb-2">{paidMilestoneBadge()}</div>}
                                  {milestone.endDate && !isPriceAdjustmentVariation && (
                                    <div className="d-flex flex-wrap gap-2 mb-1">
                                      <small className="text-muted">{t('milestones.columns.endDate')}: {milestone.endDate}</small>
                                    </div>
                                  )}
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    {getMilestoneAttestationBadge(milestone.companyAttestation || 'not_attested')}
                                    {getMilestoneAttestationBadge(milestone.personAttestation || 'not_attested')}
                                    {msgs.length > 0 && (
                                      <span
                                        className="badge bg-light text-muted border d-inline-flex align-items-center"
                                        title={t('milestones.messages.indicator', { count: msgs.length })}
                                      >
                                        <i className="feather-message-square me-1" style={{ fontSize: 11 }}></i>
                                        {msgs.length}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </Card>

                  }
                  {/* Guided milestone-plan wizard */}
                  {milestoneWizardOpen && !isPerson && (
                    <Modal
                      size="lg"
                      onClose={handleWizardRequestClose}
                      title={<><i className="feather-list me-2"></i>{t('milestones.wizard.openButton', 'Skapa etappmålsplan – Guidad process')}</>}
                      footer={<div className="w-100">
                        <MilestoneAmountSummary total={wizTotalNum} afterRot={wizAfterRotNum} rot={wizPlannedRotNum} previousTotal={Number(selectedContract.contractAmount)} />
                          <div className="d-flex flex-wrap justify-content-between gap-2">
                            <Button variant="secondary" onClick={handleWizardRequestClose}>{t('common.cancel')}</Button>
                            <div className="d-flex gap-2">
                              {wizStep > 0 && (
                                <Button variant="light-brand" onClick={() => { setWizError(''); setWizStep(s => Math.max(0, s - 1)); }}>
                                  {t('common.previous', 'Föregående')}
                                </Button>
                              )}
                              {wizStep < 2 && (
                                <Button onClick={handleWizardNext}>{t('common.next', 'Nästa')}</Button>
                              )}
                              {wizStep === 2 && (
                                <Button onClick={handleWizardSubmit} isLoading={isSavingPending}>
                                  <i className="feather-save me-2"></i>
                                  {t('milestones.createMilestone')}
                                </Button>
                              )}
                            </div>
                          </div>
                      </div>}
                    >
                        <div className="milestone-plan-wizard">
                          <Stepper
                            steps={[
                              { key: 'total', label: t('milestones.wizard.stepTotal', 'Anbudssumma') },
                              { key: 'count', label: t('milestones.wizard.stepCount', 'Antal etappmål') },
                              { key: 'distribute', label: t('milestones.wizard.stepDistribute') }
                            ]}
                            currentStep={wizStep}
                          />

                          {wizStep === 0 && (
                            <StepContent
                              title={t('milestones.wizard.step1Title')}
                              description={t('milestones.pricing.topDownHelp')}
                            >
                              <label className="form-label">{t('projects.form.contractAmount', 'Kontrakt Anbudssumma')}</label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  min={selectedContract?.contractType === 'variation' ? undefined : 0}
                                  step="1"
                                  inputMode="numeric"
                                  className="form-control"
                                  value={wizTotal}
                                  onKeyDown={blockDecimalKey}
                                  onChange={(e) => setWizTotal(toWholeKronor(e.target.value))}
                                  onFocus={(e) => e.currentTarget.select()}
                                  autoFocus
                                />
                                <span className="input-group-text">kr</span>
                              </div>
                              <div className="mt-4">
                                <label className="form-label d-block">{t('contracts.form.rot.afterRot')}</label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    step="1"
                                    inputMode="numeric"
                                    className="form-control"
                                    placeholder={String(wizTotalNum || '')}
                                    value={wizAfterRotValue}
                                    onKeyDown={blockDecimalKey}
                                    onChange={event => setWizAfterRotValue(toWholeKronor(event.target.value))}
                                  />
                                  <span className="input-group-text">kr</span>
                                </div>
                                {Math.round(wizPlannedRotNum * 100) === 0 ? (
                                  <div className="alert alert-info py-2 mt-2 mb-0 small">
                                    {t('projectView.wizard.noRotHint')}
                                  </div>
                                ) : (
                                  <div className="small text-muted mt-2" data-testid="wizard-rot-hint">
                                    <p className="fw-semibold text-body mb-1">
                                      {t('projectView.wizard.plannedRotSentence', { amount: `${formatNumber(wizPlannedRotNum)} kr` })}
                                    </p>
                                    {t('projectView.wizard.customerPaysAfterRot', { amount: `${formatNumber(wizAfterRotNum)} kr` })}
                                  </div>
                                )}
                              </div>
                            </StepContent>
                          )}

                          {wizStep === 1 && (
                            <StepContent title={t('milestones.wizard.step2Title', 'Hur många etappmål vill du skapa?')}>
                              {wizPreservedMilestones.length > 0 && (
                                <div className="mb-3" data-testid="wizard-paid-stage-summary">
                                  <p className="text-muted">{t('projectView.wizard.editableCountHelp', { count: wizPreservedMilestones.length })}</p>
                                  <h6>{t('projectView.wizard.paidMilestonesTitle')}</h6>
                                  {wizPreservedMilestones.map(milestone => (
                                    <div key={milestone.id} className="d-flex justify-content-between gap-2 border rounded bg-light p-2 mb-2">
                                      <span><i className="feather-lock me-2" aria-hidden="true"></i>#{milestone.milestoneNumber} — {milestone.description}</span>
                                      <span className="text-nowrap">{formatNumber(milestone.afterRotAmount)} kr</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="mb-2">{t('milestones.intro.p1')}</p>
                              <p className="mb-2">{t('milestones.intro.p2')}</p>
                              <p className="text-muted small mb-3">
                                {t('milestones.wizard.countHelp')}
                              </p>
                              <MilestoneGuidance />
                              <label className="form-label">{wizPreservedMilestones.length > 0 ? t('projectView.wizard.editableCountLabel') : t('milestones.countPrompt.label', 'Antal etappmål')}</label>
                              <input
                                type="number"
                                min={wizMinimumCount}
                                max={50}
                                className="form-control"
                                value={wizCount}
                                onChange={(e) => setWizCount(e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                              />
                            </StepContent>
                          )}

                          {wizStep === 2 && (
                            <StepContent
                              title={t('milestones.wizard.step3Title')}
                              description={t('milestones.wizard.step3Help')}
                            >
                              <MilestoneGuidance />
                              {wizPreservedMilestones.length > 0 && (
                                <div className="alert alert-info py-2 small">
                                  {t('projectView.wizard.preservedMilestones', {
                                    count: wizPreservedMilestones.length,
                                    amount: `${formatNumber(wizPreservedAfterRotNum)} kr`
                                  })}
                                  <p className="mb-0 mt-1">{t('projectView.wizard.paidLockedHelp')}</p>
                                </div>
                              )}
                              {wizPreservedMilestones.map(milestone => (
                                <div key={milestone.id} data-testid="paid-wizard-milestone" className="border rounded bg-light p-3 mb-2">
                                  <div className="d-flex flex-wrap justify-content-between gap-2">
                                    <span>#{milestone.milestoneNumber} — {milestone.description}</span>
                                    {paidMilestoneBadge()}
                                  </div>
                                  <div className="d-flex flex-wrap justify-content-between gap-2 mt-2">
                                    <strong>{t('contracts.form.rot.afterRot')}: {formatNumber(milestone.afterRotAmount)} kr</strong>
                                    <span>{t('milestones.columns.endDate')}: {milestone.endDate || '—'}</span>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                    <span>{t('milestones.columns.companyAttestation')}: {getMilestoneAttestationBadge(milestone.companyAttestation || 'not_attested')}</span>
                                    <span>{t('milestones.columns.personAttestation')}: {getMilestoneAttestationBadge(milestone.personAttestation || 'not_attested')}</span>
                                  </div>
                                </div>
                              ))}
                              <div className="btn-group mb-3" role="group">
                                <button
                                  type="button"
                                  className={`btn ${wizMode === 'amount' ? 'btn-primary' : 'btn-outline-primary'}`}
                                  onClick={() => { setWizMode('amount'); setWizError(''); }}
                                >
                                  {t('milestones.wizard.modeAmount', 'Belopp (kr)')}
                                </button>
                                <button
                                  type="button"
                                  className={`btn ${wizMode === 'percent' ? 'btn-primary' : 'btn-outline-primary'}`}
                                  onClick={() => { setWizMode('percent'); setWizError(''); }}
                                >
                                  {t('milestones.wizard.modePercent', 'Procentuell fördelning (%)')}
                                </button>
                              </div>

                              {wizMode === 'percent' && (
                                <p className="text-muted small">{t('milestones.wizard.percentExample')}</p>
                              )}

                              {wizRows.map((r, i) => (
                                <div key={i} className="row g-2 align-items-end mb-2 pb-2 border-bottom">
                                  <div className="col-12 col-md-2">
                                    <label className="form-label small">{t('milestones.columns.milestoneNumber', 'Etappmål Nr')}</label>
                                    <input type="text" className="form-control" value={r.milestoneNumber ?? nextMilestoneNumber + i} disabled />
                                  </div>
                                  <div className="col-12 col-md-4">
                                    <label className="form-label small">{t('milestones.columns.description', 'Beskrivning')}</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={r.description}
                                      onChange={(e) => setWizRows(rows => rows.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                                    />
                                  </div>
                                  {wizMode === 'amount' ? (
                                    <div className="col-12 col-md-3">
                                      <label className="form-label small">
                                        {wizHasRot ? t('contracts.form.rot.afterRot') : t('milestones.columns.amount', 'Belopp')} (kr)
                                      </label>
                                      <input
                                        type="number"
                                        min={wizMilestoneTargetNum >= 0 ? 0 : undefined}
                                        max={wizMilestoneTargetNum < 0 ? 0 : undefined}
                                        step="1"
                                        inputMode="numeric"
                                        className="form-control"
                                        value={r.amount}
                                        onKeyDown={blockDecimalKey}
                                        onChange={(e) => setWizRows(rows => rows.map((x, idx) => idx === i ? { ...x, amount: toWholeKronor(e.target.value) } : x))}
                                        onFocus={(e) => e.currentTarget.select()}
                                      />
                                    </div>
                                  ) : (
                                    <div className="col-12 col-md-3">
                                      <label className="form-label small">%</label>
                                      <input
                                        type="number"
                                        min={0}
                                        className="form-control"
                                        value={r.percent}
                                        onChange={(e) => setWizRows(rows => rows.map((x, idx) => idx === i ? { ...x, percent: e.target.value } : x))}
                                        onFocus={(e) => e.currentTarget.select()}
                                      />
                                    </div>
                                  )}
                                  <div className="col-12 col-md-3">
                                    <label className="form-label small">
                                      {t('milestones.columns.endDate', 'Beräknat slutdatum')} <span className="text-muted">({t('common.optional', 'valfritt')})</span>
                                    </label>
                                    <input
                                      type="date"
                                      className="form-control"
                                      value={r.endDate}
                                      onChange={(e) => setWizRows(rows => rows.map((x, idx) => idx === i ? { ...x, endDate: e.target.value } : x))}
                                    />
                                  </div>
                                </div>
                              ))}

                              <div className="mt-3 fw-semibold">
                                {wizPreservedMilestones.length > 0 && (
                                  <div data-testid="complete-plan-total" className="mb-1">
                                    {t('projectView.wizard.completePlanTotal', {
                                      amount: formatNumber(wizPreservedAfterRotNum + wizComputedAmounts().reduce((sum, amount) => sum + amount, 0)),
                                      target: formatNumber(wizAfterRotNum)
                                    })}
                                  </div>
                                )}
                                {wizMode === 'amount' ? (() => {
                                  const sumOre = wizRows.reduce((s, r) => s + Math.round(wizParseAmount(r.amount) * 100), 0);
                                  const targetOre = Math.round(wizMilestoneTargetNum * 100);
                                  const remainingOre = targetOre - sumOre;
                                  return (
                                    <span className={remainingOre === 0 ? 'text-success' : 'text-danger'}>
                                      {t('milestones.wizard.sumAmount', 'Summa')}: {formatNumber(sumOre / 100)} / {formatNumber(targetOre / 100)} kr
                                      {remainingOre > 0 && <> — {t('milestones.wizard.remaining', 'kvar')}: {formatNumber(remainingOre / 100)} kr</>}
                                      {remainingOre < 0 && <> — {t('milestones.wizard.exceededBy', 'överskridet med')}: {formatNumber(Math.abs(remainingOre) / 100)} kr</>}
                                    </span>
                                  );
                                })() : (() => {
                                  const pSum = wizRows.reduce((s, r) => s + wizParseAmount(r.percent), 0);
                                  const diff = Math.round((pSum - 100) * 100) / 100;
                                  const ok = Math.abs(pSum - 100) < 0.01;
                                  return (
                                    <span className={ok ? 'text-success' : 'text-danger'}>
                                      {t('milestones.wizard.sumPercent', 'Summa')}: {Math.round(pSum * 100) / 100} % / 100 %
                                      {' '}({t('milestones.wizard.equals', 'motsvarar')} {formatNumber(wizMilestoneTargetNum)} kr)
                                      {diff < 0 && <> — {t('milestones.wizard.remaining', 'kvar')}: {Math.abs(diff)} %</>}
                                      {diff > 0 && <> — {t('milestones.wizard.exceededBy', 'överskridet med')}: {diff} %</>}
                                    </span>
                                  );
                                })()}
                              </div>
                            </StepContent>
                          )}

                          {wizError && <div className="alert alert-danger mt-3 mb-0">{wizError}</div>}

                        </div>
                    </Modal>
                  )}

                  {/* Milestone form (create one at a time / edit / person view) as a modal */}
                  {(isCreatingMilestone || selectedMilestone) && !milestoneWizardOpen && (
                    <Modal
                      onClose={handleMilestoneModalRequestClose}
                      title={isPerson
                        ? t('personProject.milestoneTitle')
                        : (isCreatingMilestone ? t('milestones.form.createTitle') : t('milestones.form.editTitle'))}
                      footer={!isPerson ? (
                        <>
                          <MilestoneAmountSummary {...milestoneAmounts} previousTotal={Number(selectedContract.contractAmount)} />
                        <div className="d-flex justify-content-between w-100">
                          <div>
                            {selectedMilestone && !isCreatingMilestone && (
                              <Button type="button" variant="danger" onClick={() => setShowDeleteMilestoneConfirm(true)} disabled={milestoneLockedForEditing || selectedMilestone?.deliveryStatus === 'paid'}>
                                <i className="feather-trash-2 me-2"></i>
                                {t('projects.deleteProject')}
                              </Button>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            <Button type="button" variant="secondary" onClick={handleMilestoneModalRequestClose}>{t('common.cancel')}</Button>
                            <Button type="submit" form="milestone-form" isLoading={isSavingMilestone} disabled={milestoneLockedForEditing}><i className="feather-save me-2"></i>{t('common.save')}</Button>
                          </div>
                        </div>
                        </>
                      ) : undefined}
                    >
                      {selectedMilestone && <MilestoneReplacementNotice milestone={selectedMilestone} projectId={selectedContract.projectId} />}
                      <form id="milestone-form" onSubmit={isPerson ? (e: React.FormEvent) => e.preventDefault() : handleSaveMilestone}>
                        {!isPerson && !milestoneLockedForEditing && <MilestoneGuidance />}
                        {selectedMilestone?.deliveryStatus === 'paid' && (
                          <div className="alert alert-info">
                            {paidMilestoneBadge()}
                            <p className="mb-0 mt-2">{t(canAdjustPaidMilestone ? 'contractReplacement.paidAdjustmentHelp' : 'projectView.wizard.paidLockedHelp')}</p>
                            <PaidMilestoneNotice milestone={selectedMilestone} />
                          </div>
                        )}
                        <div className="mb-3">
                          <label className="form-label" htmlFor="milestone-number">{t('milestones.columns.milestoneNumber')}</label>
                          <input id="milestone-number" type="text" className="form-control" value={isCreatingMilestone ? nextMilestoneNumber : selectedMilestone?.milestoneNumber} disabled />
                          {!isPerson && <small className="text-muted">{t('milestones.form.milestoneNumberHelp')}</small>}
                        </div>
                        <div className="row">
                          {selectedContract.pricingMode !== 'bottom_up' && <div className="col-md-6 mb-3">
                            <label className="form-label" htmlFor="milestone-gross-amount">{t('milestones.form.stageTotalBeforeRot')}{!isPerson && ' *'}</label>
                            <input id="milestone-gross-amount" type={isPerson ? 'text' : 'number'} step="1" inputMode="numeric" onKeyDown={isPerson ? undefined : blockDecimalKey} className={`form-control${milestoneFormErrors.grossAmount ? ' is-invalid' : ''}`} name="grossAmount" value={milestoneFormData.grossAmount} disabled={isPerson || milestoneLockedForEditing} onChange={isPerson || milestoneLockedForEditing ? undefined : handleMilestoneInputChange} aria-invalid={!!milestoneFormErrors.grossAmount} aria-describedby="milestone-gross-amount-help milestone-gross-amount-error" />
                            {!isPerson && <small id="milestone-gross-amount-help" className="text-muted">{t('milestones.form.stageTotalHelp')}</small>}
                            {milestoneFormErrors.grossAmount && <div id="milestone-gross-amount-error" className="invalid-feedback">{milestoneFormErrors.grossAmount}</div>}
                          </div>}
                          <div className="col-md-6 mb-3">
                            <label className="form-label" htmlFor="milestone-after-rot">{t(selectedContract.pricingMode === 'bottom_up' ? 'milestones.pricing.stageGross' : 'milestones.form.stageAfterRot')}{!isPerson && ' *'}</label>
                            <input id="milestone-after-rot" type={isPerson ? 'text' : 'number'} step="1" inputMode="numeric" onKeyDown={isPerson ? undefined : blockDecimalKey} className={`form-control${milestoneFormErrors.amount ? ' is-invalid' : ''}`} name="amount" value={milestoneFormData.amount} disabled={isPerson || milestoneLockedForEditing} onChange={isPerson || milestoneLockedForEditing ? undefined : handleMilestoneInputChange} aria-invalid={!!milestoneFormErrors.amount} aria-describedby={milestoneFormErrors.amount ? 'milestone-after-rot-error' : undefined} />
                            {milestoneFormErrors.amount && <div id="milestone-after-rot-error" className="invalid-feedback">{milestoneFormErrors.amount}</div>}
                          </div>
                          {selectedContract.pricingMode === 'bottom_up' && <div className="col-md-6 mb-3">
                            <label className="form-label" htmlFor="milestone-rot">{t('milestones.pricing.stageRot')}</label>
                            <input id="milestone-rot" name="rotAmount" aria-invalid={!!milestoneFormErrors.rotAmount} aria-describedby={milestoneFormErrors.rotAmount ? 'milestone-rot-error' : undefined} type="number" inputMode="numeric" step="1" onKeyDown={blockDecimalKey} className={`form-control${milestoneFormErrors.rotAmount ? ' is-invalid' : ''}`} value={milestoneFormData.rotAmount} disabled={isPerson || milestoneLockedForEditing} onChange={handleMilestoneInputChange} />
                            {milestoneFormErrors.rotAmount && <div id="milestone-rot-error" className="invalid-feedback">{milestoneFormErrors.rotAmount}</div>}
                            <p className="small mt-2 mb-0">{t('contracts.form.rot.afterRot')}: {formatNumber((Math.round(Number(milestoneFormData.amount) * 100) - Math.round(Number(milestoneFormData.rotAmount) * 100)) / 100)} kr</p>
                          </div>}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">{t('milestones.columns.description')}</label>
                          <input type="text" className="form-control" name="description" value={milestoneFormData.description} disabled={isPerson || milestoneLockedForEditing} onChange={isPerson || milestoneLockedForEditing ? undefined : handleMilestoneInputChange} />
                          {!isPerson && <small className="text-muted">{t('milestones.form.descriptionHelp')}</small>}
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">{t('milestones.columns.endDate')}{!isPerson && <span className="text-muted"> ({t('common.optional', 'valfritt')})</span>}</label>
                            {!isPerson && <p className="text-muted small mb-2">{t('milestones.form.endDateHelp')}</p>}
                            <input type={isPerson ? 'text' : 'date'} className="form-control" name="endDate" value={isPerson ? (milestoneFormData.endDate || '–') : milestoneFormData.endDate} disabled={isPerson || milestoneLockedForEditing} onChange={isPerson || milestoneLockedForEditing ? undefined : handleMilestoneInputChange} />
                          </div>
                        </div>
                        {!isCreatingMilestone && (
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">{t('milestones.columns.companyAttestation')}</label>
                              <div>{getMilestoneAttestationBadge(selectedMilestone?.companyAttestation || 'not_attested')}</div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">{t('milestones.columns.personAttestation')}</label>
                              <div>{getMilestoneAttestationBadge(selectedMilestone?.personAttestation || 'not_attested')}</div>
                            </div>
                          </div>
                        )}
                      </form>
                    </Modal>
                  )}

                  {/* Small confirmation shown on top when closing a milestone
                      form / wizard with unsaved edits. */}
                  {pendingCloseTarget && (
                    <Modal
                      zIndex={1060}
                      title={t('milestones.form.unsavedTitle')}
                      onClose={() => setPendingCloseTarget(null)}
                      footer={
                        <>
                          <Button type="button" variant="secondary" onClick={() => setPendingCloseTarget(null)}>
                            {t('milestones.form.unsavedKeep')}
                          </Button>
                          <Button type="button" variant="danger" onClick={confirmDiscardMilestoneClose}>
                            {t('milestones.form.unsavedDiscard')}
                          </Button>
                        </>
                      }
                    >
                      <p className="mb-0">{t('milestones.form.unsavedConfirm')}</p>
                    </Modal>
                  )}

                  {/* Small confirmation shown on top when deleting a milestone. */}
                  {showDeleteMilestoneConfirm && (
                    <Modal
                      zIndex={1060}
                      title={t('milestones.form.deleteTitle')}
                      onClose={() => setShowDeleteMilestoneConfirm(false)}
                      footer={
                        <>
                          <Button type="button" variant="secondary" onClick={() => setShowDeleteMilestoneConfirm(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button type="button" variant="danger" onClick={performDeleteMilestone}>
                            <i className="feather-trash-2 me-2"></i>
                            {t('projects.deleteProject')}
                          </Button>
                        </>
                      }
                    >
                      <p className="mb-3">{t('milestones.form.deleteConfirm')}</p>
                      {selectedContract.pricingMode === 'bottom_up' && <MilestoneAmountSummary
                        {...milestoneFormAmounts(milestones, selectedMilestone?.id, 0, 0, Number(selectedContract.contractAmount), 'bottom_up', 0)}
                        previousTotal={Number(selectedContract.contractAmount)}
                      />}
                    </Modal>
                  )}


                {/* Historik — full width */}
                <div id="contract-section-history" style={{ scrollMarginTop: '90px' }}></div>
                <ContractHistory
                  entries={contractHistory}
                  contractLabel={`${t('contracts.columns.contractNumber')} #${selectedContract.contractNumber}`}
                  className="mb-4"
                />

                {legacyContractSections && (<>
                {/* Kontrakt + Uppdragsbeskrivning side by side */}
                <div id="contract-section-edit" style={{ scrollMarginTop: '90px' }}></div>
                {/* An ÄTA is changed with "Redigera" under the contract list (869fmxfb7). */}
                {(isStageVariation || isPriceAdjustmentVariation) && !isPerson ? null : <div className="row">
                  <div className="col-12 col-lg-6">
                  <Card className="mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0"><i className="feather-file-text" aria-hidden="true"></i>{isPerson ? t('personProject.contractTitle') : t('contracts.form.editTitle')}</h5>
                    </div>
                    <form onSubmit={(e: React.FormEvent) => e.preventDefault()}>
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">{t('contracts.columns.companyAttestation')}</label>
                            <div>{getAttestationBadge(selectedContract.companyAttestation || 'not_attested')}</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">{t('contracts.columns.personAttestation')}</label>
                            <div>{getAttestationBadge(selectedContract.personAttestation || 'not_attested')}</div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">{t('contracts.columns.contractNumber')}</label>
                          <input type="text" className="form-control" value={selectedContract.contractNumber} disabled />
                          {!isPerson && <small className="text-muted">{t('contracts.form.contractNumberHelp')}</small>}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">{t('contracts.form.contractTypeLabel')}</label>
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <i className="feather-lock text-muted"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control bg-light fw-semibold"
                              value={t(`contracts.types.${isStageVariation ? 'variation' : selectedContract.contractType || 'fixed'}`)}
                              readOnly
                              disabled
                            />
                          </div>
                        </div>
                        {selectedContract.contractType === 'variation' && selectedContract.variationNumber != null && (
                          <div className="mb-3">
                            <label className="form-label">{t('contracts.columns.variationNumber')}</label>
                            <input type="text" className="form-control" value={`#${selectedContract.variationNumber}`} disabled />
                          </div>
                        )}
                        {selectedContract.contractType === 'variation' && selectedContract.parentContractId && (() => {
                          const parent = contracts.find(c => c.id === selectedContract.parentContractId);
                          return (
                            <div className="mb-3">
                              <label className="form-label">{t('contracts.preview.parentContractLabel')}</label>
                              <input
                                type="text"
                                className="form-control"
                                value={parent
                                  ? `#${parent.contractNumber} — ${contractTitle(parent) || '—'} (${parent.contractType === 'variation'
                                    ? `${t('contracts.types.variation')}${parent.variationNumber != null ? ` #${parent.variationNumber}` : ''}`
                                    : t('contracts.types.fixed')})`
                                  : '—'}
                                disabled
                              />
                            </div>
                          );
                        })()}
                        <div className="mb-3">
                          <label className="form-label">{t(isStageVariation ? 'stageVariation.grossDelta' : 'contracts.form.contractAmountLabel')}</label>
                          {isPriceAdjustmentVariation && !isPerson ? (
                            <>
                              <div className="input-group">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  data-testid="price-adjustment-amount"
                                  className={`form-control${contractFormErrors.contractAmount ? ' is-invalid' : ''}`}
                                  name="contractAmount"
                                  value={contractFormData.contractAmount}
                                  disabled={contractLockedForEditing || isSaving}
                                  onChange={handleContractInputChange}
                                  onBlur={() => { void handlePriceAdjustmentAmountBlur(); }}
                                />
                                <span className="input-group-text">kr</span>
                              </div>
                              <small className="text-muted d-block mt-1">
                                {t('projectView.ata.amountHelp')}
                              </small>
                              {contractFormErrors.contractAmount && (
                                <div className="invalid-feedback d-block" role="alert">
                                  {contractFormErrors.contractAmount}
                                </div>
                              )}
                            </>
                          ) : (
                            <input type="text" className="form-control bg-light" value={formatNumber(isStageVariation ? (selectedReplacementSettlement?.grossDeltaOre ?? 0) / 100 : parseFloat(contractFormData.contractAmount?.replace(/[^0-9.-]/g, '') || '0'))} disabled />
                          )}
                        </div>
                        {selectedContract.pricingMode === 'bottom_up' && <p className="small text-muted">{t('milestones.pricing.bottomUpHelp')}</p>}
                        {!isStageVariation && <div className="mb-3" data-testid="contract-rot-display">
                          <label className="form-label d-block">{t('contracts.form.rot.title')}</label>
                          {milestoneRotAmount === 0 ? (
                            <div className="alert alert-info py-2 small" data-testid="contract-no-rot-info">
                              <strong>{t('contracts.form.rot.noRotTitle')}</strong>
                              {!isPerson && !isCompanySigned && (
                                <>{' '}{t('contracts.form.rot.noRotHelp')}</>
                              )}
                            </div>
                          ) : (
                            <div className="border rounded p-2 mb-3 small" data-testid="contract-rot-summary">
                              <div className="d-flex justify-content-between gap-2">
                                <span>{t('contracts.form.rot.planned')}</span>
                                <strong>{formatNumber(milestoneRotAmount)} kr</strong>
                              </div>
                              <div className="d-flex justify-content-between gap-2">
                                <span>{t('contracts.form.rot.afterRot')}</span>
                                <strong>{formatNumber(milestoneAfterRotAmount)} kr</strong>
                              </div>
                              <div className="text-muted mt-1">
                                {t('contracts.form.rot.summary')}
                              </div>
                            </div>
                          )}
                        </div>
                        }
                        <div className="mb-3">
                          <label className="form-label">{t('contracts.form.paidAmountLabel')}</label>
                          <input type="text" className="form-control" value={formatNumber(selectedContract.paidAmount)} disabled />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">{t('contracts.form.remainingAmountLabel')}</label>
                          <input type="text" className="form-control" value={formatNumber(selectedContract.remainingAmount)} disabled />
                        </div>
                      </div>
                    </form>
                  </Card>
                  {/* Attachments Section */}
                  <Card className="mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0"><i className="feather-paperclip" aria-hidden="true"></i>{t('contracts.attachments.contractFilesTitle')}</h5>
                      {!isPerson && (
                        <div className="d-flex gap-2">
                          <input
                            ref={attachmentInputRef}
                            type="file"
                            multiple
                            accept={ATTACHMENT_ACCEPT}
                            className="d-none"
                            onChange={(e) => handleAttachmentUpload(e.target.files)}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={openLibraryPicker}
                            disabled={isCompanySigned || isAttachmentLocked}
                            title={isAttachmentLocked ? t('contracts.attachments.lockedSigned') : undefined}
                          >
                            <i className="feather-folder me-1"></i>
                            {t('contracts.attachments.fromLibrary')}
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => attachmentInputRef.current?.click()}
                            disabled={isUploadingAttachment || isCompanySigned || isAttachmentLocked}
                            title={isAttachmentLocked ? t('contracts.attachments.lockedSigned') : undefined}
                          >
                            {isUploadingAttachment ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                {t('contracts.attachments.uploading')}
                              </>
                            ) : (
                              <>
                                <i className="feather-upload me-1"></i>
                                {t('contracts.attachments.upload')}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="card-body">
                      {!isPerson && (
                        <p className="text-muted small mb-3">
                          <i className="feather-info me-1"></i>
                          {t('contracts.attachments.formatsHint', {
                            formats: ATTACHMENT_FORMATS_LABEL,
                            max: MAX_ATTACHMENT_SIZE_MB,
                          })}
                        </p>
                      )}
                      {contractAttachments.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="feather-paperclip fs-1 d-block mb-2"></i>
                          <p className="mb-0">{t('contracts.attachments.noAttachments')}</p>
                        </div>
                      ) : (
                        <div>
                          {contractAttachments.map((doc) => (
                            <div key={doc.id} className="border rounded p-3 mb-3">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <button
                                    type="button"
                                    className="btn btn-link fw-bold text-start text-truncate p-0 mb-1 text-decoration-none"
                                    onClick={() => setAttachmentDetailsTarget(doc)}
                                    title={t('contracts.attachments.viewDetails')}
                                  >
                                    <i className="feather-file me-2"></i>
                                    {doc.originalName}
                                  </button>
                                  <div className="text-muted small">
                                    <span className="me-3">
                                      <i className="feather-user me-1"></i>
                                      {t('contracts.attachments.uploadedBy')} {doc.uploadedByName}
                                    </span>
                                    <span className="me-3">
                                      <i className="feather-calendar me-1"></i>
                                      {new Date(doc.createdAt).toLocaleDateString('sv-SE')}
                                    </span>
                                    <span>
                                      <i className="feather-hard-drive me-1"></i>
                                      {formatAttachmentSize(doc.size)}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex gap-1 flex-shrink-0 ms-2">
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handlePreview(doc)}
                                    title={t('contracts.attachments.preview')}
                                    aria-label={t('contracts.attachments.preview')}
                                  >
                                    <i className="feather-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => documentApi.downloadFile(doc.id, doc.originalName)}
                                    title={t('contracts.attachments.download')}
                                  >
                                    <i className="feather-download"></i>
                                  </button>
                                  {!isPerson && (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoveAttachment(doc)}
                                      disabled={isAttachmentLocked}
                                      title={
                                        isAttachmentLocked
                                          ? t('contracts.attachments.lockedSigned')
                                          : t('contracts.attachments.remove')
                                      }
                                    >
                                      <i className="feather-x"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                  </div>
                  <div className="col-12 col-lg-6">
                  {/* Assignment Description Section */}
                  <Card className="mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0"><i className="feather-align-left" aria-hidden="true"></i>{t('contracts.descriptions.title')}</h5>
                    </div>
                    <div className="card-body">
                      <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 position-relative">
                        {t('contracts.descriptions.contractNameTitle')}
                        <span className="etappio-info-tip">
                          <i className="feather-info" tabIndex={0} role="button" aria-label={t('contracts.descriptions.moreInfoAria', 'Mer information')}></i>
                          <span className="etappio-info-tip__bubble" role="tooltip">
                            {t('contracts.descriptions.tooltips.contractName')}
                            <em>{t('contracts.descriptions.tooltips.contractNameExample')}</em>
                            {t('contracts.descriptions.tooltips.contractNameSuffix')}
                          </span>
                        </span>
                      </h6>
                      {isPerson ? (
                        <div className="border rounded p-3 mb-4">
                          {contractName ? contractName : <span className="text-muted">—</span>}
                        </div>
                      ) : (
                        <div className="mb-4">
                          <input
                            type="text"
                            id="contractNameInput"
                            className={`form-control ${contractNameError ? 'is-invalid' : ''}`}
                            value={contractName}
                            onChange={(e) => {
                              setContractName(e.target.value.slice(0, 256));
                              descDirtyRef.current.add('contractName');
                              if (contractNameError) setContractNameError('');
                            }}
                            onBlur={() => autoSaveDescriptionField('contractName')}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                autoSaveDescriptionField('contractName');
                              }
                            }}
                            maxLength={256}
                            placeholder={t('contracts.descriptions.contractNamePlaceholder')}
                            disabled={descEditingDisabled}
                          />
                          {contractNameError && (
                            <div className="invalid-feedback d-block">{contractNameError}</div>
                          )}
                          <small className="text-muted">{contractName.length}/256</small>
                        </div>
                      )}

                      <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 position-relative">
                        {t('contracts.descriptions.assignmentTitle')}
                        <span className="etappio-info-tip">
                          <i className="feather-info" tabIndex={0} role="button" aria-label={t('contracts.descriptions.moreInfoAria', 'Mer information')}></i>
                          <span className="etappio-info-tip__bubble" role="tooltip">
                            <div className="mb-2">{t('contracts.descriptions.tooltips.assignmentIntro')}</div>
                            <div className="fw-bold mb-1">{t('contracts.descriptions.tooltips.assignmentChooseHeading')}</div>
                            <div className="mb-1"><strong>{t('contracts.descriptions.tooltips.assignmentOption1Label')}</strong>{t('contracts.descriptions.tooltips.assignmentOption1Text')}</div>
                            <div className="mb-2"><strong>{t('contracts.descriptions.tooltips.assignmentOption2Label')}</strong>{t('contracts.descriptions.tooltips.assignmentOption2Text')}</div>
                            <hr />
                            <div><strong className="etappio-info-tip__important">{t('contracts.descriptions.tooltips.assignmentImportantLabel')}</strong>{t('contracts.descriptions.tooltips.assignmentImportantText')}</div>
                          </span>
                        </span>
                      </h6>
                      {isPerson ? (
                        <div className="border rounded p-3" style={{ minHeight: '100px' }}>
                          {assignmentDescription && assignmentDescription !== '<p><br></p>' && (
                            <div dangerouslySetInnerHTML={{ __html: assignmentDescription }} />
                          )}
                          {referToAttachments && (
                            <p className="mb-0 mt-2">{t('contracts.descriptions.referToAttachmentsText')}</p>
                          )}
                          {!(assignmentDescription && assignmentDescription !== '<p><br></p>') && !referToAttachments && (
                            <p className="text-muted mb-0">—</p>
                          )}
                        </div>
                      ) : (
                        <ReactQuill
                          theme="snow"
                          className="contract-description-quill"
                          value={assignmentDescription}
                          onChange={(value, _delta, source) => {
                            setAssignmentDescription(value);
                            if (source === 'user') descDirtyRef.current.add('assignmentDescription');
                          }}
                          onBlur={() => autoSaveDescriptionField('assignmentDescription')}
                          modules={descriptionQuillModules}
                          formats={descriptionQuillFormats}
                          readOnly={descEditingDisabled}
                        />
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                          {assignmentVersions.length === 0 ? (
                            <small className="text-muted">{t('contracts.descriptions.noVersions')}</small>
                          ) : (
                            <small className="text-muted">
                              {t('contracts.descriptions.version')} {assignmentVersions[assignmentVersions.length - 1].version} | {t('contracts.descriptions.lastSavedBy')} {assignmentVersions[assignmentVersions.length - 1].savedByName} | {new Date(assignmentVersions[assignmentVersions.length - 1].savedAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </small>
                          )}
                        </div>
                      </div>

                      {!isPerson && (
                        <div className="mt-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input contract-refer-checkbox"
                              id="referToAttachments"
                              checked={referToAttachments}
                              disabled={descEditingDisabled || contractAttachments.length === 0}
                              onChange={(e) => saveReferToAttachments(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="referToAttachments">
                              {t('contracts.descriptions.referToAttachmentsLabel')}
                            </label>
                          </div>
                          {contractAttachments.length === 0 && !isSignedContract && (
                            <div className="alert alert-warning py-2 px-3 small mt-2 mb-0" role="note">
                              {t('contracts.descriptions.referToAttachmentsNoFiles')}
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ height: '20px' }}></div>

                      <h6 className="fw-bold mt-3 d-flex align-items-center gap-2 position-relative">
                        {t('contracts.descriptions.otherTitle')}
                        <span className="text-primary fw-normal">{t('contracts.descriptions.otherOptional')}</span>
                        <span className="etappio-info-tip">
                          <i className="feather-info" tabIndex={0} role="button" aria-label={t('contracts.descriptions.moreInfoAria', 'Mer information')}></i>
                          <span className="etappio-info-tip__bubble" role="tooltip">
                            {t('contracts.descriptions.tooltips.other')}
                          </span>
                        </span>
                      </h6>
                      {isPerson ? (
                        <div className="border rounded p-3" style={{ minHeight: '100px' }}>
                          {(otherDescription && otherDescription !== '<p><br></p>') || variationOtherDescription ? (
                            <div dangerouslySetInnerHTML={{ __html: [otherDescription, variationOtherDescription].filter(Boolean).join('\n') }} />
                          ) : (
                            <p className="text-muted mb-0">—</p>
                          )}
                        </div>
                      ) : (
                        <ReactQuill
                          theme="snow"
                          className="contract-description-quill"
                          value={otherDescription}
                          onChange={(value, _delta, source) => {
                            setOtherDescription(value);
                            if (source === 'user') descDirtyRef.current.add('other');
                          }}
                          onBlur={() => autoSaveDescriptionField('other')}
                          modules={descriptionQuillModules}
                          formats={descriptionQuillFormats}
                          readOnly={descEditingDisabled}
                        />
                      )}
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                          {otherVersions.length === 0 ? (
                            <small className="text-muted">{t('contracts.descriptions.noVersions')}</small>
                          ) : (
                            <small className="text-muted">
                              {t('contracts.descriptions.version')} {otherVersions[otherVersions.length - 1].version} | {t('contracts.descriptions.lastSavedBy')} {otherVersions[otherVersions.length - 1].savedByName} | {new Date(otherVersions[otherVersions.length - 1].savedAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </small>
                          )}
                        </div>
                      </div>

                      {!isPerson && (
                        <>
                          <div style={{ height: '45px' }}></div>
                          {/* Save button HIDDEN — replaced by per-field autosave on blur
                              (feat/kontrakt_andringar). Kept commented, not deleted:
                            <div className="d-flex justify-content-end mt-3">
                              <button className="btn btn-primary" onClick={handleSaveDescriptions} disabled={isSavingDescription || isCompanySigned}>
                                <i className="feather-save me-2"></i>{t('common.save')}
                              </button>
                            </div>
                          */}
                          {/* Autosave status indicator */}
                          <div
                            className="d-flex justify-content-end mt-3 small"
                            aria-live="polite"
                            aria-atomic="true"
                          >
                            {descAutoSaveStatus === 'saving' && (
                              <span className="text-muted">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('contracts.descriptions.autosave.saving')}
                              </span>
                            )}
                            {descAutoSaveStatus === 'saved' && !descHasUnsavedText && (
                              <span className="text-success">
                                <i className="feather-check me-1"></i>
                                {t('contracts.descriptions.autosave.savedAt', { time: descSavedAtLabel })}
                              </span>
                            )}
                            {(descAutoSaveStatus === 'saved' || descAutoSaveStatus === 'idle') && descHasUnsavedText && (
                              <span className="text-muted">
                                {t('contracts.descriptions.autosave.unsaved')}
                              </span>
                            )}
                            {descAutoSaveStatus === 'error' && descAutoSaveError && (
                              <span className="text-danger">
                                <i className="feather-alert-triangle me-1"></i>
                                {descAutoSaveError.message}
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                                  onClick={() => descAutoSaveError?.retry()}
                                >
                                  {t('contracts.descriptions.autosave.retry')}
                                </button>
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                  </div>
                </div>}
                </>)}

                  {/* Remove Attachment Confirmation Modal */}
                  {removeAttachmentTarget && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setRemoveAttachmentTarget(null)}>
                      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                          <div className="modal-header border-0 pb-0">
                            <h5 className="modal-title">
                              <i className="feather-alert-triangle text-warning me-2"></i>
                              {t('contracts.attachments.removeTitle')}
                            </h5>
                            <button type="button" className="btn-close" onClick={() => setRemoveAttachmentTarget(null)}></button>
                          </div>
                          <div className="modal-body">
                            <p className="mb-2">{t('contracts.attachments.removeConfirm')}</p>
                            <div className="bg-light rounded p-3">
                              <div className="fw-bold">
                                <i className="feather-file me-2"></i>
                                {removeAttachmentTarget.originalName}
                              </div>
                              <small className="text-muted">
                                {t('contracts.attachments.uploadedBy')} {removeAttachmentTarget.uploadedByName} — {new Date(removeAttachmentTarget.createdAt).toLocaleDateString('sv-SE')}
                              </small>
                            </div>
                            <p className="text-muted small mt-3 mb-0">
                              <i className="feather-info me-1"></i>
                              {t('contracts.attachments.removeNote')}
                            </p>
                          </div>
                          <div className="modal-footer border-0 pt-0">
                            <button className="btn btn-light" onClick={() => setRemoveAttachmentTarget(null)}>
                              {t('common.cancel')}
                            </button>
                            <button className="btn btn-danger" onClick={confirmRemoveAttachment}>
                              <i className="feather-trash-2 me-1"></i>
                              {t('contracts.attachments.removeConfirmButton')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Library Picker Modal */}
                  {showLibraryPicker && (
                    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setShowLibraryPicker(false)}>
                      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">
                              <i className="feather-folder me-2"></i>
                              {t('contracts.attachments.libraryTitle')}
                            </h5>
                            <button type="button" className="btn-close" onClick={() => setShowLibraryPicker(false)}></button>
                          </div>
                          <div className="modal-body">
                            <input
                              type="text"
                              className="form-control mb-3"
                              placeholder={t('contracts.attachments.librarySearch')}
                              value={librarySearch}
                              onChange={e => setLibrarySearch(e.target.value)}
                            />
                            {libraryDocs.filter(d =>
                              !librarySearch || d.originalName.toLowerCase().includes(librarySearch.toLowerCase())
                            ).length === 0 ? (
                              <div className="text-center py-4 text-muted">
                                <i className="feather-inbox fs-1 d-block mb-2"></i>
                                <p className="mb-0">{t('contracts.attachments.libraryEmpty')}</p>
                              </div>
                            ) : (
                              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {libraryDocs
                                  .filter(d => !librarySearch || d.originalName.toLowerCase().includes(librarySearch.toLowerCase()))
                                  .map(doc => (
                                    <div key={doc.id} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-truncate">
                                          <i className="feather-file me-2"></i>
                                          {doc.originalName}
                                        </div>
                                        <small className="text-muted">
                                          {formatAttachmentSize(doc.size)} — {doc.uploadedByName} — {new Date(doc.createdAt).toLocaleDateString('sv-SE')}
                                        </small>
                                      </div>
                                      <button
                                        className="btn btn-sm btn-primary flex-shrink-0 ms-2"
                                        onClick={() => handleLinkFromLibrary(doc)}
                                      >
                                        <i className="feather-plus me-1"></i>
                                        {t('contracts.attachments.addAttachment')}
                                      </button>
                                    </div>
                                  ))
                                }
                              </div>
                            )}
                          </div>
                          <div className="modal-footer">
                            <button className="btn btn-light" onClick={() => setShowLibraryPicker(false)}>
                              {t('common.close')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </>
            )}
            </div>
            {showCustomerPanel && (
              <ProjectCustomerPanel
                onInvite={() => openAddCustomer('invite')}
                onPick={() => openAddCustomer('pick')}
              />
            )}
            </div>
          </div>
        )}

        {/* The company's read-only preview of a contract the wizards cannot open */}
        {companyPreviewContract && (
          <ContractPreviewModal
            title={companyPreviewContract.contractType === 'variation' || companyPreviewContract.stageVariation
              ? agreementLabel(companyPreviewContract, contracts, t)
              : t('contracts.wizard.view.title', { number: companyPreviewContract.contractNumber })}
            preview={storedContractPreview(
              companyPreviewContract,
              'company',
              contractWizardCompany,
              { name: companyPreviewCustomer?.name, email: companyPreviewCustomer?.email },
            )}
            onClose={() => setCompanyPreviewContractId(null)}
          />
        )}

        {/* The person's review and signing (Granska och signera avtal) */}
        {personReviewOpen && selectedContract && personReviewPreview && (
          <PersonContractReview
            key={personReviewAsPreview ? 'preview' : 'review'}
            startInPreview={personReviewAsPreview}
            personSigned={Boolean(selectedContract.personSigned)}
            preview={personReviewPreview}
            companyName={previewCompanyName}
            companySignedAt={
              selectedContract.companySigned
                ? selectedContract.companySignedAt ?? selectedContract.signingSentAt ?? null
                : null
            }
            amountToPay={personReviewAmountToPay}
            providerName={signingProviderName}
            isVariation={personReviewPreview.contractType === 'variation'}
            resolvesObjection={reviewObjection}
            messages={personReviewMessages}
            signBlockedReason={personReviewBlockedReason}
            objectionBlocked={isProjectClosedForSigning}
            signing={isOpeningSigning}
            signError={signingError}
            onSign={() => {
              void startExternalSigning(() =>
                contractApi.signingLink(selectedContract.id, signingStartDecisions)
              );
            }}
            onSendObjection={sendPersonObjection}
            onClose={() => {
              setShowPreview(false);
              setSigningError(null);
            }}
          />
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <ProjectParticipantsTab
            participants={participants}
            customerSlot={!isPerson && customerState === 'missing' ? (
              // The same "Lägg till beställare" as on the Kontrakt tab (ClickUp 869fmxjgt).
              <ProjectCustomerPanel onInvite={() => openAddCustomer('invite')} onPick={() => openAddCustomer('pick')} />
            ) : undefined}
          />
        )}


        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <ProjectDocumentsTab
            projectId={id!}
            documents={documents}
            folderTree={folderTree}
            canUpload={userCanUpload}
            canRename={userCanRename}
            canDelete={!!userCanDelete}
            isAdmin={!!user?.isAdmin}
            onReload={loadDocuments}
            contractFolders={contractFolderHierarchy(contracts)}
            onShowFileInfo={setAttachmentDetailsTarget}
            sourceHref={source => subjectPath({
              threadType: source.threadType,
              projectId: id!,
              milestoneId: source.threadType === 'milestone' ? source.threadId : null,
              contractId: source.threadType === 'contract' ? source.threadId : null,
            }, isPerson ? 'person' : 'company')}
          />
        )}

        {/* Transactions Tab */}
        {/* Tidslinje Tab */}
        {activeTab === 'timeline' && (
          <div className="tab-pane fade show active">
            {/* Projektstatus: the whole project at a glance — status, next steps
                and totals across every contract. Styling in App.css (.project-status). */}
            {project && (() => {
              const milestoneConflictStatus = isConflictDeliveryStatus(projectDisplayStatus)
                ? projectDisplayStatus
                : null;
              const isOnHold = project.status === 'on_hold' && !milestoneConflictStatus;
              const inactivityViewerSide = isPerson ? 'person' as const : canUseProjectMarketplace ? 'company' as const : null;
              const isConflict = project.status === 'conflict' || projectDisplayStatus === 'conflict';
              const isDispute = project.status === 'dispute' || projectDisplayStatus === 'external_dispute';
              const isDisputed = projectDisplayStatus === 'disputed';
              const isUnderResolution = projectDisplayStatus === 'under_resolution';
              const isCompleted = project.status === 'completed';
              const isException = isOnHold || isConflict || isDispute || isDisputed || isUnderResolution;
              const viewerSide = isPerson ? 'person' : 'company';
              const overview = projectStatusOverview({
                frames: currentProjectContracts,
                contracts,
                milestonesByContract: contractMilestonesMap,
                fundingContracts: projectFunding?.contracts,
                projectAccount: Boolean(projectFunding?.account),
                side: viewerSide,
                projectFunded: isProjectFunded,
                projectClosed: isCompleted || isDispute
              });
              // A stage with an open objection has one notice: its objection (ClickUp 869fj6n8x).
              const objectionMilestoneIds = objectionNoticeMilestoneIds(overview.rows, viewerSide);
              const pendingPaymentTotal = transactions
                .filter((txn: any) => txn.type === 'payment_pending')
                .reduce((sum: number, txn: any) => sum + (Number(txn.amountSek) || Math.round((Number(txn.amountOre) || 0) / 100)), 0);
              const menuItems: ProjectStatusMenuItem[] = !isException && !isCompleted && project.status !== 'not_started'
                ? [{ key: 'on-hold', label: t('projectView.overview.markOnHold'), onClick: () => setShowStatusChangeModal('on_hold') }]
                : [];
              const openPayment = (contract: Contract) => {
                const params = new URLSearchParams(searchParams);
                params.set('paymentPicker', 'open');
                params.set('paymentContractId', contract.id);
                navigate(`?${params.toString()}`);
              };
              return (
                <ProjectStatusOverview
                  showFinancials={!projectFunding?.account}
                  rows={overview.rows}
                  totals={overview.totals}
                  pendingPayment={pendingPaymentTotal}
                  side={viewerSide}
                  statusBadge={
                    <ProjectWorkflowStatusBadge
                      project={project}
                      milestones={displayProjectMilestones}
                      steps={projectStatusSteps}
                      testId="project-timeline-workflow-status"
                    />
                  }
                  notices={<>
                      {/* Objection, remedy, conflict and dispute already show in the status
                          badge, the sentence and "Nästa steg" (with the stage's own notice),
                          so only an on-hold project gets a notice: it needs an action
                          (ClickUp 869fjrytd). */}
                      {isOnHold && (
                        <Notice
                          level="action"
                          icon="pause-circle"
                          className="mb-3"
                          testId="project-on-hold-notice"
                          title={t('personHome.projectTimeline.onHoldInfo', 'Projektet är vilande')}
                          action={{ label: t('projectView.overview.resume'), onClick: () => setShowStatusChangeModal('resume') }}
                        />
                      )}

                      {isCompleted && (
                        <div className="alert alert-success py-3 px-3 mb-3" data-testid="project-completed-summary">
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <i className="feather-check-circle" style={{ fontSize: 20 }}></i>
                                <div className="fw-bold">Projektet och ekonomin är avslutade</div>
                              </div>
                              <div className="small">
                                {project.subStatus === 'closed_buyer_silence'
                                  ? 'Projektet avslutades efter uteblivet svar från beställaren. Etappbeloppet betalades ut till hantverkaren och kapitalet för återstående, ej utförda etapper återbetalades till beställaren.'
                                  : project.subStatus === 'closed_seller_silence'
                                    ? 'Projektet avslutades efter uteblivet svar från hantverkaren. Hela etappbeloppet återbetalades.'
                                    : t('projectFunding.closedNoMoreDeposits')}
                              </div>
                              {completedSilenceCase && (
                                <div className="small mt-1">
                                  Slutrapporten redovisar vilka medel som gick till respektive part och varför.
                                </div>
                              )}
                            </div>
                            {completedSilenceCase && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                disabled={isDownloadingFinalReport}
                                onClick={() => handleDownloadInactivityFinalReport(completedSilenceCase)}
                              >
                                <i className="feather-download me-1"></i>
                                {isDownloadingFinalReport ? 'Hämtar slutrapport…' : 'Ladda ner slutrapport (PDF)'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Silence cases the viewer waits on stay with the status; the
                          ones to act on go under "Nästa steg" (ClickUp 869fjrytd). */}
                      <ProjectInactivitySummary
                        placement="waiting"
                        contracts={contracts}
                        milestones={currentProjectMilestones}
                        cases={inactivityCases}
                        viewerSide={inactivityViewerSide}
                        excludeMilestoneIds={objectionMilestoneIds}
                        onResolved={handleInactivityResolved}
                        onOpenMilestone={milestone => { void openAttestationModal(milestone); }}
                      />
                  </>}
                  renderContractActions={contractIds => {
                    const contractId = contractIds.find(candidate => inactivityCases.some(lifecycleCase =>
                      lifecycleCase.contractId === candidate &&
                      ['seller_silence', 'buyer_silence'].includes(lifecycleCase.caseType) &&
                      ['active', 'settling'].includes(lifecycleCase.status) &&
                      !objectionMilestoneIds.has(lifecycleCase.milestoneId) &&
                      inactivityViewerActs(lifecycleCase, inactivityViewerSide)));
                    return contractId ? (
                      <ProjectInactivitySummary
                        placement="action"
                        contractId={contractId}
                        contracts={contracts}
                        milestones={currentProjectMilestones}
                        cases={inactivityCases}
                        viewerSide={inactivityViewerSide}
                        excludeMilestoneIds={objectionMilestoneIds}
                        onResolved={handleInactivityResolved}
                        onOpenMilestone={milestone => { void openAttestationModal(milestone); }}
                      />
                    ) : null;
                  }}
                  menuItems={menuItems}
                  formatCurrency={formatCurrency}
                  onOpenMilestone={(milestone, mode) => { void openAttestationModal(milestone, mode); }}
                  objectionTurn={objectionTurn}
                  onAttestRemedied={milestone => { void handleOpenRemediedMilestoneAttestation(milestone); }}
                  preparingMilestoneId={progressingMilestoneId}
                  onSign={contract => { void handleOpenPreview('sign', contract); }}
                  signingDisabled={isOpeningSigning}
                  onPay={openPayment}
                  onShowContract={root => document.querySelector(`[data-contract-group="${root.id}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              );
            })()}

            {project.status !== 'completed' &&
              !projectFunding?.account &&
              projectFunding?.needsTopUp &&
              projectFunding.remainingFundingNeedOre > 0 && (
              <div
                className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
                data-testid="project-timeline-funding-shortage"
              >
                <div>
                  <div className="fw-semibold">
                    <i className="feather-alert-circle me-2"></i>
                    Projektet saknar {formatCurrency(projectFunding.remainingFundingNeedOre / 100)}
                  </div>
                  <div className="small">
                    {t('projectFunding.missingReason', { reason: projectFunding.fundingReason })}
                  </div>
                </div>
                {isPerson && projectFunding.contracts.some(contract => contract.canPay || contract.canResumePayment) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    onClick={() => navigate('?paymentPicker=open')}
                  >
                    {t('projectFunding.selectContractToPay')}
                  </button>
                )}
              </div>
            )}

            {/* Per-contract timelines — one timeline + actions + totals per contract */}
            {currentProjectContracts.length > 0 && (
              <div id="contract-timelines">
              {/* Same headline as Projektstatus (App.css .project-status-header). */}
              <section className="card project-status mb-4" aria-labelledby="contract-timelines-title">
                <header className="project-status-header">
                  <h2 id="contract-timelines-title" className="project-status-title">
                    <i className="feather-file-text" aria-hidden="true"></i>
                    {t('projectView.contractTimelines.title', 'Kontraktstidslinjer')}
                  </h2>
                </header>
                <div className="project-section-body contract-frames">
                  {currentProjectContracts.map(c => {
                    const cMilestones = contractMilestonesMap[c.id] || [];
                    const parent = c.parentContractId ? contracts.find(x => x.id === c.parentContractId) || null : null;
                    const replacementSettlement = contractReplacements[c.id];
                    const replacementContract = replacementSettlement
                      ? contracts.find(
                          contract =>
                            contract.id ===
                            replacementSettlement.replacementContractId
                        ) ?? null
                      : null;
                    const originalReplacementContract = replacementSettlement
                      ? contracts.find(
                          contract =>
                            contract.id ===
                            replacementSettlement.originalContractId
                        ) ?? null
                      : null;
                    const viewerSide = isPerson ? 'person' : 'company';
                    const contractFunded = contractFundingConfirmed(c,
                      projectFunding?.contracts.find(item => item.contractId === c.id), isProjectFunded);
                    const { attestableMilestones } =
                      contractMilestoneActionState(c, cMilestones, contractFunded, viewerSide);
                    const milestoneActionHandlers: MilestoneActionHandlers = {
                      onCheckPayout: milestone => { void handleCheckMilestonePayout(milestone); },
                      checkingPayoutId,
                      onOpen: (milestone, mode) => { void openAttestationModal(milestone, mode); },
                      onAttestRemedied: milestone => { void handleOpenRemediedMilestoneAttestation(milestone); },
                      preparingMilestoneId: progressingMilestoneId
                    };
                    const renderTimelineMilestoneActions = (candidate: Milestone, closeDetails: () => void) => {
                      const candidateContract = contracts.find(item => item.id === candidate.contractId) ?? c;
                      return (
                      <MilestoneNodeActions
                        milestone={candidate}
                        contract={candidateContract}
                        projectFunded={contractFundingConfirmed(candidateContract,
                          projectFunding?.contracts.find(item => item.contractId === candidateContract.id), isProjectFunded)}
                        side={viewerSide}
                        handlers={milestoneActionHandlers}
                        closeDetails={closeDetails}
                      />
                      );
                    };
                    const { totals: contractTotals, totalsNote: contractTotalsNote } = contractFrameTotals({
                      contract: c,
                      fundingContract: projectFunding?.contracts.find(item => item.contractId === c.id),
                      milestones: cMilestones,
                      relatedContracts: contracts,
                      relatedMilestones: Object.values(contractMilestonesMap).flat(),
                      payoutOreByMilestone: Object.fromEntries((projectFunding?.milestones ?? [])
                        .map(milestone => [milestone.milestoneId, milestone.netPayoutOre])),
                      viewerRole: isPerson ? 'person' : 'company',
                      t,
                      formatCurrency,
                      onPay: () => {
                        const params = new URLSearchParams(searchParams);
                        params.set('paymentPicker', 'open');
                        params.set('paymentContractId', c.id);
                        navigate(`?${params.toString()}`);
                      }
                    });
                    const contractNotice = replacementContract && replacementSettlement ? (
                          <ContractReplacementNotice
                            contract={replacementContract}
                            originalContract={originalReplacementContract}
                            settlement={replacementSettlement}
                            compact
                            onSign={
                              isPerson
                                ? () => {
                                    void handleOpenPreview(
                                      'sign',
                                      replacementContract
                                    );
                                  }
                                : undefined
                            }
                            isSigning={isOpeningSigning}
                            onApproveProposal={
                              !isPerson
                                ? () => void handleReplacementProposalResponse(
                                    replacementSettlement,
                                    'approve'
                                  )
                                : undefined
                            }
                            onRejectProposal={
                              !isPerson
                                ? () => confirmReplacementProposalRejection(
                                    replacementSettlement
                                  )
                                : undefined
                            }
                            onWithdrawProposal={
                              isPerson
                                ? () => confirmReplacementProposalWithdrawal(
                                    replacementSettlement
                                  )
                                : undefined
                            }
                            isUpdatingProposal={isUpdatingReplacementProposal}
                          />
                        ) : null;
                    // Only when something waits — otherwise the frame would show an
                    // empty "Att göra" card.
                    const contractActions = hasContractMilestoneActions(c, cMilestones, contractFunded, viewerSide, objectionTurn) ? (
                      <MilestoneContractActions
                        contract={c}
                        milestones={cMilestones}
                        projectFunded={contractFunded}
                        side={viewerSide}
                        objectionTurn={objectionTurn}
                        handlers={milestoneActionHandlers}
                        payoutOreByMilestone={Object.fromEntries((projectFunding?.milestones ?? [])
                          .filter(milestone => milestone.contractId === c.id)
                          .map(milestone => [milestone.milestoneId, milestone.netPayoutOre]))}
                      />
                    ) : null;
                    return (
                      <div key={c.id}>
                        <ContractTimeline
                          contract={c}
                          milestones={cMilestones}
                          relatedContracts={contracts}
                          relatedMilestones={Object.values(contractMilestonesMap).flat()}
                          parentContract={parent}
                          projectFunded={contractFunded}
                          projectCompleted={project.status === 'completed'}
                          projectExternallyDisputed={
                            project.status === 'dispute' || projectDisplayStatus === 'external_dispute'
                          }
                          {...timelineStartProps(c, cMilestones)}
                          viewerRole={isPerson ? 'person' : 'company'}
                          fundingMilestones={projectFunding?.milestones.filter(
                            milestone => milestone.contractId === c.id
                          )}
                          renderMilestoneActions={renderTimelineMilestoneActions}
                          actionMilestoneIds={attestableMilestones.map(milestone => milestone.id)}
                          contractSigningDisabled={isOpeningSigning ||
                            (selectedContract?.id === c.id && stageVariationDirty) ||
                            !!contractReplacements[c.id]?.amendmentScope?.newStageRequested ||
                            (contractReplacements[c.id]?.initiatorRole === 'person' &&
                              contractReplacements[c.id]?.proposalStatus === 'pending')}
                          onSignContract={target => { void handleOpenPreview('sign', target); }}
                          customerName={customers.find(item => item.id === project?.customerId)?.name ?? project?.customerName}
                          onReviseContract={isPerson ? undefined : target => handleContractRowAction(target.id, 'edit')}
                          fundingContract={projectFunding?.contracts.find(contract => contract.contractId === c.id)}
                          relatedFundingContracts={projectFunding?.contracts}
                          onPayContract={isPerson ? (target = c) => {
                            const params = new URLSearchParams(searchParams);
                            params.set('paymentPicker', 'open');
                            params.set('paymentContractId', target.id);
                            navigate(`?${params.toString()}`);
                          } : undefined}
                          frameSlots={{
                            headerBadge: replacementContract && replacementSettlement?.amendmentScope &&
                              stageVariationState(replacementContract, replacementSettlement) === 'active'
                              ? <StageVariationBadge contract={replacementContract} settlement={replacementSettlement} />
                              : undefined,
                            notice: contractNotice,
                            actions: contractActions,
                            totals: contractTotals,
                            totalsNote: contractTotalsNote,
                            // Same header as the contracts on the start page.
                            compactHeader: true
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
              </div>
            )}

            {statusMessages.length > 0 && (
              <Card className="mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="feather-clock me-2"></i>
                    Statusändringshistorik
                  </h5>
                </div>
                <div className="card-body">
                  {statusMessages.map(msg => (
                    <div key={msg.id} className="d-flex align-items-start gap-2 mb-2 pb-2 border-bottom">
                      <i className={`feather-${msg.toStatus === 'on_hold' ? 'pause-circle text-warning' : msg.toStatus === 'dispute' ? 'x-octagon text-danger' : msg.toStatus === 'conflict' ? 'alert-triangle text-danger' : msg.toStatus === 'completed' ? 'check-circle text-success' : 'play-circle text-success'} mt-1`}></i>
                      <div>
                        <div style={{ fontSize: 13 }}>
                          <span className="fw-semibold">{msg.userName}</span>
                          <span className={`badge bg-${msg.userRole === 'company' || msg.userRole === 'builder' ? 'primary' : msg.userRole === 'person' ? 'info' : 'secondary'} ms-1`} style={{ fontSize: 9 }}>
                            {msg.userRole === 'company' || msg.userRole === 'builder' ? t('persona.company') : msg.userRole === 'person' ? t('persona.person') : msg.userRole}
                          </span>
                          <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                            {new Date(msg.createdAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="small text-muted">
                          {msg.fromStatus} → <span className={`fw-semibold text-${msg.toStatus === 'on_hold' ? 'warning' : ['conflict', 'dispute'].includes(msg.toStatus ?? '') ? 'danger' : 'success'}`}>{msg.toStatus === 'on_hold' ? t('projectView.statusOptions.on_hold') : msg.toStatus === 'conflict' ? t('projectView.statusOptions.conflict') : msg.toStatus === 'dispute' ? t('projectView.statusOptions.dispute') : msg.toStatus === 'in_progress' ? t('projectView.statusOptions.in_progress') : msg.toStatus === 'completed' ? t('projectView.statusOptions.completed') : msg.toStatus}</span>
                        </div>
                        <div style={{ fontSize: 13 }} className="mt-1">{msg.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Current Milestones — one row per signed contract that has an unfinished milestone */}
            {project && project.status !== 'completed' && (() => {
              type Row = { contract: Contract; milestone: Milestone };
              const rows: Row[] = [];
              const sortedContracts = currentProjectContracts;
              for (const c of sortedContracts) {
                if (c.draftStatus !== 'signed_by_both' || (c.versionStatus ?? 'current') !== 'current') continue;
                const ms = (contractMilestonesMap[c.id] || [])
                  .slice()
                  .sort((a, b) => a.milestoneNumber - b.milestoneNumber);
                const flexibleFlow = ms.some(milestone =>
                  Boolean(milestone.workStartedAt) ||
                  Boolean(milestone.workStartedByRole) ||
                  Boolean(milestone.companyStartDecision) ||
                  Boolean(milestone.startPredecessorIds?.length)
                );
                const unfinished = ms.filter(m =>
                  m.companyAttestation !== 'approved' || m.personAttestation !== 'approved'
                );
                const candidates = flexibleFlow
                  ? unfinished.filter(m =>
                      m.deliveryStatus !== 'not_started' ||
                      ['disputed', 'conflict', 'under_resolution', 'ready_for_reattestation', 'external_dispute']
                        .includes(m.deliveryStatus ?? '')
                    )
                  : unfinished.slice(0, 1);
                for (const candidate of candidates) {
                  const keepsDialogueOpen = [
                    'disputed',
                    'conflict',
                    'under_resolution',
                    'ready_for_reattestation',
                    'external_dispute'
                  ].includes(candidate.deliveryStatus ?? '');
                  if (!projectFunding?.account && !contractFundingConfirmed(c, projectFunding?.contracts.find(item => item.contractId === c.id), isProjectFunded) && !keepsDialogueOpen) continue;
                  rows.push({ contract: c, milestone: candidate });
                }
              }
              if (rows.length === 0) return null;
              const onlyPriceAdjustment =
                rows.length === 1 &&
                rows[0].milestone.itemType === 'variation_price_adjustment';
              const onlyZeroValuePriceAdjustment =
                onlyPriceAdjustment && rows[0].milestone.amount === 0;
              return (
                <section className="card project-status mb-4" aria-labelledby="current-milestone-title">
                  <header className="project-status-header">
                    <h2 id="current-milestone-title" className="project-status-title">
                      <i className="feather-flag" aria-hidden="true"></i>
                      {rows.length > 1
                        ? t('projectView.currentMilestone.titlePlural', 'Aktuella Etappmål')
                        : onlyPriceAdjustment
                          ? onlyZeroValuePriceAdjustment ? t('projectView.ata.currentChange') : t('projectView.ata.currentPriceChange')
                          : t('projectView.currentMilestone.title', 'Aktuellt Etappmål')}
                    </h2>
                  </header>
                  <div className="project-section-body d-flex flex-column gap-3">
                    {rows.map(({ contract: c, milestone: nextMilestone }) => {
                      const parent = c.parentContractId ? contracts.find(x => x.id === c.parentContractId) : null;
                      const typeLabel = c.contractType === 'variation'
                        ? `${t('contracts.types.variation')}${c.variationNumber != null ? ` #${c.variationNumber}` : ''}`
                        : t('contracts.types.fixed');
                      const otherSideApproved = isPerson
                        ? nextMilestone.companyAttestation === 'approved'
                        : nextMilestone.personAttestation === 'approved';
                      const currentSideApproved = isPerson
                        ? nextMilestone.personAttestation === 'approved'
                        : nextMilestone.companyAttestation === 'approved';
                      const needsCurrentUserAttestation = canAttestMilestone(
                        nextMilestone,
                        isPerson ? 'person' : 'company'
                      );
                      const canOpenDisputeDialogue = ['disputed', 'conflict', 'external_dispute', 'under_resolution'].includes(
                        nextMilestone.deliveryStatus ?? ''
                      );
                      const isCompanyRemedy =
                        !isPerson && nextMilestone.deliveryStatus === 'under_resolution';
                      const isPersonRemedy =
                        isPerson && nextMilestone.deliveryStatus === 'under_resolution';
                      const canReopenConflictManagement =
                        nextMilestone.deliveryStatus === 'ready_for_reattestation' &&
                        (!isPerson || Boolean(nextMilestone.conflictStartedAt));
                      const latestCounterpartyReply = latestCounterpartyReplies[nextMilestone.id];
                      const counterpartyLabel = isPerson ? 'hantverkaren' : 'beställaren';
                      const isPriceAdjustment = nextMilestone.itemType === 'variation_price_adjustment';
                      const isZeroValuePriceAdjustment =
                        isPriceAdjustment && nextMilestone.amount === 0;
                      const priceAdjustmentLabel = isZeroValuePriceAdjustment
                        ? t('projectView.ata.change')
                        : t('projectView.ata.priceChange');
                      const priceAdjustmentDefinite = isZeroValuePriceAdjustment
                        ? t('projectView.ata.changeDefinite')
                        : t('projectView.ata.priceChangeDefinite');
                      const disputeDialogueLabel = nextMilestone.deliveryStatus === 'external_dispute'
                        ? t('projectView.dispute.showDisputeExport')
                        : nextMilestone.deliveryStatus === 'conflict'
                        ? isPerson
                          ? isPriceAdjustment ? t('projectView.dispute.manageConflictAbout', { label: priceAdjustmentLabel }) : t('projectView.dispute.manageConflict')
                          : isPriceAdjustment ? t('projectView.dispute.respondConflictAbout', { label: priceAdjustmentLabel }) : t('projectView.dispute.respondConflict')
                        : nextMilestone.deliveryStatus === 'under_resolution'
                          ? isPerson
                            ? t('projectView.dispute.openConflictManagement')
                            : isPriceAdjustment ? t('projectView.dispute.manageActionOf', { label: priceAdjustmentLabel }) : t('projectView.dispute.manageAction')
                        // An open objection: one way in, the same for both parties (ClickUp 869fj6n8x).
                        : t('projectView.overview.notice.openObjection');
                      const waitingForLabel = (() => {
                        switch (nextMilestone.deliveryStatus) {
                          case 'not_started':
                            return isPriceAdjustment
                              ? t('projectView.waiting.awaitingHandling', { label: priceAdjustmentDefinite })
                              : t('projectView.waiting.notStarted');
                          case 'in_progress':
                            return isPerson
                              ? isPriceAdjustment
                                ? t('projectView.waiting.awaitingCompanyAttestation', { label: priceAdjustmentDefinite })
                                : t('projectView.waiting.inProgressAwaitingCompany')
                              : isPriceAdjustment ? t('projectView.waiting.beingHandled', { label: priceAdjustmentDefinite }) : t('projectView.waiting.inProgress');
                          case 'ready_for_attestation':
                          case 'ready_for_reattestation':
                            return t('projectView.currentMilestone.waitingForCompany', 'Väntar på hantverkarens attestering');
                          case 'disputed':
                            return isPriceAdjustment
                              ? t('projectView.waiting.disputedBlocked', { label: priceAdjustmentDefinite })
                              : t('projectView.waiting.stageDisputedBlocked');
                          case 'conflict':
                            return isPriceAdjustment
                              ? t('projectView.waiting.conflictPaused', { label: priceAdjustmentDefinite })
                              : t('projectView.waiting.stageConflictPaused');
                          case 'external_dispute':
                            return isPriceAdjustment
                              ? t('projectView.waiting.disputeExternal', { label: priceAdjustmentDefinite })
                              : t('projectView.waiting.stageDisputeExternal');
                          case 'under_resolution':
                            return t('projectView.waiting.objectionUnderHandling');
                          case 'payout_processing':
                            return t('projectView.waiting.payoutProcessing');
                          case 'payout_failed':
                            return t('projectView.waiting.payoutFailed');
                          default:
                            return isPerson
                              ? t('projectView.currentMilestone.waitingForCompany', 'Väntar på hantverkarens attestering')
                              : t('projectView.currentMilestone.waitingForPerson', 'Väntar på beställarens godkännande');
                        }
                      })();
                      return (
                        <div
                          key={`${c.id}-${nextMilestone.id}`}
                          className="border rounded p-3"
                          data-testid={`current-milestone-${nextMilestone.id}`}
                        >
                          {/* Contract context */}
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <span className="badge bg-light text-dark border">
                              <i className="feather-file-text me-1"></i>
                              {t('home.pendingContracts.contract', 'Kontrakt')} #{c.contractNumber}
                            </span>
                            <span className={`badge ${c.contractType === 'variation' ? 'bg-warning text-dark' : 'bg-light text-dark border'}`}>
                              {typeLabel}
                              {parent ? ` → ${t('home.pendingContracts.contract', 'Kontrakt')} #${parent.contractNumber}` : ''}
                            </span>
                            <span className="text-muted small">{contractTitle(c)}</span>
                          </div>

                          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <span className="badge bg-light text-dark border">
                                  <i className="feather-flag me-1"></i>
                                  {isPriceAdjustment
                                    ? priceAdjustmentLabel
                                    : `${t('personHome.projectTimeline.milestone', 'Etappmål')} #${nextMilestone.milestoneNumber}`}
                                </span>
                                <span className="fw-bold text-primary">
                                  {new Intl.NumberFormat('sv-SE').format(nextMilestone.amount)} kr
                                </span>
                                {nextMilestone.deliveryStatus === 'under_resolution' && (
                                  <span
                                    className="badge bg-warning text-dark"
                                    data-testid={`milestone-status-under-resolution-${nextMilestone.id}`}
                                  >
                                    <i className="feather-tool me-1"></i>
                                    {t('contractTimeline.beingRemedied', 'Åtgärdas')}
                                  </span>
                                )}
                              </div>
                              <div className="fw-semibold mb-1">{nextMilestone.description}</div>
                              {nextMilestone.endDate && !isPriceAdjustment && (
                                <div className="small text-muted">
                                  <i className="feather-calendar me-1"></i>
                                  {t('milestones.columns.endDate')}: {nextMilestone.endDate}
                                </div>
                              )}
                              <div className="d-flex gap-3 mt-2">
                                <div>
                                  <small className="text-muted d-block">{t('persona.company')}</small>
                                  {getMilestoneAttestationBadge(nextMilestone.companyAttestation)}
                                </div>
                                <div>
                                  <small className="text-muted d-block">{t('persona.person')}</small>
                                  {getMilestoneAttestationBadge(nextMilestone.personAttestation)}
                                </div>
                              </div>
                              {(() => {
                                const thread = projectThreads.find(item => item.threadType === 'milestone' && item.threadId === nextMilestone.id);
                                return thread
                                  ? <div className="mt-2"><MessageThreadStateLink thread={thread} mySide={isPerson ? 'person' : 'company'} /></div>
                                  : null;
                              })()}
                            </div>
                            {isCompanyRemedy || isPersonRemedy ? (
                              <MilestoneRemediationActions
                                deliveryStatus={nextMilestone.deliveryStatus}
                                isPerson={isPerson}
                                attestLabel={isPriceAdjustment
                                  ? t('projectView.ata.attest')
                                  : t('personHome.projectTimeline.attest', 'Attestera etappmål')}
                                preparingLabel={t('projectView.dispute.preparingAttestation')}
                                conflictLabel={t('projectView.dispute.openConflictManagement')}
                                isPreparing={isCompanyRemedy && progressingMilestoneId === nextMilestone.id}
                                dataTestId={`milestone-remediation-actions-current-${nextMilestone.id}`}
                                attestDataTestId={`milestone-remedy-attest-${nextMilestone.id}`}
                                onAttest={() => {
                                  if (isPersonRemedy) {
                                    openAttestationModal(nextMilestone);
                                  } else {
                                    void handleOpenRemediedMilestoneAttestation(nextMilestone);
                                  }
                                }}
                                onOpenConflict={() => openAttestationModal(nextMilestone, 'resolution')}
                              />
                            ) : needsCurrentUserAttestation || canOpenDisputeDialogue ? (
                              canOpenDisputeDialogue ? (
                                <MilestoneConflictActionButton
                                  deliveryStatus={nextMilestone.deliveryStatus}
                                  dataTestId={`milestone-conflict-cta-${nextMilestone.id}`}
                                  onClick={() => openAttestationModal(nextMilestone, 'resolution')}
                                >
                                  {disputeDialogueLabel}
                                </MilestoneConflictActionButton>
                              ) : isPerson ? (
                                <div className="d-flex flex-column flex-sm-row gap-2 flex-shrink-0">
                                  <button
                                    className="btn btn-primary"
                                  onClick={() => openAttestationModal(nextMilestone)}
                                >
                                    <i className="feather-flag me-1"></i>
                                    {isPriceAdjustment ? t('projectView.ata.attest') : t('personHome.projectTimeline.attest', 'Attestera etappmål')}
                                  </button>
                                  {canReopenConflictManagement ? (
                                    <MilestoneConflictActionButton
                                      deliveryStatus={nextMilestone.deliveryStatus}
                                      onClick={() => openAttestationModal(nextMilestone, 'resolution')}
                                    >
                                      {t('projectView.dispute.openConflictManagement')}
                                    </MilestoneConflictActionButton>
                                  ) : (
                                    <button
                                      className="btn btn-outline-secondary"
                                      onClick={() => openAttestationModal(nextMilestone, 'resolution')}
                                    >
                                      <i className="feather-help-circle me-1"></i>Något stämmer inte?
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex flex-column flex-sm-row gap-2 flex-shrink-0">
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => openAttestationModal(nextMilestone)}
                                  >
                                    <i className="feather-flag me-1"></i>
                                    {isPriceAdjustment ? t('projectView.ata.attest') : t('personHome.projectTimeline.attest', 'Attestera etappmål')}
                                  </button>
                                  {canReopenConflictManagement && (
                                    <MilestoneConflictActionButton
                                      deliveryStatus={nextMilestone.deliveryStatus}
                                      onClick={() => openAttestationModal(nextMilestone, 'resolution')}
                                    >
                                      {t('projectView.dispute.openConflictManagement')}
                                    </MilestoneConflictActionButton>
                                  )}
                                </div>
                              )
                            ) : (
                              <div className="alert alert-light border mb-0 py-2 px-3 flex-shrink-0" style={{ maxWidth: 320 }}>
                                <div className="d-flex align-items-start gap-2">
                                  <i className={`feather-${otherSideApproved ? 'check-circle' : 'clock'} text-${otherSideApproved ? 'success' : 'muted'} mt-1`}></i>
                                  <div>
                                    <div className="fw-semibold small">
                                      {currentSideApproved
                                        ? t('projectView.currentMilestone.yourSideDone', 'Din attestering är registrerad.')
                                        : otherSideApproved
                                          ? isPerson
                                            ? t('projectView.currentMilestone.companyDone')
                                            : t('projectView.currentMilestone.personDone')
                                          : waitingForLabel}
                                    </div>
                                    {(currentSideApproved || otherSideApproved) && (
                                      <small className="text-muted">
                                        {waitingForLabel}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {/* An open objection's dialogue is read in its modal ("Öppna invändningen"), not excerpted here (ClickUp 869fj6n8x). */}
                          {latestCounterpartyReply && canOpenDisputeDialogue && !['external_dispute', 'disputed'].includes(nextMilestone.deliveryStatus ?? '') && (
                            <div
                              className="alert alert-info border-info mt-3 mb-0"
                              data-testid={`milestone-reply-highlight-${nextMilestone.id}`}
                            >
                              <div className="d-flex flex-column flex-sm-row align-items-sm-start justify-content-between gap-2">
                                <div className="min-w-0">
                                  <div className="fw-semibold mb-1">
                                    <i className="feather-message-circle me-1"></i>
                                    Senaste svar från {counterpartyLabel}
                                  </div>
                                  <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                    {latestCounterpartyReply.message.length > 280
                                      ? `${latestCounterpartyReply.message.slice(0, 277)}…`
                                      : latestCounterpartyReply.message}
                                  </div>
                                  <small className="text-muted d-block mt-1">
                                    {latestCounterpartyReply.userName} · {new Date(latestCounterpartyReply.createdAt).toLocaleString('sv-SE', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </small>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-info flex-shrink-0"
                                  onClick={() => openAttestationModal(nextMilestone, 'resolution')}
                                >
                                  {t('counterproposal.openDialogue')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="tab-pane fade show active">
            {showPaymentCompletedNotice && (
              <div className="alert alert-info d-flex align-items-start gap-2" role="alert">
                <i className="feather-info mt-1"></i>
                <div>
                  <strong>
                    {hasConfirmedPaymentTransaction
                      ? t('projectView.transactions.paymentCompletedTitle', 'Insättningen är bekräftad.')
                      : t('projectView.transactions.paymentRegisteredTitle', 'Insättningen är registrerad hos betalpartnern.')}
                  </strong>
                  <div>
                    {hasConfirmedPaymentTransaction
                      ? t('projectView.transactions.paymentCompletedInfo', 'Beloppet har registrerats bland projektets insättningar.')
                      : t('projectView.transactions.paymentRegisteredInfo', 'Vi väntar på slutlig bekräftelse från Adyen. I sandbox bör det normalt ske snabbt.')}
                  </div>
                </div>
              </div>
            )}
            {showPaymentCompletedNotice && projectFunding?.account?.fulfilled && <Notice level="done" title={t('fundingNotice.projectAccount.receiptTitle')} testId="funding-receipt-fulfilled">
              <p className="text-explain mb-0">{t('fundingNotice.projectAccount.remindersStopped')}</p>
            </Notice>}
            {/* Transaction overview */}
            {(projectContract || transactions.length > 0) ? (
              <div className="row g-4">
                {/* Summary column — left on desktop, stacks on tablet/mobile: the company's
                    economics summary, then the funds held in the project. The held funds
                    card is the customer's summary (ClickUp 869fmxjbb). */}
                {showTransactionSummaryColumn && (
                <div className="col-12 col-xl-6 d-flex flex-column gap-4">{showEconomicsSummary && (() => {
                  const eco = projectContract || projectEconomics || {};
                  const pvSek = eco.project_value_sek ?? eco.projectValue ?? totalContractAmount;
                  const cfSek = eco.customer_fee_sek ?? eco.customerFee ?? 0;
                  const coSek = eco.company_fee_sek ?? eco.companyFee ?? 0;
                  const companyFeeBookedSek = transactions
                    .filter((txn: any) => txn.type === 'builder_fee_booked')
                    .reduce((sum: number, txn: any) => {
                      if (typeof txn.amountSek === 'number') return sum + txn.amountSek;
                      if (typeof txn.amountOre === 'number') return sum + Math.round(txn.amountOre / 100);
                      return sum;
                    }, 0);
                  const companyFeeConfirmed =
                    coSek > 0 &&
                    (companyFeeBookedSek >= coSek ||
                      eco.status === 'active' ||
                      eco.status === 'completed');
                  const mSek = eco.etappio_margin_sek ?? eco.etappioMargin ?? 0;
                  const mPct = eco.etappio_margin_pct ?? eco.etappioMarginPct ?? 0;
                  const rvSek = eco.etappio_revenue_sek ?? eco.etappioRevenue ?? 0;
                  const csSek = eco.etappio_cost_sek ?? eco.etappioCost ?? 0;
                  const cfgName = eco.config_snapshot?.name ?? eco.configName ?? '';
                  const cfgType = eco.config_snapshot?.customer_type ?? eco.configType ?? '';
                  const snapDate = eco.snapshot_taken_at ?? eco.snapshotDate ?? null;
                  return (
                <section className="card project-status mb-0" aria-labelledby="project-economics-title">
                  <header className="project-status-header">
                    <h2 id="project-economics-title" className="project-status-title"><i className="feather-credit-card" aria-hidden="true"></i>{t('projectView.transactions.economics', 'Ekonomisk sammanfattning')}</h2>
                  </header>
                  <div className="project-section-body">
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-sm-6">
                        <div className="border rounded p-3 text-center h-100">
                          <small className="text-muted d-block mb-1">{t(eco.fee_basis === 'initial_project_amount' ? 'projectView.transactions.initialFeeBasis' : 'projectView.transactions.projectValue')}</small>
                          <div className="fs-5 fw-bold text-nowrap">{new Intl.NumberFormat('sv-SE').format(pvSek)} kr</div>
                        </div>
                      </div>
                      {user?.isAdmin && (
                        <div className="col-12 col-sm-6">
                          <div className="border rounded p-3 text-center h-100 border-info">
                            <small className="text-muted d-block mb-1">{t('projectView.transactions.customerFee', 'Kundavgift')}</small>
                            <div className="fs-5 fw-bold text-info text-nowrap">{new Intl.NumberFormat('sv-SE').format(cfSek)} kr</div>
                          </div>
                        </div>
                      )}
                      <div className="col-12 col-sm-6">
                        <div className="border rounded p-3 text-center h-100 border-primary">
                          <small className="text-muted d-block mb-1">{t('projectView.transactions.companyFee', 'Plattformsavgift')}</small>
                          <div className="fs-5 fw-bold text-primary text-nowrap">{new Intl.NumberFormat('sv-SE').format(coSek)} kr</div>
                        </div>
                      </div>
                      {user?.isAdmin && (
                        <div className="col-12 col-sm-6">
                          <div className="border rounded p-3 text-center h-100 border-success">
                            <small className="text-muted d-block mb-1">{t('projectView.transactions.etappioMargin', 'Etappio marginal')}</small>
                            <div className="fs-5 fw-bold text-success text-nowrap">{new Intl.NumberFormat('sv-SE').format(mSek)} kr</div>
                            <small className="text-muted">({mPct}%)</small>
                          </div>
                        </div>
                      )}
                    </div>

                    {eco.fee_basis === 'initial_project_amount' && (
                      <p className="text-muted small">{t('projectView.transactions.initialFeeExplanation')}</p>
                    )}
                    {/* Fee breakdown */}
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>{t('projectView.transactions.description', 'Beskrivning')}</th>
                            <th className="text-end">{t('projectView.transactions.amount', 'Belopp')}</th>
                            <th>{t('projectView.transactions.statusHeading', 'Status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {user?.isAdmin && cfSek > 0 && (
                            <tr>
                              <td>
                                <i className="feather-user me-2 text-muted"></i>
                                {t('projectView.transactions.customerTransactionFee', 'Transaktionsavgift — Beställare')}
                              </td>
                              <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(cfSek)} kr</td>
                              <td><span className={`badge bg-${eco.status === 'active' || eco.status === 'completed' ? 'success' : 'secondary'}`}>{eco.status === 'active' || eco.status === 'completed' ? t('projectView.transactions.confirmed', 'Bekräftad') : t('projectView.transactions.pending', 'Väntande')}</span></td>
                            </tr>
                          )}
                          {coSek > 0 && (
                            <tr>
                              <td>
                                <i className="feather-briefcase me-2 text-muted"></i>
                                {t('projectView.transactions.companyTransactionFee', 'Plattformsavgift')}
                              </td>
                              <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(coSek)} kr</td>
                              <td><span className={`badge bg-${companyFeeConfirmed ? 'success' : 'secondary'}`}>{companyFeeConfirmed ? t('projectView.transactions.confirmed', 'Bekräftad') : t('projectView.transactions.pending', 'Väntande')}</span></td>
                            </tr>
                          )}
                          {user?.isAdmin && (
                            <>
                              <tr className="table-light">
                                <td><i className="feather-trending-up me-2 text-muted"></i>{t('projectView.transactions.etappioRevenue', 'Etappio intäkt (totalt)')}</td>
                                <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(rvSek)} kr</td>
                                <td></td>
                              </tr>
                              <tr className="table-light">
                                <td><i className="feather-minus-circle me-2 text-muted"></i>{t('projectView.transactions.etappioCost', 'Etappio kostnad')}</td>
                                <td className="text-end fw-semibold text-danger">-{new Intl.NumberFormat('sv-SE').format(csSek)} kr</td>
                                <td></td>
                              </tr>
                              <tr className="table-success">
                                <td className="fw-bold"><i className="feather-check-circle me-2"></i>{t('projectView.transactions.etappioNetMargin', 'Etappio nettomarginal')}</td>
                                <td className="text-end fw-bold text-success">{new Intl.NumberFormat('sv-SE').format(mSek)} kr</td>
                                <td><span className="badge bg-success">{mPct}%</span></td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Config info */}
                    <div className="mt-3 pt-3 border-top">
                      <small className="text-muted">
                        <i className="feather-info me-1"></i>
                        {t('projectView.transactions.configInfo', 'Avtalskonfiguration')}: <strong>{cfgName}</strong>
                        {cfgType === 'partner' && (
                          <span className="badge bg-primary ms-2">B2B Partner</span>
                        )}
                        {snapDate && (
                          <span className="ms-2">{t('projectView.transactions.snapshotDate')}: {new Date(snapDate).toLocaleDateString(i18n.language)}</span>
                        )}
                      </small>
                    </div>
                  </div>
                </section>
                  );
                })()}
                {hasHeldFunds && (
                  <ProjectHeldFundsSummary
                    summary={heldFundsSummary}
                    pingPayouts={pingPayouts}
                    transactions={transactions}
                  />
                )}
                </div>
                )}

                {/* Transaction History — right column on desktop, full width when there is no summary column */}
                <div className={`col-12 ${showTransactionSummaryColumn ? 'col-xl-6' : ''}`}>
                <section className="card project-status mb-0" aria-labelledby="payment-history-title">
                  <header className="project-status-header">
                    <h2 id="payment-history-title" className="project-status-title"><i className="feather-list" aria-hidden="true"></i>{t('projectView.transactions.paymentHistory')}</h2>
                  </header>
                  <div className="project-section-body">
                    {pingPayoutPanel}
                    {transactions.length === 0 && signedVariations.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="feather-clock fs-1 d-block mb-3"></i>
                        <p className="mb-0">{t('projectView.transactions.noPayments')}</p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        {visibleTransactions.map((txn: any) => {
                          if (txn.type === 'variation_signed') {
                            return <VariationTransactionItem key={txn.id} change={txn.change} isBuyer={isPerson} />;
                          }
                          if (txn.type === 'ping_payout_confirmed') {
                            return <PingPayoutHistoryItem key={txn.id} transaction={txn} />;
                          }
                          if (txn.type && txn.display) {
                            // The row's texts in the viewer's language (ClickUp 869ev0jcn).
                            const display = milestonePaymentDisplay(
                              { ...txn, display: paymentTimelineDisplay(txn, t, i18n.language) },
                              t
                            );
                            const amountSek = typeof txn.amountSek === 'number'
                              ? txn.amountSek
                              : Math.round((txn.amountOre || 0) / 100);
                            const isOutgoing = txn.direction === 'outgoing';
                            const isNeutral = txn.direction === 'neutral';
                            const showCompanyPayoutAsNeutral =
                              !user?.isAdmin && !isPerson && txn.type === 'payout_booked';
                            const amountClass = showCompanyPayoutAsNeutral
                              ? 'text-muted'
                              : isOutgoing ? 'text-warning' : isNeutral ? 'text-muted' : 'text-success';
                            const amountPrefix = amountSek === 0 || showCompanyPayoutAsNeutral
                              ? ''
                              : (display.amountPrefix ?? (isOutgoing ? '−' : isNeutral ? '' : '+'));
                            const occurredAt = txn.occurredAt || txn.createdAt;
                            const displayTooltip = display.tooltip || display.subtitle || txn.description;
                            const payoutTransferDates = txn.type === 'payout_booked'
                              ? timelinePayoutTransferDates(txn)
                              : { bookingDate: null, valueDate: null };
                            return (
                              <div key={txn.id} className="border rounded overflow-hidden">
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start p-3 bg-light border-bottom">
                                  <div className="d-flex align-items-center gap-2 mb-2 mb-sm-0">
                                    <div
                                      className={`rounded-circle d-flex align-items-center justify-content-center ${display.badgeClass || 'bg-secondary'} text-white`}
                                      style={{ width: 36, height: 36, flexShrink: 0 }}
                                      title={displayTooltip}
                                      aria-label={displayTooltip}
                                    >
                                      <i className={`feather-${display.icon || 'credit-card'}`} style={{ fontSize: 16 }}></i>
                                    </div>
                                    <div>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <TransactionKindPill kind={transactionKindForType(txn.type)} />
                                        <span className="fw-bold">{display.title || txn.title}</span>
                                        <span
                                          className={`badge ${display.badgeClass || 'bg-secondary'}`}
                                          title={displayTooltip}
                                        >
                                          {display.badgeLabel || txn.statusLabel}
                                        </span>
                                        {display.attemptLabel && (
                                          <span className="badge bg-light text-muted border">
                                            {display.attemptLabel}
                                          </span>
                                        )}
                                        {user?.isAdmin && txn.provider && txn.mode && (
                                          <span className="badge bg-light text-muted border">
                                            {txn.provider}/{txn.mode}
                                          </span>
                                        )}
                                      </div>
                                      <small className="text-muted">
                                        {display.subtitle || txn.description}
                                      </small>
                                      {(payoutTransferDates.bookingDate || payoutTransferDates.valueDate) && (
                                        <div>
                                          <small className="text-muted">
                                            {payoutTransferDates.bookingDate && (
                                              <>
                                                {t('projectView.transactions.bookingDate', 'Booking date')}: {payoutTransferDates.bookingDate}
                                              </>
                                            )}
                                            {payoutTransferDates.bookingDate && payoutTransferDates.valueDate && ' · '}
                                            {payoutTransferDates.valueDate && (
                                              <>
                                                {t('projectView.transactions.valueDate', 'Value date')}: {payoutTransferDates.valueDate}
                                              </>
                                            )}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-sm-end flex-shrink-0 ms-sm-2">
                                    <div className={`fs-5 fw-bold text-nowrap ${amountClass}`}>
                                      {amountPrefix}{new Intl.NumberFormat('sv-SE').format(amountSek)} kr
                                    </div>
                                    {occurredAt && (
                                      <small className="text-muted text-nowrap">
                                        {new Date(occurredAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          const isOutgoing = txn.direction === 'outgoing';
                          const sign = isOutgoing ? '−' : '+';
                          const isAdmin = !!user?.isAdmin;
                          const isReleasedToBuilder = transactionReleasedToBuilder(txn);
                          const displayStatus = isReleasedToBuilder ? 'released' : (txn.status || 'held');
                          const statusDisplay = transactionStatusDisplay(displayStatus);
                          const statusLabel = isPerson && isReleasedToBuilder
                            ? t('projectView.transactions.status.customerReleased', 'Utbetalning utförd')
                            : t(`projectView.transactions.status.${displayStatus}`, statusDisplay.label);
                          const releasedAt = txn.releasedAt ?? txn.released_at;
                          const baseAmountSek = transactionBaseAmountSek(txn);
                          const totalAmountSek = transactionTotalAmountSek(txn);
                          const payoutAmountSek = transactionPayoutAmountSek(txn);
                          const customerFeeSek = transactionCustomerFeeSek(txn);
                          const hasCustomerFeeSplit =
                            typeof txn.customerTransactionFeeSek === 'number' ||
                            typeof txn.customerProjectFeeSek === 'number' ||
                            typeof txn.customerMinimumFeeAdjustmentSek === 'number';
                          const customerProjectFeeSek =
                            typeof txn.customerProjectFeeSek === 'number' ? txn.customerProjectFeeSek : 0;
                          const customerMinimumFeeAdjustmentSek =
                            typeof txn.customerMinimumFeeAdjustmentSek === 'number'
                              ? txn.customerMinimumFeeAdjustmentSek
                              : 0;
                          const customerTransactionFeeSek =
                            typeof txn.customerTransactionFeeSek === 'number'
                              ? Math.max(txn.customerTransactionFeeSek - customerMinimumFeeAdjustmentSek, 0)
                              : Math.max(customerFeeSek - customerProjectFeeSek - customerMinimumFeeAdjustmentSek, 0);
                          const showCustomerPaymentView = isPerson && !isOutgoing && !isAdmin;
                          const displayAmountSek = showCustomerPaymentView ? totalAmountSek : undefined;
                          const showDirectionBadge = shouldShowIncomingDirection(txn, { isPerson });
                          const contractId = txn.contractId ?? txn.contract_id;
                          const contractNumber = txn.contractNumber ?? txn.contract_number;
                          const milestoneNumber = txn.milestoneNumber ?? txn.milestone_number;
                          const contractMilestoneCount =
                            typeof contractId === 'string'
                              ? contractMilestonesMap[contractId]?.length
                              : undefined;
                          const baseAmountLabelKey = transactionBaseAmountLabelKey(txn, { contractMilestoneCount });
                          // Private persons on outgoing payouts and the company
                          // on incoming payments only see the contract amount —
                          // no transaction fee, project fee or fee totals.
                          // Admin always sees the full breakdown.
                          const bareAmount =
                            (isPerson && isOutgoing) ||
                            (!isPerson && !isAdmin && !isOutgoing);
                          const visibleHeaderAmountSek = displayAmountSek ?? (bareAmount ? baseAmountSek : totalAmountSek);
                          const methodIcon =
                            txn.paymentMethod === 'bank_payout' ? 'send' :
                            txn.paymentMethod === 'bank' ? 'home' :
                            txn.paymentMethod === 'card' ? 'credit-card' :
                            'smartphone';
                          const releaseRecipientName =
                            typeof txn.recipientName === 'string' && txn.recipientName.trim()
                              ? txn.recipientName.trim()
                              : t('projectView.transactions.releaseFallbackRecipient', 'hantverkaren');
                          const contractMilestoneParts = [
                            contractNumber != null
                              ? `${t('projectView.transactions.contractRef', 'Kontrakt')} #${contractNumber}`
                              : null,
                            milestoneNumber != null
                              ? `${t('projectView.transactions.milestoneRef', 'Etappmål')} #${milestoneNumber}`
                              : null
                          ].filter(Boolean);
                          const customerTimelineItems = showCustomerPaymentView
                            ? [
                                {
                                  id: `${txn.id}:customer-payment-received`,
                                  kind: 'deposit' as TransactionKind,
                                  title: t('projectView.transactions.paymentReceived', 'Insättning mottagen'),
                                  badge: t('projectView.transactions.paymentReceived', 'Insättning mottagen'),
                                  subtitle:
                                    customerFeeSek > 0
                                      ? `${t('projectView.transactions.customerPaymentRegistered', 'Beställarens insättning är registrerad')}: ${t('projectView.transactions.milestoneAmount', 'Etappbelopp').toLowerCase()} ${new Intl.NumberFormat('sv-SE').format(baseAmountSek)} kr + ${t('projectView.transactions.customerFeeAmountLabel', 'avgift')} ${new Intl.NumberFormat('sv-SE').format(customerFeeSek)} kr.`
                                      : t('projectView.transactions.customerPaymentReceived', 'Beställarens insättning är registrerad.'),
                                  amountSek: totalAmountSek,
                                  amountClass: 'text-success',
                                  amountPrefix: '+',
                                  icon: 'check-circle',
                                  toneClass: 'bg-info',
                                  badgeClass: 'bg-info',
                                  occurredAt: txn.createdAt,
                                  fees: hasCustomerFeeSplit
                                    ? [
                                        {
                                          label: `${t('projectView.transactions.transactionFee', 'Transaktionsavgift')}${typeof txn.feePct === 'number' ? ` (${txn.feePct}%)` : ''}`,
                                          amountSek: customerTransactionFeeSek,
                                          icon: 'percent'
                                        },
                                        {
                                          label: t('projectView.transactions.projectFee', 'Projektavgift'),
                                          amountSek: customerProjectFeeSek,
                                          icon: 'file'
                                        },
                                        {
                                          label: t('projectView.transactions.minimumFeeAdjustment', 'Minimiavgiftstillägg'),
                                          amountSek: customerMinimumFeeAdjustmentSek,
                                          icon: 'arrow-up-circle'
                                        }
                                      ].filter((fee) => fee.amountSek > 0)
                                    : customerFeeSek > 0
                                      ? [{
                                          label: t('projectView.transactions.customerFee', 'Avgift'),
                                          amountSek: customerFeeSek,
                                          icon: 'percent'
                                        }]
                                      : []
                                },
                                {
                                  id: `${txn.id}:customer-escrow`,
                                  // Held money is neither kind; once released it is a payout.
                                  kind: isReleasedToBuilder ? ('payout' as TransactionKind) : null,
                                  title: isReleasedToBuilder
                                    ? t('projectView.transactions.status.customerReleased', 'Utbetalning utförd')
                                    : t('projectView.transactions.customerMoneyHeld', 'Pengarna hålls'),
                                  badge: isReleasedToBuilder
                                    ? t('projectView.transactions.released', 'Utförd')
                                    : t('projectView.transactions.held', 'Hålls'),
                                  subtitle: isReleasedToBuilder
                                    ? [
                                        releaseRecipientName,
                                        ...contractMilestoneParts,
                                        txn.milestoneDescription || null
                                      ].filter(Boolean).join(' · ')
                                    : t('projectView.transactions.customerMoneyHeldInfo', 'Pengarna hålls tills etappmålet är godkänt, utbetalt, delvis utbetalt eller återbetalat vid tvist.'),
                                  amountSek: baseAmountSek,
                                  amountClass: 'text-muted',
                                  amountPrefix: '',
                                  icon: isReleasedToBuilder ? 'unlock' : 'lock',
                                  toneClass: isReleasedToBuilder ? 'bg-success' : 'bg-info',
                                  badgeClass: isReleasedToBuilder ? 'bg-success' : 'bg-info',
                                  occurredAt: releasedAt || txn.createdAt
                                }
                              ].filter(Boolean)
                            : [];
                          if (customerTimelineItems.length > 0) {
                            return (
                              <React.Fragment key={txn.id}>
                                {customerTimelineItems.map((item: any) => (
                                  <div key={item.id} className="border rounded overflow-hidden">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start p-3 bg-light border-bottom">
                                      <div className="d-flex align-items-center gap-2 mb-2 mb-sm-0">
                                        <div
                                          className={`rounded-circle d-flex align-items-center justify-content-center ${item.toneClass} text-white`}
                                          style={{ width: 36, height: 36, flexShrink: 0 }}
                                          title={item.subtitle}
                                          aria-label={item.subtitle}
                                        >
                                          <i className={`feather-${item.icon}`} style={{ fontSize: 16 }}></i>
                                        </div>
                                        <div>
                                          <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <TransactionKindPill kind={item.kind} />
                                            <span className="fw-bold">{item.title}</span>
                                            <span className={`badge ${item.badgeClass}`} title={item.subtitle}>
                                              {item.badge}
                                            </span>
                                          </div>
                                          <small className="text-muted">{item.subtitle}</small>
                                        </div>
                                      </div>
                                      <div className="text-sm-end">
                                        <div className={`fs-5 fw-bold ${item.amountClass}`}>
                                          {item.amountPrefix}{new Intl.NumberFormat('sv-SE').format(item.amountSek)} kr
                                        </div>
                                        {item.occurredAt && (
                                          <small className="text-muted">
                                            {new Date(item.occurredAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                    {item.fees?.length > 0 && (
                                      <div className="p-3">
                                        <small className="fw-semibold text-muted d-block mb-2">
                                          {t('projectView.transactions.feeBreakdown', 'Avgiftsspecifikation')}
                                        </small>
                                        <AmountBreakdownTable
                                          rows={item.fees.map((fee: any) => ({
                                            key: fee.label,
                                            icon: fee.icon,
                                            label: fee.label,
                                            amount: `+${new Intl.NumberFormat('sv-SE').format(fee.amountSek)} kr`,
                                            amountClassName: 'text-muted',
                                          }))}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </React.Fragment>
                            );
                          }
                          return (
                          <React.Fragment key={txn.id}>
                          <div className="border rounded overflow-hidden">
                            {/* Transaction header */}
                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start p-3 bg-light border-bottom">
                              <div className="d-flex align-items-center gap-2 mb-2 mb-sm-0">
                                <div className={`rounded-circle d-flex align-items-center justify-content-center ${statusDisplay.toneClass} text-white`} style={{ width: 36, height: 36, flexShrink: 0 }}>
                                  <i className={`feather-${isOutgoing ? 'arrow-up-right' : statusDisplay.icon}`} style={{ fontSize: 16 }}></i>
                                </div>
                                <div>
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <TransactionKindPill kind={transactionKindForType(txn.type) ?? (isOutgoing ? 'payout' : 'deposit')} />
                                    <span className="fw-bold">{txn.paymentMethodLabel}</span>
                                    {showDirectionBadge && (
                                      <span className={`badge ${isOutgoing ? 'bg-warning text-dark' : 'bg-info'}`}>
                                        {isOutgoing
                                          ? t('projectView.transactions.directionOutgoing', 'Utgående')
                                          : t('projectView.transactions.directionIncoming', 'Inkommande')}
                                      </span>
                                    )}
                                    <span className={`badge ${statusDisplay.badgeClass}`}>
                                      {statusLabel}
                                    </span>
                                    {isOutgoing && txn.isFirstPayout && (
                                      <span className="badge bg-secondary">{t('projectView.transactions.firstPayout', 'Första utbetalningen')}</span>
                                    )}
                                  </div>
                                  {user?.isAdmin && (txn.transactionReference || txn.adyenReference) && (
                                    <small className="text-muted">
                                      {txn.transactionReference}
                                      {txn.adyenReference && (
                                        <> · <span title="Adyen PSP reference">{txn.adyenReference}</span></>
                                      )}
                                    </small>
                                  )}
                                  {(contractNumber != null || milestoneNumber != null) && (
                                    <div>
                                      <small className="text-muted">
                                        <i className="feather-flag me-1" style={{ fontSize: 11 }}></i>
                                        {contractNumber != null && (
                                          <>{t('projectView.transactions.contractRef', 'Kontrakt')} #{contractNumber}</>
                                        )}
                                        {contractNumber != null && milestoneNumber != null && ' · '}
                                        {milestoneNumber != null && (
                                          <>
                                            {t('projectView.transactions.milestoneRef', 'Etappmål')} #{milestoneNumber}
                                            {txn.milestoneDescription ? `: ${txn.milestoneDescription}` : ''}
                                          </>
                                        )}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm-end">
                                <div className={`fs-5 fw-bold ${isOutgoing ? 'text-warning' : 'text-success'}`}>
                                  {isOutgoing ? '−' : ''}{new Intl.NumberFormat('sv-SE').format(visibleHeaderAmountSek)} kr
                                </div>
                                <small className="text-muted">{new Date(txn.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</small>
                              </div>
                            </div>

                            {/* Fee breakdown - transparent */}
                            <div className="p-3">
                              {(() => {
                                const personPayout = bareAmount;
                                return (
                              <>
                              {!personPayout && (
                                <small className="fw-semibold text-muted d-block mb-2">{t('projectView.transactions.feeBreakdown', 'Avgiftsspecifikation')}</small>
                              )}
                              <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                  <tr>
                                    <td className="ps-0">
                                      <i className={`feather-${baseAmountLabelKey === 'milestoneAmount' ? 'flag' : 'home'} me-2 text-muted`} style={{ fontSize: 13 }}></i>
                                      {baseAmountLabelKey === 'milestoneAmount'
                                        ? t('projectView.transactions.milestoneAmount', 'Etappbelopp')
                                        : t('projectView.transactions.contractAmount', 'Anbudssumma')}
                                    </td>
                                    <td className="text-end pe-0 fw-semibold">{new Intl.NumberFormat('sv-SE').format(baseAmountSek)} kr</td>
                                  </tr>
                                  {showCustomerPaymentView && hasCustomerFeeSplit && customerTransactionFeeSek > 0 && (
                                    <tr>
                                      <td className="ps-0">
                                        <i className="feather-percent me-2 text-muted" style={{ fontSize: 13 }}></i>
                                        {t('projectView.transactions.transactionFee', 'Transaktionsavgift')} ({txn.feePct}%)
                                      </td>
                                      <td className="text-end pe-0 text-muted">+{new Intl.NumberFormat('sv-SE').format(customerTransactionFeeSek)} kr</td>
                                    </tr>
                                  )}
                                  {showCustomerPaymentView && hasCustomerFeeSplit && customerProjectFeeSek > 0 && (
                                    <tr>
                                      <td className="ps-0">
                                        <i className="feather-file me-2 text-muted" style={{ fontSize: 13 }}></i>
                                        {t('projectView.transactions.projectFee', 'Projektavgift')}
                                      </td>
                                      <td className="text-end pe-0 text-muted">+{new Intl.NumberFormat('sv-SE').format(customerProjectFeeSek)} kr</td>
                                    </tr>
                                  )}
                                  {showCustomerPaymentView && !hasCustomerFeeSplit && customerFeeSek > 0 && (
                                    <tr>
                                      <td className="ps-0">
                                        <i className="feather-percent me-2 text-muted" style={{ fontSize: 13 }}></i>
                                        {t('projectView.transactions.customerFee', 'Avgift')}
                                      </td>
                                      <td className="text-end pe-0 text-muted">+{new Intl.NumberFormat('sv-SE').format(customerFeeSek)} kr</td>
                                    </tr>
                                  )}
                                  {!personPayout && !showCustomerPaymentView && txn.transactionFeeSek > 0 && (
                                    <tr>
                                      <td className="ps-0">
                                        <i className="feather-percent me-2 text-muted" style={{ fontSize: 13 }}></i>
                                        {t('projectView.transactions.transactionFee', 'Transaktionsavgift')} ({txn.feePct}%)
                                      </td>
                                      <td className="text-end pe-0 text-muted">{sign}{new Intl.NumberFormat('sv-SE').format(txn.transactionFeeSek)} kr</td>
                                    </tr>
                                  )}
                                  {!personPayout && !showCustomerPaymentView && txn.projectFeeSek > 0 && (
                                    <tr>
                                      <td className="ps-0">
                                        <i className="feather-file me-2 text-muted" style={{ fontSize: 13 }}></i>
                                        {t('projectView.transactions.projectFee', 'Projektavgift')}
                                      </td>
                                      <td className="text-end pe-0 text-muted">{sign}{new Intl.NumberFormat('sv-SE').format(txn.projectFeeSek)} kr</td>
                                    </tr>
                                  )}
                                  {!personPayout && !isOutgoing && txn.customerFeeSek === 0 && txn.configType === 'partner' && (
                                    <tr>
                                      <td className="ps-0" colSpan={2}>
                                        <span className="badge bg-primary"><i className="feather-gift me-1" style={{ fontSize: 10 }}></i>B2B Partner — {t('projectView.transactions.noCustomerFee', 'Ingen avgift för beställare')}</span>
                                      </td>
                                    </tr>
                                  )}
                                  {!personPayout && isOutgoing && !txn.isFirstPayout && (
                                    <tr>
                                      <td className="ps-0" colSpan={2}>
                                        <small className="text-muted fst-italic">
                                          <i className="feather-info me-1" style={{ fontSize: 11 }}></i>
                                          {t('projectView.transactions.feesAlreadyTaken', 'Etappio-avgifter togs vid första utbetalningen — inget avdrag på resterande etappmål.')}
                                        </small>
                                      </td>
                                    </tr>
                                  )}
                                  {!personPayout && (
                                  <tr className="border-top">
                                    <td className="ps-0 fw-bold pt-2">
                                      <i className={`feather-${isOutgoing ? 'arrow-up-right' : 'check-circle'} me-2 ${isOutgoing ? 'text-warning' : 'text-success'}`} style={{ fontSize: 13 }}></i>
                                      {isOutgoing
                                        ? t('projectView.transactions.totalPaidOut', 'Utbetalt till hantverkare')
                                        : t('projectView.transactions.totalPaid', 'Totalt insatt')}
                                    </td>
                                    <td className={`text-end pe-0 fw-bold pt-2 fs-6 ${isOutgoing ? 'text-warning' : 'text-success'}`}>
                                      {new Intl.NumberFormat('sv-SE').format(totalAmountSek)} kr
                                    </td>
                                  </tr>
                                  )}
                                </tbody>
                              </table>
                              </>
                                );
                              })()}

                              {/* Admin-only: Etappio margin */}
                              {user?.isAdmin && txn.etappioRevenueSek !== undefined && txn.etappioRevenueSek !== null && (txn.etappioRevenueSek > 0 || !isOutgoing) && (
                                <div className="mt-3 pt-3 border-top">
                                  <small className="fw-semibold text-muted d-block mb-2">{t('projectView.transactions.etappioInternals', 'Etappio Intern')}</small>
                                  <div className="row g-2">
                                    <div className="col-4">
                                      <div className="text-center p-2 bg-light rounded">
                                        <small className="text-muted d-block">{t('projectView.transactions.etappioRevenue', 'Intäkt')}</small>
                                        <span className="fw-bold text-primary">{new Intl.NumberFormat('sv-SE').format(txn.etappioRevenueSek)} kr</span>
                                      </div>
                                    </div>
                                    <div className="col-4">
                                      <div className="text-center p-2 bg-light rounded">
                                        <small className="text-muted d-block">{t('projectView.transactions.etappioCost', 'Kostnad')}</small>
                                        <span className="fw-bold text-danger">-{new Intl.NumberFormat('sv-SE').format(txn.etappioCostSek)} kr</span>
                                      </div>
                                    </div>
                                    <div className="col-4">
                                      <div className="text-center p-2 bg-light rounded">
                                        <small className="text-muted d-block">{t('projectView.transactions.etappioNetMargin', 'Marginal')}</small>
                                        <span className="fw-bold text-success">{new Intl.NumberFormat('sv-SE').format(txn.etappioMarginSek)} kr ({txn.etappioMarginPct}%)</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Config + payer/recipient info */}
                              <div className="mt-3 pt-2 border-top d-flex flex-wrap gap-3">
                                {((isOutgoing && txn.recipientName) || (!isOutgoing && txn.paidByName)) && (
                                  <small className="text-muted">
                                    <i className={`feather-${isOutgoing ? 'briefcase' : 'user'} me-1`}></i>
                                    {isOutgoing
                                      ? <>{t('projectView.transactions.paidTo', 'Utbetalt till')}: <strong>{txn.recipientName}</strong></>
                                      : <>{t('projectView.transactions.paidBy', 'Insatt av')}: <strong>{txn.paidByName}</strong></>}
                                  </small>
                                )}
                                {txn.configName && (
                                  <small className="text-muted">
                                    <i className="feather-sliders me-1"></i>
                                    {t('projectView.transactions.configInfo', 'Avtal')}: <strong>{txn.configName}</strong>
                                    {txn.configType === 'partner' && <span className="badge bg-primary ms-1" style={{ fontSize: 9 }}>B2B</span>}
                                  </small>
                                )}
                                <small className="text-muted">
                                  <i className={`feather-${methodIcon} me-1`}></i>
                                  {txn.paymentMethodLabel}
                                </small>
                              </div>
                            </div>
                          </div>
                          {!isPerson && isReleasedToBuilder && (
                            <div className="border rounded overflow-hidden">
                              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start p-3 bg-light border-bottom">
                                <div className="d-flex align-items-center gap-2 mb-2 mb-sm-0">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center bg-success text-white" style={{ width: 36, height: 36, flexShrink: 0 }}>
                                    <i className="feather-send" style={{ fontSize: 16 }}></i>
                                  </div>
                                  <div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <TransactionKindPill kind="payout" />
                                      <span className="fw-bold">
                                        {t('projectView.transactions.releaseCardTitleNamed', {
                                          defaultValue: 'Pengarna överförs till {{recipientName}}',
                                          recipientName: releaseRecipientName
                                        })}
                                      </span>
                                      <span className="badge bg-success">
                                        {t('projectView.transactions.releaseCardStatus', 'Utbetalning utförd')}
                                      </span>
                                    </div>
                                    <small className="text-muted">
                                      {txn.payoutReference || txn.payout_reference || t('projectView.transactions.releaseCardReference', 'Utbetalning via Adyen')}
                                    </small>
                                    {(contractNumber != null || milestoneNumber != null) && (
                                      <div>
                                        <small className="text-muted">
                                          <i className="feather-flag me-1" style={{ fontSize: 11 }}></i>
                                          {contractNumber != null && (
                                            <>{t('projectView.transactions.contractRef', 'Kontrakt')} #{contractNumber}</>
                                          )}
                                          {contractNumber != null && milestoneNumber != null && ' · '}
                                          {milestoneNumber != null && (
                                            <>{t('projectView.transactions.milestoneRef', 'Etappmål')} #{milestoneNumber}</>
                                          )}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-sm-end">
                                  <div className="fs-5 fw-bold text-success">
                                    {new Intl.NumberFormat('sv-SE').format(payoutAmountSek)} kr
                                  </div>
                                  <small className="text-muted">
                                    {releasedAt
                                      ? new Date(releasedAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                      : new Date(txn.createdAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </small>
                                </div>
                              </div>
                              <div className="p-3">
                                <div className="alert alert-success mb-0 py-2 px-3">
                                  <i className="feather-check-circle me-2"></i>
                                  <strong>{t('projectView.transactions.releaseCardHeading', 'Utbetalningen är utförd.')}</strong>
                                  <span className="ms-1">
                                    {releasedAt
                                      ? t('projectView.transactions.releaseCardDate', {
                                          defaultValue: 'Adyen överför nu pengarna till {{recipientName}} sedan {{date}}.',
                                          recipientName: releaseRecipientName,
                                          date: new Date(releasedAt).toLocaleString('sv-SE', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })
                                        })
                                      : t('projectView.transactions.releaseCardInfoNamed', {
                                          defaultValue: 'Adyen överför nu pengarna till {{recipientName}}.',
                                          recipientName: releaseRecipientName
                                        })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
                </div>
              </div>
            ) : (
              <Card>
                <div className="card-body">
                  <div className="text-center py-5 text-muted">
                    <i className="feather-credit-card fs-1 d-block mb-3"></i>
                    <h6>{t('projectView.transactions.noContract', 'Inga transaktioner ännu')}</h6>
                    <p className="mb-0">{t('projectView.transactions.noContractInfo', 'Transaktioner visas när kontraktet har signerats av båda parter och en avtalskonfiguration har fastställts.')}</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Marketplace Tab */}
        {activeTab === 'marketplace' && canUseProjectMarketplace && id && (
          <div className="tab-pane fade show active">
            <MarketplaceTab projectId={id} />
          </div>
        )}
      </div>

      {paymentInterestNotice && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1050 }} onClick={() => setPaymentInterestNotice(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <div className="rounded-circle bg-warning d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64 }}>
                  <i className="feather-clock text-white fs-3"></i>
                </div>
                <h5 className="fw-bold mb-2">{t('projectView.paymentInterest.title', 'Insättning kommer snart')}</h5>
                <p className="text-muted mb-3">{paymentInterestNotice}</p>
                <div className="alert alert-warning text-start small mb-3">
                  {t('projectView.paymentInterest.noAdyenPayment', 'Ingen insättning har startats. Hantverkaren har fått en notifiering och du får besked när insättningen kan genomföras.')}
                </div>
                <button type="button" className="btn btn-primary" onClick={() => setPaymentInterestNotice(null)}>
                  {t('common.ok', 'OK')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal (top-level so it renders from any tab: Documents & Contract) */}
      {previewDoc && (previewUrl || previewHtml) && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('documents.preview.title')} - {previewDoc.originalName}
                </h5>
                <button type="button" className="btn-close" onClick={closePreview}></button>
              </div>
              <div className="modal-body" style={{ minHeight: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                {previewHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ textAlign: 'left' }} />
                ) : IMAGE_EXTENSIONS.includes(getFileExtension(previewDoc.originalName)) ? (
                  <div className="text-center">
                    <img src={previewUrl!} alt={previewDoc.originalName} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                  </div>
                ) : getFileExtension(previewDoc.originalName) === '.pdf' ? (
                  <iframe src={previewUrl!} style={{ width: '100%', height: '70vh', border: 'none' }} title={previewDoc.originalName} />
                ) : (
                  <p className="text-muted text-center">{t('documents.preview.notSupported')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && project && (() => {
        const selEco = methodEcos[selectedPaymentMethod] || projectEconomics;
        const signedContracts = effectiveContractVersions(contracts).filter(c => c.draftStatus === 'signed_by_both' &&
          contractOutstandingAmount(c, projectFunding?.contracts.find(item => item.contractId === c.id)) > 0);
        const selectedContractForPayment = signedContracts.find(c => c.id === selectedPaymentContractId) || null;
        const selectedContractMilestones = selectedPaymentContractId ? (contractMilestonesMap[selectedPaymentContractId] || []) : [];
        const selectedMilestoneForPayment = selectedPaymentMilestoneId
          ? selectedContractMilestones.find(milestone => milestone.id === selectedPaymentMilestoneId) || null
          : null;
        const paymentSubjectLabel = selectedMilestoneForPayment
          ? `${t('personHome.projectTimeline.milestone', 'Etappmål')} #${selectedMilestoneForPayment.milestoneNumber}`
          : selectedContractForPayment
            ? `${t('personHome.projectTimeline.contract', 'Kontrakt')} #${selectedContractForPayment.contractNumber}`
            : t('personHome.projectTimeline.project', 'Projekt');
        // Use the active milestone as the payment basis when the timeline is waiting
        // for the next milestone to be funded; otherwise fall back to contract/project.
        const pv = selectedMilestoneForPayment
          ? selectedMilestoneForPayment.afterRotAmount || selectedMilestoneForPayment.amount || 0
          : selectedContractForPayment
            ? parseFloat(String(selectedContractForPayment.afterRotAmount || '0').replace(/[^0-9.-]/g, '')) || 0
            : (selEco?.projectValue ?? totalContractAmount);

        const paymentMethods = [
          {
            method: 'bank' as const,
            icon: 'home',
            iconClass: 'text-primary',
            label: t('projectView.payment.trustly', 'Trustly'),
            subtitle: t('projectView.payment.viaTrustly', 'via Trustly'),
            timing: '1–3 bankdagar',
            feeLabel: t('projectView.payment.bankFee', 'Trustlyavgift'),
            proceedLabel: t('projectView.payment.proceedBank', 'Gå vidare — Banköverföring via Trustly'),
            detailLabel: t('projectView.payment.bankTransfer', 'Banköverföring via Trustly')
          },
          {
            method: 'swish' as const,
            icon: 'smartphone',
            iconClass: 'text-success',
            label: 'Swish',
            subtitle: '',
            timing: t('projectView.payment.instant', 'Direkt'),
            feeLabel: t('projectView.payment.swishFee', 'Swishavgift'),
            proceedLabel: t('projectView.payment.proceedSwish', 'Gå vidare — Swish'),
            detailLabel: 'Swish'
          },
          {
            method: 'card' as const,
            icon: 'credit-card',
            iconClass: 'text-info',
            label: t('projectView.payment.card', 'Kort'),
            subtitle: t('projectView.payment.cardSubtitle', 'Visa eller Mastercard'),
            timing: t('projectView.payment.instant', 'Direkt'),
            feeLabel: t('projectView.payment.cardFee', 'Kortavgift'),
            proceedLabel: t('projectView.payment.proceedCard', 'Gå vidare — Kort'),
            detailLabel: t('projectView.payment.card', 'Kort')
          }
        ].filter(({ method }) => method !== 'card' || cardPaymentsEnabled);

        // Per-method derived figures so every card shows its own total.
        const deriveFor = (m: 'bank' | 'swish' | 'card') => {
          const e = methodEcos[m] || {};
          const ecf = Number(e?.customerFee ?? 0);
          const epf = Number(e?.projectFee ?? 0);
          const paymentMethodFee = Number(e?.paymentMethodFee ?? 0);
          const minimumFeeAdjustment = Number(e?.minimumFeeAdjustment ?? e?.customerMinimumFeeAdjustment ?? e?.minimum_fee_adjustment_sek ?? 0);
          const etappioVariableFee = Number(e?.etappioVariableFee ?? Math.max(ecf - epf - paymentMethodFee - minimumFeeAdjustment, 0));
          return {
            cf: ecf,
            projectFee: epf,
            etappioVariableFee,
            minimumFeeAdjustment,
            etappioVariableFeePct: Number(e?.etappioVariableFeePct ?? e?.feePct ?? 0),
            paymentMethodFee,
            paymentMethodFeePct: Number(e?.paymentMethodFeePct ?? 0),
            transFee: etappioVariableFee + paymentMethodFee + minimumFeeAdjustment,
            feePct: Number(e?.feePct ?? 0),
            total: pv + ecf
          };
        };
        const M = {
          bank: deriveFor('bank'),
          swish: deriveFor('swish'),
          card: deriveFor('card')
        };
        const minTotal = Math.min(...paymentMethods.map(({ method }) => M[method].total));
        const paymentEconomicsReady =
          !paymentEconomicsLoading &&
          !paymentEconomicsError &&
          paymentMethods.every(({ method }) => !!methodEcos[method]);

        // Selected-method values drive the lower cost specification + footer.
        const sel = M[selectedPaymentMethod];
        const projectFee = sel.projectFee;
        const transFee = sel.transFee;
        const etappioVariableFee = sel.etappioVariableFee;
        const etappioVariableFeePct = sel.etappioVariableFeePct;
        const minimumFeeAdjustment = sel.minimumFeeAdjustment;
        const paymentMethodFee = sel.paymentMethodFee;
        const paymentMethodFeePct = sel.paymentMethodFeePct;
        const totalToPay = sel.total;
        const configName = selEco?.configName ?? '';
        const configType = selEco?.configType ?? '';
        const selectedPaymentMethodConfig = paymentMethods.find(({ method }) => method === selectedPaymentMethod) ?? paymentMethods[0];
        const methodFeeLabel = selectedPaymentMethodConfig.feeLabel;
        const formatPct = (value: number) => new Intl.NumberFormat('sv-SE', {
          minimumFractionDigits: value % 1 === 0 ? 0 : 1,
          maximumFractionDigits: 2
        }).format(value);

        return (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, overflow: 'auto' }} onClick={() => setShowPaymentModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="feather-credit-card me-2"></i>
                  {t('projectView.payment.title', 'Insättning')} — {t('personHome.projectTimeline.project', 'Projekt')} #{project.projectNumber}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Contract picker — pick which contract to pay */}
                {signedContracts.length > 0 && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      {selectedMilestoneForPayment
                        ? t('projectView.payment.selectedMilestone', 'Valt etappmål att sätta in pengar för')
                        : t('projectView.payment.selectContract', 'Välj kontrakt att sätta in pengar för')}
                    </label>
                    <div className="d-flex flex-column gap-2">
                      {signedContracts.map(c => {
                        const amt = parseFloat(String(c.afterRotAmount || '0').replace(/[^0-9.-]/g, '')) || 0;
                        const isSelected = selectedPaymentContractId === c.id;
                        const cMilestones = contractMilestonesMap[c.id] || [];
                        const cSelectedMilestone = selectedPaymentMilestoneId
                          ? cMilestones.find(milestone => milestone.id === selectedPaymentMilestoneId) || null
                          : null;
                        const displayAmount = cSelectedMilestone
                          ? cSelectedMilestone.afterRotAmount || cSelectedMilestone.amount || 0
                          : amt;
                        const typeLabel = c.contractType === 'variation'
                          ? `${t('contracts.types.variation')}${c.variationNumber != null ? ` #${c.variationNumber}` : ''}`
                          : t('contracts.types.fixed');
                        return (
                          <div
                            key={c.id}
                            className={`border rounded p-3 ${isSelected ? 'border-primary border-2 bg-light' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedPaymentContractId(c.id);
                              if (!cSelectedMilestone) setSelectedPaymentMilestoneId(null);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedPaymentContractId(c.id);
                                if (!cSelectedMilestone) setSelectedPaymentMilestoneId(null);
                              }
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div className="flex-grow-1 min-width-0">
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                  <input
                                    type="radio"
                                    className="form-check-input flex-shrink-0"
                                    checked={isSelected}
                                    onChange={() => {
                                      setSelectedPaymentContractId(c.id);
                                      if (!cSelectedMilestone) setSelectedPaymentMilestoneId(null);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="fw-semibold">
                                    {t('projectView.payment.contractRef', 'Kontrakt')} #{c.contractNumber}
                                  </span>
                                  <span className="badge bg-light text-dark border">{typeLabel}</span>
                                </div>
                                <div className="text-truncate small">
                                  {cSelectedMilestone
                                    ? `${t('personHome.projectTimeline.milestone', 'Etappmål')} #${cSelectedMilestone.milestoneNumber}: ${cSelectedMilestone.description}`
                                    : contractTitle(c)}
                                </div>
                              </div>
                              <div className="text-end flex-shrink-0">
                                <div className="fw-bold">{new Intl.NumberFormat('sv-SE').format(displayAmount)} kr</div>
                                <small className="text-muted">
                                  {cSelectedMilestone
                                    ? t('projectView.payment.milestoneAmount', 'Etappbelopp')
                                    : 'Att sätta in efter ROT'}
                                </small>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Anbudssumma (read-only) */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    {selectedMilestoneForPayment
                      ? t('projectView.payment.milestoneAmount', 'Etappbelopp')
                      : t('projectView.payment.contractAmount', 'Anbudssumma')}
                  </label>
                  <div className="input-group">
                    <input type="text" className="form-control form-control-lg bg-light" value={new Intl.NumberFormat('sv-SE').format(pv)} disabled />
                    <span className="input-group-text">SEK</span>
                  </div>
                </div>

                {paymentEconomicsLoading && (
                  <div className="alert alert-info d-flex align-items-center gap-2 mb-4" role="status">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    <span>{t('projectView.payment.loadingEconomics', 'Hämtar avgifter för insättningen...')}</span>
                  </div>
                )}

                {paymentEconomicsError && (
                  <div className="alert alert-danger mb-4" role="alert">
                    <i className="feather-alert-triangle me-2"></i>
                    {paymentEconomicsError}
                  </div>
                )}

                {paymentEconomicsReady && (
                <>
                {/* Payment method selection */}
                <div className="mb-4">
                  <label className="form-label fw-semibold text-uppercase small text-muted">{t('projectView.payment.totalCostPerMethod', 'TOTALKOSTNAD PER SÄTT ATT SÄTTA IN')}</label>
                  <div className="row g-3">
                    {paymentMethods.map(({ method, icon, iconClass, label, subtitle, timing, feeLabel }) => {
                      const figures = M[method];
                      return (
                        <div className="col-md-4" key={method}>
                          <div
                            className={`card h-100 ${selectedPaymentMethod === method ? 'border-primary border-2 bg-light' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedPaymentMethod(method)}
                          >
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <i className={`feather-${icon} fs-5 ${iconClass}`}></i>
                                <div>
                                  <div className="fw-bold small">{label}</div>
                                  {subtitle && <div className="text-muted" style={{ fontSize: 11 }}>{subtitle}</div>}
                                </div>
                              </div>
                              {figures.total === minTotal && (
                                <span className="badge bg-success mb-2">{t('projectView.payment.lowestCost', 'Lägst kostnad')}</span>
                              )}
                              <div>
                                <small className="text-muted">{t('projectView.payment.totalToPay', 'Totalt att sätta in')}</small>
                                <div className="fs-5 fw-bold">{new Intl.NumberFormat('sv-SE').format(figures.total)} kr</div>
                                <small className="text-muted">{timing}</small>
                              </div>
                              <div className="mt-2 pt-2 border-top">
                                <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                                  <span className="text-muted">{selectedMilestoneForPayment ? t('projectView.payment.milestoneAmount', 'Etappbelopp') : t('projectView.payment.project', 'Anbudssumma')}</span>
                                  <span>{new Intl.NumberFormat('sv-SE').format(pv)} kr</span>
                                </div>
                                <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                                  <span className="text-muted">{t('projectView.payment.etappioVariableFee', 'Etappio rörlig')}</span>
                                  <span>+{new Intl.NumberFormat('sv-SE').format(figures.etappioVariableFee)} kr</span>
                                </div>
                                <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                                  <span className="text-muted">{t('projectView.payment.fixedFee', 'Fast avgift')}</span>
                                  <span>+{new Intl.NumberFormat('sv-SE').format(figures.projectFee)} kr</span>
                                </div>
                                {figures.minimumFeeAdjustment > 0 && (
                                  <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                                    <span className="text-muted">{t('projectView.payment.minimumFeeAdjustment', 'Minimiavgiftstillägg')}</span>
                                    <span>+{new Intl.NumberFormat('sv-SE').format(figures.minimumFeeAdjustment)} kr</span>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                                  <span className="text-muted">{feeLabel}</span>
                                  <span>+{new Intl.NumberFormat('sv-SE').format(figures.paymentMethodFee)} kr</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Cost specification detail */}
                <div className="border rounded p-3 mb-4">
                  <h6 className="fw-semibold mb-3">
                    {t('projectView.payment.costSpecification', 'Kostnadsspecifikation')} — {selectedPaymentMethodConfig.detailLabel}
                  </h6>
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td>{selectedMilestoneForPayment ? t('projectView.payment.milestoneAmount', 'Etappbelopp') : t('projectView.payment.contractAmount', 'Anbudssumma')}</td>
                        <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(pv)} kr</td>
                      </tr>
                      <tr>
                        <td>{t('projectView.payment.etappioVariableFee', 'Etappio rörlig')} ({formatPct(etappioVariableFeePct)}%)</td>
                        <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(etappioVariableFee)} kr</td>
                      </tr>
                      <tr>
                        <td>{t('projectView.payment.fixedFeeDetail', 'Fast avgift')}</td>
                        <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(projectFee)} kr</td>
                      </tr>
                      {minimumFeeAdjustment > 0 && (
                        <tr>
                          <td>{t('projectView.payment.minimumFeeAdjustment', 'Minimiavgiftstillägg')}</td>
                          <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(minimumFeeAdjustment)} kr</td>
                        </tr>
                      )}
                      <tr>
                        <td>{methodFeeLabel} ({formatPct(paymentMethodFeePct)}%)</td>
                        <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(paymentMethodFee)} kr</td>
                      </tr>
                      <tr>
                        <td>{t('projectView.payment.transactionFeeDetail', 'Transaktionsavgift totalt')}</td>
                        <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(transFee + projectFee)} kr</td>
                      </tr>
                      <tr className="border-top">
                        <td className="fw-bold">{t('projectView.payment.totalToPay', 'Totalt att sätta in')}</td>
                        <td className="text-end fw-bold text-success fs-5">{new Intl.NumberFormat('sv-SE').format(totalToPay)} kr</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                </>
                )}
              </div>
              <div className="modal-footer flex-column">
                <button
                  className="btn btn-primary btn-lg w-100"
                  disabled={!paymentEconomicsReady || (signedContracts.length > 0 && !selectedPaymentContractId)}
                  onClick={() => {
                    setShowPaymentModal(false);
                    const contractParam = selectedPaymentContractId ? `&contractId=${selectedPaymentContractId}` : '';
                    const milestoneParam = selectedPaymentMilestoneId ? `&milestoneId=${selectedPaymentMilestoneId}` : '';
                    navigate(`/payment-checkout?projectId=${id}&method=${selectedPaymentMethod}${contractParam}${milestoneParam}`);
                  }}>
                  <i className={`feather-${selectedPaymentMethodConfig.icon} me-2`}></i>
                  {selectedPaymentMethodConfig.proceedLabel}
                  <span className="visually-hidden"> {paymentSubjectLabel}</span>
                  <i className="feather-arrow-right ms-2"></i>
                </button>
                <small className="text-muted text-center mt-2">
                  {configType === 'partner'
                    ? t('projectView.payment.partnerDisclaimer', 'Hantverkaren har B2B Partner-avtal. Beställaren betalar ingen avgift.')
                    : t('projectView.payment.disclaimer', 'Avgifter gäller för beställare och faktureras tillsammans med anbudssumman.')
                  }
                </small>
                {configName && (
                  <small className="text-muted text-center d-block mt-1">
                    <i className="feather-info me-1"></i>
                    {t('projectView.payment.configUsed', 'Avtalskonfiguration')}: <strong>{configName}</strong>
                    {selEco?.source === 'snapshot' && selEco?.snapshotDate && (
                      <span className="ms-1">({t('projectView.payment.locked', 'fastställd')} {new Date(selEco.snapshotDate).toLocaleDateString('sv-SE')})</span>
                    )}
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}
      {stageVariationDraft && (
        <VariationWizard
          {...stageVariationDraft}
          contracts={contracts}
          allStages={Object.values(contractMilestonesMap).flat()}
          company={{ name: contractWizardCompany.name || projectCompanyNameOrEmpty(projectCompany?.companyName ?? project?.companyName), organizationNumber: contractWizardCompany.organizationNumber }}
          customer={customers.find(c => c.id === project?.customerId) ?? {}}
          providerName={signingProviderName}
          onBeforeChange={milestoneId => confirmOverlap('variation', stageVariationDraft.contract.id, milestoneId)}
          onPersisted={async (contract, settlement) => {
            if (settlement) await applySavedStageVariation(settlement);
            else setContractReplacements(previous => {
              const next = { ...previous }; delete next[contract.id];
              if (next[stageVariationDraft.contract.id]?.replacementContractId === contract.id) delete next[stageVariationDraft.contract.id];
              return next;
            });
            setContracts(previous => previous.some(c => c.id === contract.id) ? previous.map(c => c.id === contract.id ? contract : c) : [...previous, contract]);
            if (settlement?.milestones) setContractMilestonesMap(previous => ({ ...previous, [contract.id]: settlement.milestones! }));
            navigate(`?tab=contract&contractId=${encodeURIComponent(contract.id)}`, { replace: true });
          }}
          onOpenAttachment={doc => void attachmentPreview.openPreview(doc)}
          onVerifyAttachment={doc => setAttachmentDetailsTarget(doc)}
          onClose={created => {
            setStageVariationDraft(null);
            void loadContracts();
            if (created) { handleSelectContract(created); setVariationAgreementId(created.id); }
          }}
        />
      )}
      {variationAgreementId && (() => {
        const contract = contracts.find(c => c.id === variationAgreementId);
        return contract ? <VariationAgreementModal viewerRole={isPerson ? 'person' : 'company'} contract={contract} contracts={contracts} stages={Object.values(contractMilestonesMap).flat()}
          onClose={() => setVariationAgreementId(null)}
          replacements={contractReplacements}
          onCreateVariation={contractId => { setVariationAgreementId(null); void openStageVariation(contractId).catch(error => setMessage({ type: 'error', text: variationOpenErrorText(error, knownApiErrorText(t, error) ?? t('variationWizard.loadError')) })); }}
          onSignedDocument={selected => { void attachmentPreview.openPreviewFrom(() => contractApi.getSignedDocument(selected.id), t('contracts.signedDocument.fileName', { number: selected.contractNumber }), t('contracts.signedDocument.missing')); }}
          onEdit={selected => { setVariationAgreementId(null); void openVariationWizardForEdit(selected); }} /> : null;
      })()}
      {showStatusChangeModal && (
        <StatusChangeModal
          type={showStatusChangeModal}
          onSubmit={handleStatusChange}
          onClose={() => setShowStatusChangeModal(null)}
        />
      )}
      {startModal}
      {showAttestModal && attestMilestone && (() => {
        const candidateSettlement = contractReplacements[attestMilestone.contractId];
        const replacementSettlement =
          candidateSettlement?.originalContractId === attestMilestone.contractId &&
          candidateSettlement.disputedMilestoneId === attestMilestone.id
            ? candidateSettlement
            : null;
        const replacementContract = replacementSettlement
          ? contracts.find(
              contract => contract.id === replacementSettlement.replacementContractId
            ) ?? null
          : null;
        const replacementOriginalContract = replacementSettlement
          ? contracts.find(
              contract => contract.id === replacementSettlement.originalContractId
            ) ?? null
          : null;
        const attestThread = projectThreads.find(thread => thread.threadType === 'milestone'
          && thread.threadId === attestMilestone.id);

        if (attestMilestone.deliveryStatus === 'external_dispute') {
          return (
            <ExternalDisputeModal
              milestone={attestMilestone}
              messages={milestoneMessages}
              userRole={isPerson ? 'person' : 'company'}
              onPostMessage={handlePostMilestoneMessage}
              onDownloadExport={() => handleDownloadDisputeExport(attestMilestone)}
              onOpenDocuments={handleOpenProjectDocuments}
              onClose={() => setShowAttestModal(false)}
            />
          );
        }

        return (
          <MilestoneAttestationModal
            milestone={attestMilestone}
            contractNumber={contracts.find(contract => contract.id === attestMilestone.contractId)?.contractNumber}
            contractMilestones={contractMilestonesMap[attestMilestone.contractId] ?? []}
            messages={milestoneMessages}
            userRole={isPerson ? 'person' : 'company'}
            initialMode={attestModalMode}
            revealLatestMessage={attestOpenedFromLink}
            attestationRequestPending={!!attestThread?.attestationRequestPending}
            attestationRequestLatestAt={attestThread?.latestRequestAt ?? null}
            attestationRequestNextAllowedAt={attestThread?.nextRequestAllowedAt ?? null}
            onAttest={handleAttest}
            onPostMessage={handlePostMilestoneMessage}
            onProgress={handleProgressMilestoneResolution}
            onStartConflict={() => handleStartMilestoneConflict(attestMilestone)}
            onStartDispute={(verificationRef) => handleStartMilestoneDispute(attestMilestone, verificationRef)}
            onDownloadDisputeExport={() => handleDownloadDisputeExport(attestMilestone)}
            onOpenMarketplace={handleOpenConflictMarketplace}
            onCreateVariation={!isPerson
              ? reason => handleProposeConflictVariation(attestMilestone, reason)
              : undefined}
            onCreateVariationWithReference={!isPerson
              ? async reference => { await handleProposeConflictVariation(attestMilestone, '', reference); }
              : undefined}
            replacementContract={replacementContract}
            replacementOriginalContract={replacementOriginalContract}
            replacementSettlement={replacementSettlement}
            onSignReplacement={
              isPerson && replacementContract
                ? () => {
                    setShowAttestModal(false);
                    void handleOpenPreview('sign', replacementContract);
                  }
                : undefined
            }
            onViewReplacement={
              !isPerson && replacementContract
                ? () => {
                    setShowAttestModal(false);
                    void handleOpenPreview('preview', replacementContract);
                  }
                : undefined
            }
            isOpeningReplacementSigning={isOpeningSigning}
            onApproveReplacementProposal={
              !isPerson && replacementSettlement
                ? () => void handleReplacementProposalResponse(
                    replacementSettlement,
                    'approve'
                  )
                : undefined
            }
            onRejectReplacementProposal={
              !isPerson && replacementSettlement
                ? () => confirmReplacementProposalRejection(
                    replacementSettlement
                  )
                : undefined
            }
            onWithdrawReplacementProposal={
              isPerson && replacementSettlement
                ? () => confirmReplacementProposalWithdrawal(
                    replacementSettlement
                  )
                : undefined
            }
            isUpdatingReplacementProposal={isUpdatingReplacementProposal}
            onClose={() => setShowAttestModal(false)}
          />
        );
      })()}

      {/* Rendered outside the review/signing overlay so closing the preview never
          closes the overlay underneath. */}
      <DocumentPreviewModal
        previewDoc={attachmentPreview.previewDoc}
        previewUrl={attachmentPreview.previewUrl}
        previewHtml={attachmentPreview.previewHtml}
        previewLoading={attachmentPreview.previewLoading}
        previewError={attachmentPreview.previewError}
        previewErrorText={attachmentPreview.previewErrorText}
        onClose={attachmentPreview.closePreview}
      />

      {attachmentDetailsTarget && (
        <DocumentIntegrityModal
          document={attachmentDetailsTarget}
          onClose={() => setAttachmentDetailsTarget(null)}
        />
      )}

      {/* Reusable confirmation overlay */}
      {confirmDialog && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
          role="dialog"
          onClick={() => setConfirmDialog(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`feather-${confirmDialog.icon} me-2 text-${confirmDialog.confirmVariant}`}></i>
                  {confirmDialog.title}
                </h5>
                <button type="button" className="btn-close" onClick={() => setConfirmDialog(null)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{confirmDialog.message}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirmDialog(null)}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  className={`btn btn-${confirmDialog.confirmVariant}`}
                  onClick={async () => {
                    const action = confirmDialog.onConfirm;
                    setConfirmDialog(null);
                    await action();
                  }}
                >
                  <i className={`feather-${confirmDialog.icon} me-1`}></i>
                  {confirmDialog.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
